/* eslint-disable react/prop-types */
import React, {
  startTransition,
  useMemo,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { useLocation, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Modal,
  Box,
  Select,
  MenuItem,
  Button,
  TextField,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api from "api/api";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import useRecordsheetData from "./data/RecordSheetData";
import Swal from "sweetalert2";
import LoadingScreen from "layouts/loading/loadingscreen";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  formatSensitiveFieldInputValue,
  maskSensitiveFieldValue,
  shouldMaskSensitiveField,
} from "utils/maskingUtils";
import { getDinerColumnAverages } from "utils/dinerTotalCalc";

// 근무 타입별 배경색
const typeColors = {
  1: "#d9f2d9",
  2: "#fff7cc",
  3: "#e6d9f2",
  4: "#f9d9d9",
  5: "#ffe6cc",
  6: "#cce6ff",
  19: "#d9f2e6",
  16: "#DDAED3",
  17: "#9F8383",
  21: "#dd7b93",
};

const TYPE_LABEL = {
  0: "-",
  1: "영양사",
  2: "상용",
  3: "초과",
  17: "조기퇴근",
  4: "결근",
  5: "파출",
  6: "직원파출",
  7: "유틸",
  8: "대체근무",
  9: "연차",
  10: "반차",
  11: "대체휴무",
  12: "병가",
  13: "출산휴가",
  14: "육아휴직",
  15: "하계휴가",
  16: "업장휴무",
  18: "경조사",
  19: "통합",
  20: "재택근무",
  21: "공휴일",
};

const SALARY_INPUT_TYPE_VALUES = new Set(["5", "6"]);
const NOTE_INPUT_TYPE_VALUES = new Set(["3", "11", "17"]);
const shouldClearSalaryForType = (type) => !SALARY_INPUT_TYPE_VALUES.has(String(type ?? ""));
const shouldSendEmptyNoteForType = (type) => !NOTE_INPUT_TYPE_VALUES.has(String(type ?? ""));

const FULL_TYPE_OPTIONS = [
  { value: "0", label: "-" },
  { value: "1", label: "영양사" },
  { value: "2", label: "상용" },
  { value: "3", label: "초과" },
  { value: "17", label: "조기퇴근" },
  { value: "4", label: "결근" },
  { value: "5", label: "파출" },
  { value: "6", label: "직원파출" },
  { value: "7", label: "유틸" },
  { value: "19", label: "통합" },
  { value: "8", label: "대체근무" },
  { value: "9", label: "연차" },
  { value: "10", label: "반차" },
  { value: "11", label: "대체휴무" },
  { value: "12", label: "병가" },
  { value: "13", label: "출산휴가" },
  { value: "14", label: "육아휴직" },
  { value: "15", label: "하계휴가" },
  { value: "16", label: "업장휴무" },
  { value: "18", label: "경조사" },
  { value: "20", label: "재택근무" },
  { value: "21", label: "공휴일" },
];
const EMPLOYEE_DISPATCH_TYPE_OPTIONS = [
  { value: "0", label: "-" },
  { value: "6", label: "직원파출" },
];
const DISPATCH_TYPE_OPTIONS = [
  { value: "0", label: "-" },
  { value: "5", label: "파출" },
];
const ATTENDANCE_TIME_OPTIONS = (() => {
  const times = [];
  for (let h = 5; h <= 20; h += 1) {
    for (const m of ["00", "30"]) {
      if (h === 20 && m !== "00") continue;
      times.push(`${h.toString().padStart(2, "")}:${m}`);
    }
  }
  return times;
})();

const safeStr = (v, fallback = "") => (v == null ? fallback : String(v));
const safeTrim = (v, fallback = "") => safeStr(v, fallback).trim();

// 퇴사자 노출 규칙(엑셀):
// del_dt 월까지는 노출, 그 다음 달부터 미노출
const getRetiredMonthStart = (row) => {
  const delDtRaw = safeTrim(row?.del_dt ?? "", "");
  if (!delDtRaw) return null;

  const delDt = dayjs(delDtRaw);
  if (!delDt.isValid()) return null;

  return delDt.startOf("month");
};

const isRetiredByDelInfo = (row) => {
  const delYn = safeTrim(row?.del_yn ?? "", "").toUpperCase();
  if (delYn === "Y") return true;
  return getRetiredMonthStart(row) !== null;
};

const shouldIncludeRetiredForExcel = (row, rangeStartDate) => {
  const retiredMonthStart = getRetiredMonthStart(row);
  if (!retiredMonthStart) return true;
  return !rangeStartDate.startOf("month").isAfter(retiredMonthStart);
};

// 화면 조회용 퇴사자 노출 규칙:
// del_dt 월까지는 노출, 그 다음 달부터 미노출
const shouldIncludeRetiredForViewMonth = (row, viewYear, viewMonth) => {
  const viewMonthDate = dayjs(`${viewYear}-${String(viewMonth).padStart(2, "0")}-01`);
  if (!viewMonthDate.isValid()) return true;
  return shouldIncludeRetiredForExcel(row, viewMonthDate);
};

const isDispatchTypeValue = (v) => {
  const t = safeTrim(v, "");
  if (!t) return false;
  return t === "5" || t === "6" || t === "파출" || t === "직원파출";
};

const isEmployeeDispatchType = (v) => {
  const t = safeTrim(v, "");
  return t === "6" || t === "직원파출";
};

const getDispatchKeys = (row) => {
  if (!row) return [];
  const keys = [];
  const mid = safeTrim(row.member_id ?? row.memberId ?? "", "");
  if (mid) keys.push(String(mid));
  const name = safeTrim(row.name ?? "", "");
  if (name) keys.push(name);
  return keys;
};

const getDispatchStatFromMap = (map, row) => {
  if (!map || !row) return null;
  const keys = getDispatchKeys(row);
  for (const k of keys) {
    if (map.has(k)) return map.get(k);
  }
  return null;
};

const pickLatestDispatchHistoryRowsByName = (rows) => {
  const source = Array.isArray(rows) ? rows : [];
  const seenNames = new Set();
  const result = [];

  source.forEach((row) => {
    const nameKey = safeTrim(row?.name ?? "", "").toLowerCase();
    if (!nameKey) {
      result.push(row);
      return;
    }
    if (seenNames.has(nameKey)) return;
    seenNames.add(nameKey);
    result.push(row);
  });

  return result;
};

const toNumberLike = (v) => {
  const s = String(v ?? "").replace(/[^0-9.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

// ✅ 은행명 추출
const extractBankName = (accountNumber) => {
  const s = safeTrim(accountNumber, "");
  if (!s) return "";

  const firstToken = s.split(/\s+/)[0] || "";
  const m = s.match(/^([A-Za-z가-힣]+(?:은행)?)/) || firstToken.match(/^([A-Za-z가-힣]+(?:은행)?)/);

  return safeTrim(m?.[1] ?? firstToken, "");
};

// ✅ 계좌번호만 추출
const extractAccountOnly = (accountNumber) => {
  const s = safeTrim(accountNumber, "");
  if (!s) return "";

  const bank = extractBankName(s);
  let rest = s;

  if (bank) {
    rest = rest.replace(new RegExp(`^\\s*${bank}\\s*`), "");
  }

  const only = rest.replace(/[^0-9-]/g, "").trim();
  if (!only) return s.replace(/[^0-9-]/g, "").trim();

  return only;
};

// ✅ 숫자/문자 모두 보기좋게
const formatMoneyLike = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";

  if (/[가-힣]/.test(s) || /회/.test(s) || /원/.test(s)) return s;

  const n = Number(s.replace(/,/g, ""));
  if (!Number.isNaN(n)) return n.toLocaleString();

  return s;
};

const parseEmployeeDispatchInfo = (v) => {
  const s = safeTrim(v, "");
  if (!s) return { count: 0, amount: "", origin: "", dispatch: "" };

  let count = 0;
  const countMatch = s.match(/([0-9]+)\s*회/);
  if (countMatch) count = Number(countMatch[1]);

  let amount = "";
  const nums = s.match(/([0-9][0-9,]*)/g) || [];
  if (nums.length >= 2) {
    amount = toNumberLike(nums[nums.length - 1]);
  } else if (nums.length === 1 && /원/.test(s)) {
    const n = toNumberLike(nums[0]);
    if (!(countMatch && n === count)) amount = n;
  }

  let origin = "";
  let dispatch = "";
  const originMatch = s.match(
    /원\s*소\s*속\s*[:：]?\s*([^,\/>→]+?)(?=\s*(파견|직원파출|\d+\s*회|\d|$))/
  );
  if (originMatch) origin = originMatch[1].trim();
  const dispatchMatch = s.match(/파견(?:업장)?\s*[:：]?\s*([^,\/>→]+?)(?=\s*(\d+\s*회|\d|$))/);
  if (dispatchMatch) dispatch = dispatchMatch[1].trim();

  if (!origin || !dispatch) {
    const parts = s.split(/\s*(?:->|→|=>|>|\/)\s*/).filter(Boolean);
    if (parts.length >= 2) {
      if (!origin) origin = parts[0].trim();
      if (!dispatch) dispatch = parts[1].trim();
    }
  }

  return { count, amount, origin, dispatch };
};

// ✅ 숫자 변환 (엑셀용)
const toNumberMaybe = (v) => {
  if (v == null || v === "") return "";
  const cleaned = String(v)
    .replace(/[^0-9.-]/g, "")
    .trim();
  if (!cleaned) return "";
  const n = Number(cleaned);
  return Number.isNaN(n) ? "" : n;
};

const toFirstNumberMaybe = (v) => {
  const s = safeTrim(v, "");
  if (!s) return "";
  const m = s.match(/-?\d+(?:\.\d+)?/);
  if (!m) return "";
  const n = Number(m[0]);
  return Number.isNaN(n) ? "" : n;
};

const toMinutesFromHHMM = (v) => {
  const s = safeTrim(v, "");
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const calcDurationHours = (start, end) => {
  const s = toMinutesFromHHMM(start);
  const e = toMinutesFromHHMM(end);
  if (s == null || e == null) return 0;
  let diff = e - s;
  if (diff < 0) diff += 24 * 60;
  return Number((diff / 60).toFixed(2));
};

// ✅ 셀 비교용 헬퍼
const normalizeCell = (cell) => {
  if (!cell) return { type: "", start: "", end: "", salary: 0, note: "", pay_yn: "N" };

  const toNum = (v) => {
    if (v == null || v === "") return 0;
    const n = Number(String(v).replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
  };

  const payYn = safeTrim(cell.pay_yn ?? cell.payYn ?? "", "").toUpperCase() === "Y" ? "Y" : "N";

  return {
    type: cell.type ?? "",
    start: cell.start || cell.start_time || "",
    end: cell.end || cell.end_time || "",
    salary: toNum(cell.salary),
    note: cell.note ?? cell.note ?? "",
    pay_yn: payYn,
  };
};

const isCellEqual = (a, b) => {
  const na = normalizeCell(a);
  const nb = normalizeCell(b);
  return (
    na.type === nb.type &&
    na.start === nb.start &&
    na.end === nb.end &&
    na.salary === nb.salary &&
    na.note === nb.note &&
    na.pay_yn === nb.pay_yn
  );
};

// ✅ 출근현황 셀
const AttendanceCell = React.memo(function AttendanceCell({
  getValue,
  row,
  column,
  table,
  typeOptions,
}) {
  const isJoinLocked = Boolean(table.options.meta?.isCellLocked?.(row.original, column.id));
  const rawVal = getValue() || {};
  const val = {
    type: "",
    start: "",
    end: "",
    salary: "",
    note: "",
    pay_yn: "N",
    ...rawVal,
  };
  const [salaryDraft, setSalaryDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");

  useEffect(() => {
    const salaryRaw = String(val?.salary ?? "").replace(/[^0-9]/g, "");
    setSalaryDraft(salaryRaw);
    setNoteDraft(val?.note ?? "");
  }, [val?.salary, val?.note, row.index, column.id]);
  const isDispatchType = ["5", "6"].includes(String(val.type));
  const isPayableDispatch = String(val.type) === "5" || String(val.type) === "파출";

  const bgColor = typeColors[val.type] || "#ffefd5";

  const parseTime = (str) => {
    if (!str) return null;
    const [h, m] = str.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return dayjs().hour(h).minute(m).second(0);
  };

  const handleChange = (field, newVal) => {
    if (isJoinLocked) return;
    const dayKey = column.id;

    const rowGubun = safeTrim(row.original?.gubun, "nor");
    const rowPt = safeTrim(row.original?.position_type, "");

    const baseValue = row.original?.[dayKey] || {};

    const updatedValue = {
      ...baseValue,
      ...val,

      // ✅ gubun/position_type 유지
      gubun: safeTrim(baseValue.gubun ?? val.gubun ?? rowGubun, "nor"),
      position_type: safeTrim(baseValue.position_type ?? val.position_type ?? rowPt, ""),

      [field]: newVal,
    };

    // ✅ type을 0/-로 내리면 나머지 초기화
    if (field === "type" && (newVal === "0" || newVal === "")) {
      updatedValue.start = "";
      updatedValue.end = "";
      updatedValue.start_time = "";
      updatedValue.end_time = "";
      updatedValue.salary = "";
      updatedValue.note = "";
      updatedValue.pay_yn = "N";
    }

    if (field === "type") {
      const nextType = String(newVal ?? "");
      const nextIsDispatch = SALARY_INPUT_TYPE_VALUES.has(nextType);
      if (!nextIsDispatch) updatedValue.pay_yn = "N";
      if (shouldClearSalaryForType(nextType)) updatedValue.salary = "";
      if (shouldSendEmptyNoteForType(nextType)) updatedValue.note = "";
    }

    // 🔹 초과/조기퇴근 자동 계산 (note에 0.5 단위로 반영)
    if (
      (updatedValue.type === "3" || updatedValue.type === "17") &&
      updatedValue.start &&
      updatedValue.end &&
      (field === "start" || field === "end")
    ) {
      const start = parseTime(updatedValue.start);
      const end = parseTime(updatedValue.end);

      const org = table.options.meta?.getOrgTimes?.(row.original) || {};
      const baseStart = parseTime(org.org_start_time);
      const baseEnd = parseTime(org.org_end_time);

      if (start && end && baseStart && baseEnd) {
        const workedMinutes = end.diff(start, "minute");
        const baseMinutes = baseEnd.diff(baseStart, "minute");
        const diffMinutes = workedMinutes - baseMinutes; // 초과면 +, 조기퇴근이면 -

        // 30분 단위로 환산(0.5)
        const abs = Math.abs(diffMinutes);
        const units = Math.floor(abs / 60) + (abs % 60 >= 30 ? 0.5 : 0);

        if (updatedValue.type === "3") {
          // 초과: +일 때만 표시
          updatedValue.note = diffMinutes > 0 ? String(units) : "";
        } else if (updatedValue.type === "17") {
          // 조기퇴근: -일 때만 표시
          updatedValue.note = diffMinutes < 0 ? String(-units) : "";
        }
      }
    }

    table.options.meta?.updateData(row.index, dayKey, updatedValue);
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        backgroundColor: bgColor,
        ...(isJoinLocked ? { backgroundColor: "#e0e0e0" } : {}),
        padding: "2px",
        borderRadius: "4px",
        width: "100%",
      }}
    >
      <select
        disabled={isJoinLocked}
        value={val.type}
        onChange={(e) => handleChange("type", e.target.value)}
        style={{
          fontSize: "0.75rem",
          textAlign: "center",
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "block",
        }}
      >
        {typeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {["1", "2", "3", "5", "6", "7", "8", "10", "17", "19"].includes(val.type) && (
        <>
          <select
            disabled={isJoinLocked}
            value={val.start}
            onChange={(e) => handleChange("start", e.target.value)}
            style={{
              fontSize: "0.725rem",
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "block",
            }}
          >
            <option value="">출근</option>
            {ATTENDANCE_TIME_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            disabled={isJoinLocked}
            value={val.end}
            onChange={(e) => handleChange("end", e.target.value)}
            style={{
              fontSize: "0.725rem",
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "block",
            }}
          >
            <option value="">퇴근</option>
            {ATTENDANCE_TIME_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </>
      )}

      {isDispatchType && (
        <input
          disabled={isJoinLocked}
          type="text"
          placeholder="급여"
          value={salaryDraft !== "" ? Number(salaryDraft).toLocaleString() : ""}
          onChange={(e) => setSalaryDraft(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={() => {
            if (isJoinLocked) return;
            handleChange("salary", salaryDraft);
          }}
          style={{
            fontSize: "0.725rem",
            textAlign: "center",
            border: "1px solid black",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            display: "block",
          }}
        />
      )}

      {isPayableDispatch && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            fontSize: "0.7rem",
          }}
        >
          <input
            type="checkbox"
            disabled={isJoinLocked}
            checked={String(val.pay_yn ?? "N").toUpperCase() === "Y"}
            onChange={(e) => handleChange("pay_yn", e.target.checked ? "Y" : "N")}
          />
          지급
        </label>
      )}

      {["3", "11", "17"].includes(val.type) && (
        <input
          disabled={isJoinLocked}
          type="text"
          placeholder={val.type === "3" ? "초과" : val.type === "17" ? "조기퇴근" : "대체휴무"}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={() => {
            if (isJoinLocked) return;
            handleChange("note", noteDraft);
          }}
          style={{
            fontSize: "0.725rem",
            textAlign: "center",
            border: "1px solid black",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            display: "block",
          }}
        />
      )}
    </div>
  );
});

AttendanceCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
  table: PropTypes.object.isRequired,
  typeOptions: PropTypes.array.isRequired,
};

function ReadonlyCell({ getValue }) {
  return <span style={{ fontSize: "0.75rem" }}>{getValue() || ""}</span>;
}
ReadonlyCell.propTypes = { getValue: PropTypes.func.isRequired };

// =======================================================
// ✅ 파출 테이블 기능(원본스냅샷/변경감지/변경분만 저장/자동 재조회/레이스 방지)
// =======================================================

// ✅ _rid 안정적으로 부여 (조회 시에만 부여되므로, 편집 중 _rid가 바뀌지 않음)
const ensureDispatchRid = (row) => {
  if (!row) return row;
  if (row._rid) return row;

  const base = row.dispatch_id ?? row.dispatchId ?? row.id ?? row.member_id ?? row.memberId ?? "";
  if (base) return { ...row, _rid: String(base) };

  // base가 없으면 그래도 최대한 안정적으로(조회 기준) 구성
  const fallback = [row.account_id ?? "", row.name ?? "", row.rrn ?? "", row.account_number ?? ""]
    .filter(Boolean)
    .join("_");

  if (fallback) return { ...row, _rid: `DIS_${fallback}` };

  return { ...row, _rid: `DIS_${Date.now()}_${Math.random().toString(16).slice(2)}` };
};

const normalizeDispatchValue = (field, v) => {
  const s = String(v ?? "");
  // ✅ 파출정보 수정이력 비교 시 공백(스페이스)도 변경으로 인식
  //    (기존처럼 trim/공백제거를 하지 않고 원문 그대로 비교)
  if (field === "del_yn") return s.trim().toUpperCase();
  return s;
};

function DispatchEditableCell({ getValue, row, table, field, maskingEnabled = false }) {
  const value = getValue() ?? "";
  const [inputValue, setInputValue] = useState(value);
  const [isComposing, setIsComposing] = useState(false);
  const rid = String(row?.original?._rid ?? "");
  const isMaskedSensitiveField = shouldMaskSensitiveField(field, maskingEnabled);

  const original = table.options.meta?.getOriginalDispatchValueByRid?.(rid, field) ?? "";
  useEffect(() => {
    if (isComposing) return;
    setInputValue(value ?? "");
  }, [value, rid, field, isComposing]);

  const comparedValue = isMaskedSensitiveField ? value : inputValue;
  const isChanged =
    normalizeDispatchValue(field, comparedValue) !== normalizeDispatchValue(field, original);
  const displayValue = isMaskedSensitiveField
    ? maskSensitiveFieldValue(field, value, maskingEnabled)
    : inputValue;

  const commitValue = useCallback(
    (nextVal) => {
      if (isMaskedSensitiveField) return;
      table.options.meta?.updateDispatchByRid?.(rid, { [field]: nextVal });
    },
    [isMaskedSensitiveField, table, rid, field]
  );

  const handleChange = (e) => {
    if (isMaskedSensitiveField) return;
    const newVal = formatSensitiveFieldInputValue(field, e.target.value);
    setInputValue(newVal);
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (e) => {
    const newVal = formatSensitiveFieldInputValue(field, e.currentTarget.value);
    setIsComposing(false);
    setInputValue(newVal);
    commitValue(newVal);
  };

  const handleBlur = () => {
    commitValue(formatSensitiveFieldInputValue(field, inputValue));
  };

  return (
    <input
      className="dispatch-centered-input"
      value={displayValue}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onBlur={handleBlur}
      readOnly={isMaskedSensitiveField}
      style={{
        width: "100%",
        height: 20,
        boxSizing: "border-box",
        fontSize: "0.75rem",
        lineHeight: 1.1,
        textAlign: "center",
        textAlignLast: "center",
        direction: "ltr",
        border: "1px solid #ccc",
        borderRadius: 3,
        padding: "0 2px",
        background: isMaskedSensitiveField ? "#f7f7f7" : "#fff",
        color: isChanged ? "red" : "black",
        fontWeight: isChanged ? 700 : 400,
      }}
    />
  );
}

DispatchEditableCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
  table: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  maskingEnabled: PropTypes.bool,
};

// ✅ 파출 삭제/복원 버튼 셀
function DispatchActionCell({ row, onToggle }) {
  const delYn = row.original?.del_yn ?? "N";
  const isDeleted = String(delYn).toUpperCase() === "Y";

  return (
    <MDButton
      size="small"
      variant="gradient"
      color={isDeleted ? "success" : "error"}
      onClick={() => onToggle(row.original)}
      sx={{
        minHeight: 20,
        height: 20,
        px: 0.75,
        py: 0,
        minWidth: 52,
        fontSize: "0.65rem",
        lineHeight: 1,
      }}
    >
      {isDeleted ? "복원" : "삭제"}
    </MDButton>
  );
}

function DispatchPayCell({ row, status, onToggle }) {
  const total = status?.total ?? 0;
  const paid = status?.paid ?? 0;
  const checked = total > 0 && paid === total;
  const indeterminate = total > 0 && paid > 0 && paid < total;
  const label = total === 0 ? "-" : checked ? "지급" : indeterminate ? "부분" : "미지급";

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
      <Checkbox
        size="small"
        checked={checked}
        indeterminate={indeterminate}
        disabled={total === 0}
        onChange={(e) => onToggle(row.original?.member_id, e.target.checked)}
      />
      <span style={{ fontSize: "0.7rem" }}>{label}</span>
    </div>
  );
}

DispatchPayCell.propTypes = {
  row: PropTypes.object.isRequired,
  status: PropTypes.object,
  onToggle: PropTypes.func.isRequired,
};

function RecordSheet() {
  const today = dayjs();
  const [year, setYear] = useState(today.year());
  const [month, setMonth] = useState(today.month() + 1);

  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceSummaryRows, setAttendanceSummaryRows] = useState([]);
  const [originalAttendanceRows, setOriginalAttendanceRows] = useState([]);
  const [defaultTimes, setDefaultTimes] = useState({});
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const accountInputRef = useRef("");

  const [dispatchDelFilter, setDispatchDelFilter] = useState("N");
  const [dispatchMappingRows, setDispatchMappingRows] = useState([]);
  const dispatchMappingReqSeqRef = useRef(0);
  const [holidayDays, setHolidayDays] = useState(new Set());

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const account_name = queryParams.get("name"); // 유지

  const { account_id } = useParams();
  const daysInMonth = dayjs(`${year}-${month}`).daysInMonth();
  const currentViewKey = useMemo(() => {
    const accId = String(selectedAccountId || "").trim();
    if (!accId) return "";
    return `${accId}_${year}_${month}`;
  }, [selectedAccountId, year, month]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTabletOrSmallDesktop = useMediaQuery("(max-width:1280px)");
  const isNarrowDesktop = useMediaQuery("(min-width:1281px) and (max-width:1447px)");
  const isTopToolbarWrap = isTabletOrSmallDesktop || isNarrowDesktop;

  // ✅ 상단 "전체 거래처 엑셀/지급 일괄" 버튼 권한:
  // - position: 0(대표님), 1(팀장)
  // - department: 3(인사팀), 6(개발팀)
  const loginPositionCode = safeTrim(localStorage.getItem("position"), "");
  const loginDepartmentCode = safeTrim(localStorage.getItem("department"), "");
  const canUseBulkExcelAndPay =
    ["0", "1"].includes(loginPositionCode) || ["3", "6"].includes(loginDepartmentCode);
  // ✅ 파출이력 조회 버튼 전체 노출
  const canViewDispatchHistory = true;

  // ✅ "유틸 출근부" 버튼 노출 권한: department 6, 또는 user_id가 si1/db1인 경우만
  const loginUserId = safeTrim(localStorage.getItem("user_id"), "");
  const canUseUtilRecord = loginDepartmentCode === "6" || ["si1", "db1"].includes(loginUserId);
  // ✅ "통합 출근부" 버튼 노출 권한: department 6, 또는 user_id가 iy1인 경우만
  const canUseIntegrationRecord = loginDepartmentCode === "6" || loginUserId === "iy1";

  const [open, setOpen] = useState(false);
  const [dispatchImportOpen, setDispatchImportOpen] = useState(false);
  const [dispatchImportRows, setDispatchImportRows] = useState([]);
  const [dispatchImportLoading, setDispatchImportLoading] = useState(false);
  const handleModalOpen = () => setOpen(true);
  // ✅ 민감정보 마스킹 토글(파출정보 본문 / 파출이력 모달 분리)
  const [dispatchMaskingEnabled, setDispatchMaskingEnabled] = useState(true);
  const [dispatchImportMaskingEnabled, setDispatchImportMaskingEnabled] = useState(true);

  const [excelDownloading, setExcelDownloading] = useState(false);
  const [excelRangeOpen, setExcelRangeOpen] = useState(false);
  const [excelRange, setExcelRange] = useState({ start: "", end: "" });

  const [payRangeOpen, setPayRangeOpen] = useState(false);
  const [payRange, setPayRange] = useState({ start: "", end: "" });
  const [payRangeLoading, setPayRangeLoading] = useState(false);
  const [payRangeRows, setPayRangeRows] = useState([]);
  const [payRangeSelected, setPayRangeSelected] = useState({});
  const payRangeRecordMapRef = useRef(new Map());

  // ✅ 유틸 출근부 엑셀 업로드 상태
  const [utilRecordUploading, setUtilRecordUploading] = useState(false);
  const utilRecordFileInputRef = useRef(null);
  // ✅ 거래처명이 정확히 하나로 매칭되지 않는 경우(동명 후보 여럿/매칭 실패) 사용자가 직접 선택
  const [utilRecordAmbiguousOpen, setUtilRecordAmbiguousOpen] = useState(false);
  const [utilRecordAmbiguousItems, setUtilRecordAmbiguousItems] = useState([]);
  // ✅ 이름이 동명이인인 경우, 입력된 날짜가 있으면 버리지 않고 실제 회원을 선택
  const [utilRecordDuplicateItems, setUtilRecordDuplicateItems] = useState([]);
  const utilRecordUploadContextRef = useRef(null);

  // =========================================================
  // ✅ 통합 출근부 모달 상태
  //   - 왼쪽: 통합(position_type=7) 직원 목록
  //   - 가운데: 매핑 목록(등록 전 임시 편집용 — tb_account_util_member_mapping에는 저장하지 않음)
  //   - 오른쪽: 업장 목록(이미 매핑에 있는 업장은 제외하고 노출)
  //   - "등록"을 누르면 가운데 목록에 있는 업장 각각에 대해, 조회 중인 연/월의
  //     평일(공휴일 제외)만 골라 재택근무(type 20)를 tb_account_record에 등록한다.
  //     ⚠ 통합(position_type=7) 직원의 실제 출근 데이터는 tb_account_util_record가 아니라
  //        tb_account_record에 저장된다(AccountRecordSheetList의 통합 직원 UNION 브랜치 참고).
  //        tb_account_util_member_mapping은 "이 직원이 이 업장에 노출될 수 있다"는 명단일 뿐이다.
  //     (재등록 시 해당 직원의 그 연/월 기존 재택근무(type=20)만 먼저 삭제 후 재삽입하므로
  //      다시 등록해도 중복되지 않고 최신 매핑 목록 기준으로 갱신되며, 다른 근무기록은 유지된다)
  // =========================================================
  const [integrationRecordOpen, setIntegrationRecordOpen] = useState(false);
  const [integrationRecordLoading, setIntegrationRecordLoading] = useState(false);
  const [integrationRecordRegistering, setIntegrationRecordRegistering] = useState(false);
  const [integrationMemberRows, setIntegrationMemberRows] = useState([]);
  const [integrationSelectedMember, setIntegrationSelectedMember] = useState(null);
  const [integrationMappingRows, setIntegrationMappingRows] = useState([]);
  // ✅ 가운데(매핑)/오른쪽(업장) 모두 다중선택 후 한 번에 이동 가능 — account_id 기준 Set으로 관리
  const [integrationSelectedMappingIds, setIntegrationSelectedMappingIds] = useState(() => new Set());
  const [integrationSelectedAccountIds, setIntegrationSelectedAccountIds] = useState(() => new Set());
  // ✅ 이번 달에 이미 tb_account_util_record로 등록되어 있는 업장(확인용, 클릭 불가)
  const [integrationMonthRecordRows, setIntegrationMonthRecordRows] = useState([]);
  const [integrationMonthRecordLoading, setIntegrationMonthRecordLoading] = useState(false);

  // ✅ hook: dispatchRows는 여기서 쓰지 않고 "파출은 로컬 state + fetchDispatchOnly"로 통일
  const { memberRows, sheetRows, timesRows, accountList, fetchAllData, loading } =
    useRecordsheetData(selectedAccountId, year, month);

  const isCellLockedByActJoin = useCallback(
    (row, columnId) => {
      if (!row || !String(columnId || "").startsWith("day_")) return false;
      const gubun = safeTrim(row?.gubun ?? "", "").toLowerCase();
      if (gubun !== "rec") return false;

      const joinRaw = safeTrim(row?.act_join_dt ?? "", "");
      if (!joinRaw) return false;

      const joinDate = dayjs(joinRaw);
      if (!joinDate.isValid()) return false;

      const dayNum = Number(String(columnId).replace("day_", ""));
      if (!Number.isFinite(dayNum)) return false;

      const joinYear = joinDate.year();
      const joinMonth = joinDate.month() + 1;
      const joinDay = joinDate.date();

      if (year < joinYear || (year === joinYear && month < joinMonth)) return true;
      if (year > joinYear || (year === joinYear && month > joinMonth)) return false;
      return dayNum < joinDay;
    },
    [year, month]
  );

  const selectAccountByInput = useCallback(
    (rawInput) => {
      const q = String(rawInput ?? accountInputRef.current ?? "").trim();
      if (!q) return;
      const list = accountList || [];
      const qLower = q.toLowerCase();
      const exact = list.find((a) => String(a?.account_name || "").toLowerCase() === qLower);
      let partial = exact;
      if (!partial) {
        const candidates = list.filter((a) =>
          String(a?.account_name || "")
            .toLowerCase()
            .includes(qLower)
        );
        // 부분일치 후보가 여럿이면(예: 늘사랑(부천), 늘사랑(서구)) 잘못 선택되지 않도록 매칭하지 않음
        if (candidates.length === 1) partial = candidates[0];
      }
      if (partial) {
        setSelectedAccountId(partial.account_id);
        accountInputRef.current = partial.account_name || q;
      }
    },
    [accountList]
  );

  useEffect(() => {
    if (!year || !month) return;
    api
      .get("/Operate/HolidayList", { params: { year, month } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setHolidayDays(new Set(list.map((h) => Number(h.holiday_day))));
      })
      .catch(() => setHolidayDays(new Set()));
  }, [year, month]);

  // 식수현황의 업장별 노출 컬럼과 동일한 기준으로, 조회 월의 조식/중식/석식 등 컬럼별 평균을 표시
  // (하나로 합친 숫자 대신 식수현황 표 맨 아래 "평균" 행 값을 컬럼별로 그대로 보여준다)
  const [mealColumnAverages, setMealColumnAverages] = useState([]);
  useEffect(() => {
    if (!selectedAccountId || !year || !month) {
      setMealColumnAverages([]);
      return;
    }

    let cancelled = false;
    const accountType = (accountList || []).find(
      (account) => String(account?.account_id) === String(selectedAccountId)
    )?.account_type;

    Promise.all([
      api.get("/Operate/AccountDinnersNumberList", {
        params: { account_id: selectedAccountId, year, month },
      }),
      api.get("/Business/AccountEctDietList", {
        params: { account_id: selectedAccountId },
      }),
    ])
      .then(([dinersRes, extraDietRes]) => {
        if (cancelled) return;

        const extraDietRow = Array.isArray(extraDietRes.data)
          ? extraDietRes.data[0] || {}
          : extraDietRes.data || {};
        const extraDietCols = Array.from({ length: 5 }, (_, index) => {
          const number = index + 1;
          const name = extraDietRow[`extra_diet${number}_name`];
          return name && String(name).trim()
            ? { name, priceKey: `extra_diet${number}_price` }
            : null;
        }).filter(Boolean);
        const dinersRows = Array.isArray(dinersRes.data) ? dinersRes.data : [];

        setMealColumnAverages(
          getDinerColumnAverages(dinersRows, accountType, extraDietCols, selectedAccountId, year, month)
        );
      })
      .catch(() => {
        if (!cancelled) setMealColumnAverages([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, year, month, accountList]);

  // ✅ 로딩화면 없이 "직원정보 테이블"만 쓱 새로고침
  const [employeeRowsView, setEmployeeRowsView] = useState([]);
  const [employeeLoadedViewKey, setEmployeeLoadedViewKey] = useState("");
  useLayoutEffect(() => {
    if (!currentViewKey) {
      setEmployeeRowsView([]);
      setEmployeeLoadedViewKey("");
      return;
    }
    if (loading) return;

    const visibleEmployeeRows = (Array.isArray(memberRows) ? memberRows : []).filter((row) =>
      shouldIncludeRetiredForViewMonth(row, year, month)
    );

    setEmployeeRowsView(visibleEmployeeRows);
    setEmployeeLoadedViewKey(currentViewKey);
  }, [memberRows, loading, currentViewKey, year, month]);

  // =========================
  // ✅ 1) payload 안전 처리 (문자열 JSON 파싱까지)
  // =========================
  const parseMaybeJson = (payload) => {
    if (typeof payload !== "string") return payload;
    const s = payload.trim();
    if (!s) return payload;
    if (!(s.startsWith("{") || s.startsWith("["))) return payload;
    try {
      return JSON.parse(s);
    } catch {
      return payload;
    }
  };

  const extractArray = (payload) => {
    payload = parseMaybeJson(payload);

    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];

    if (Array.isArray(payload.resultList)) return payload.resultList;
    if (Array.isArray(payload.result)) return payload.result;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.list)) return payload.list;
    if (Array.isArray(payload.rows)) return payload.rows;

    if (payload.data && typeof payload.data === "object") {
      if (Array.isArray(payload.data.resultList)) return payload.data.resultList;
      if (Array.isArray(payload.data.list)) return payload.data.list;
      if (Array.isArray(payload.data.rows)) return payload.data.rows;
      if (Array.isArray(payload.data.data)) return payload.data.data;
    }

    const v1 = Object.values(payload).find(Array.isArray);
    if (v1) return v1;

    if (payload.data && typeof payload.data === "object") {
      const v2 = Object.values(payload.data).find(Array.isArray);
      if (v2) return v2;
    }

    return [];
  };

  const closeDispatchImportModal = useCallback(() => {
    setDispatchImportOpen(false);
    setDispatchImportRows([]);
  }, []);

  const formatDispatchImportCellValue = useCallback(
    (field, value) => {
      const raw = safeTrim(value, "");
      if (!raw) return "-";
      return maskSensitiveFieldValue(field, raw, dispatchImportMaskingEnabled, {
        user_id:
          typeof window !== "undefined" && window?.localStorage
            ? String(window.localStorage.getItem("user_id") ?? "").trim()
            : "",
      });
    },
    [dispatchImportMaskingEnabled]
  );

  const isDispatchImportAlreadyRegistered = useCallback(
    (row) => safeTrim(row?.already_registered, "N").toUpperCase() === "Y",
    []
  );

  // ✅ 파출이력 조회: 현재 업장 기준으로 해당 월까지 등록된 인원만 조회
  const openDispatchImportModal = useCallback(async () => {
    if (!selectedAccountId) {
      Swal.fire("안내", "업장을 먼저 선택해주세요.", "info");
      return;
    }

    setDispatchImportRows([]);
    setDispatchImportOpen(true);
    setDispatchImportLoading(true);

    try {
      const res = await api.get("/Account/AccountDispatchMemberHistoryList", {
        params: { account_id: selectedAccountId, year, month },
      });
      const list = extractArray(res.data);
      const dedupedList = pickLatestDispatchHistoryRowsByName(list);
      // 조회중 문구가 먼저 사라지고 목록이 늦게 붙지 않도록 결과는 바로 반영
      setDispatchImportRows(dedupedList);
    } catch (err) {
      setDispatchImportRows([]);
      Swal.fire("조회 실패", err?.message || "파출이력 조회 중 오류가 발생했습니다.", "error");
    } finally {
      setDispatchImportLoading(false);
    }
  }, [selectedAccountId, year, month]);

  const openExcelRangeModal = () => {
    const hasAttendanceChanges = (() => {
      if (!attendanceRows || attendanceRows.length === 0) return false;
      const useDiffMode =
        originalAttendanceRows && originalAttendanceRows.length === attendanceRows.length;
      if (!useDiffMode) return true;
      for (let i = 0; i < attendanceRows.length; i += 1) {
        const row = attendanceRows[i];
        const originalRow = originalAttendanceRows[i];
        if (!originalRow) return true;
        const dayKeys = Object.keys(row || {}).filter((k) => k.startsWith("day_"));
        for (const key of dayKeys) {
          if (!isCellEqual(row?.[key], originalRow?.[key])) return true;
        }
      }
      return false;
    })();

    const hasDispatchChanges = (() => {
      if (!dispatchRows || dispatchRows.length === 0) return false;
      const editableFields = ["phone", "rrn", "account_number"];
      return (dispatchRows || []).some((row) => {
        const rid = String(row?._rid ?? "");
        const original = originalDispatchMap.get(rid);
        if (!original) return true;
        return editableFields.some((f) => {
          const cur = normalizeDispatchValue(f, row?.[f]);
          const org = normalizeDispatchValue(f, original?.[f]);
          return cur !== org;
        });
      });
    })();

    if (hasAttendanceChanges || hasDispatchChanges) {
      Swal.fire({
        title: "안내",
        html: "변경된 내용이 있습니다.<br/>저장 후에 전체 거래처 엑셀을 다운로드 해주세요.",
        icon: "info",
      });
      return;
    }

    const start = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).format("YYYY-MM-DD");
    const end = dayjs(`${year}-${String(month).padStart(2, "0")}-${daysInMonth}`).format(
      "YYYY-MM-DD"
    );
    setExcelRange({ start, end });
    setExcelRangeOpen(true);
  };

  const handleExcelRangeConfirm = async () => {
    const start = dayjs(excelRange.start);
    const end = dayjs(excelRange.end);

    if (!start.isValid() || !end.isValid()) {
      Swal.fire("기간 오류", "시작일/종료일을 올바르게 선택하세요.", "warning");
      return;
    }

    setExcelRangeOpen(false);
    await handleExcelDownloadAllAccounts();
  };

  const openPayRangeModal = () => {
    const start = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).format("YYYY-MM-DD");
    const end = dayjs(`${year}-${String(month).padStart(2, "0")}-${daysInMonth}`).format(
      "YYYY-MM-DD"
    );
    setPayRange({ start, end });
    setPayRangeRows([]);
    setPayRangeSelected({});
    payRangeRecordMapRef.current = new Map();
    setPayRangeOpen(true);
  };

  const handlePayRangeCompute = async () => {
    if (!selectedAccountId) {
      Swal.fire("거래처 선택", "먼저 거래처를 선택해주세요.", "warning");
      return;
    }

    const start = dayjs(payRange.start);
    const end = dayjs(payRange.end);

    if (!start.isValid() || !end.isValid()) {
      Swal.fire("기간 오류", "시작일/종료일을 올바르게 선택하세요.", "warning");
      return;
    }

    setPayRangeLoading(true);
    try {
      const realStart = start.isBefore(end) ? start.startOf("day") : end.startOf("day");
      const realEnd = start.isBefore(end) ? end.startOf("day") : start.startOf("day");

      const monthsInRange = [];
      let cursor = realStart.startOf("month");
      const endCursor = realEnd.startOf("month");
      while (cursor.isBefore(endCursor) || cursor.isSame(endCursor)) {
        monthsInRange.push({ y: cursor.year(), m: cursor.month() + 1 });
        cursor = cursor.add(1, "month");
      }

      const sumMap = new Map();
      const infoMap = new Map();
      const recordMap = new Map();
      const seenKeys = new Set();

      for (let mi = 0; mi < monthsInRange.length; mi++) {
        const { y, m } = monthsInRange[mi];
        const daysInThisMonth = dayjs(`${y}-${String(m).padStart(2, "0")}-01`).daysInMonth();

        const { sheetRowsArg, dispatchRowsArg } = await fetchBundleForAccount(
          selectedAccountId,
          y,
          m
        );

        (dispatchRowsArg || []).forEach((d) => {
          const mid = d.member_id;
          if (!mid) return;
          if (!infoMap.has(mid)) {
            infoMap.set(mid, {
              member_id: mid,
              name: d.name || "",
              phone: d.phone || "",
              rrn: d.rrn || "",
              account_number: d.account_number || "",
            });
          }
        });

        const monthStart = dayjs(`${y}-${String(m).padStart(2, "0")}-01`);
        const monthEnd = monthStart.endOf("month");
        const startInMonth =
          (realStart.isAfter(monthStart) || realStart.isSame(monthStart, "day")) &&
          (realStart.isBefore(monthEnd) || realStart.isSame(monthEnd, "day"));
        const endInMonth =
          (realEnd.isAfter(monthStart) || realEnd.isSame(monthStart, "day")) &&
          (realEnd.isBefore(monthEnd) || realEnd.isSame(monthEnd, "day"));

        const fromDay = startInMonth ? realStart.date() : realStart.isAfter(monthEnd) ? null : 1;
        const toDay = endInMonth
          ? realEnd.date()
          : realEnd.isBefore(monthStart)
            ? null
            : daysInThisMonth;

        if (fromDay == null || toDay == null) continue;

        (sheetRowsArg || []).forEach((r) => {
          const dayNum = toDayNumber(r?.record_date ?? r?.record_day ?? r?.day ?? r?.date);
          if (!(dayNum >= fromDay && dayNum <= toDay)) return;
          const g = safeTrim(r?.gubun ?? "", "").toLowerCase();
          if (g !== "dis") return;

          const mid = r?.member_id;
          if (!mid) return;

          const t = String(r?.type ?? "");
          const isDispatch = t === "5" || t === "6";
          if (!isDispatch) return;

          const dedupeKey = getDispatchDedupKey(r, y, m, dayNum, t);
          if (seenKeys.has(dedupeKey)) return;
          seenKeys.add(dedupeKey);

          const sal = Number(String(r?.salary ?? 0).replace(/,/g, "")) || 0;
          const payYn = String(r?.pay_yn ?? "").toUpperCase() === "Y";

          const cur = sumMap.get(mid) || {
            member_id: mid,
            name: r?.name || infoMap.get(mid)?.name || "",
            total_pay: 0,
            paid_cnt: 0,
            total_cnt: 0,
          };
          cur.total_pay += sal;
          cur.total_cnt += 1;
          if (payYn) cur.paid_cnt += 1;
          sumMap.set(mid, cur);

          const recordObj = {
            gubun: "dis",
            account_id: r?.account_id || selectedAccountId,
            member_id: mid,
            position_type: safeTrim(r?.position_type ?? "", ""),
            positionType: safeTrim(r?.position_type ?? "", ""),
            record_date: dayNum,
            record_year: r?.record_year ?? y,
            record_month: r?.record_month ?? m,
            type: Number(safeTrim(t, "0")) || 0,
            start_time: r?.start_time || "",
            end_time: r?.end_time || "",
            salary: sal,
            note: r?.note ?? "",
            pay_yn: "Y",
            position: r?.position || "",
          };

          if (!recordMap.has(mid)) recordMap.set(mid, []);
          recordMap.get(mid).push(recordObj);
        });
      }

      const rows = Array.from(sumMap.values())
        .map((r) => {
          const info = infoMap.get(r.member_id) || {};
          const totalCnt = Number(r.total_cnt || 0);
          const paidCnt = Number(r.paid_cnt || 0);
          const payStatus =
            totalCnt > 0
              ? paidCnt === totalCnt
                ? "지급"
                : paidCnt === 0
                  ? "미지급"
                  : "부분"
              : "-";

          return {
            ...info,
            ...r,
            pay_status: payStatus,
          };
        })
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

      setPayRangeRows(rows);
      setPayRangeSelected(
        rows.reduce((acc, r) => {
          acc[r.member_id] = false;
          return acc;
        }, {})
      );
      payRangeRecordMapRef.current = recordMap;
    } catch (e) {
      console.error(e);
      Swal.fire("오류", e?.message || "기간 합계 계산 중 오류", "error");
    } finally {
      setPayRangeLoading(false);
    }
  };

  const handlePayRangeApply = async () => {
    const selectedIds = Object.keys(payRangeSelected || {}).filter((k) => payRangeSelected[k]);
    if (!selectedIds.length) {
      Swal.fire("선택 필요", "지급 처리할 인원을 선택하세요.", "warning");
      return;
    }

    const disRecords = [];
    selectedIds.forEach((mid) => {
      const list = payRangeRecordMapRef.current.get(mid) || [];
      list.forEach((r) => disRecords.push({ ...r, pay_yn: "Y" }));
    });

    if (!disRecords.length) {
      Swal.fire("안내", "지급 처리할 데이터가 없습니다.", "info");
      return;
    }

    try {
      const res = await api.post("/Account/AccountRecordSave", {
        normalRecords: [],
        disRecords,
        recRecords: [],
      });

      if (res?.data?.code && res.data.code !== 200) {
        throw new Error(res.data?.message || "지급 처리 실패");
      }

      // ✅ 현재 화면 월 데이터만 갱신
      await Promise.all([fetchAllData?.(), fetchDispatchOnly(dispatchDelFilter)]);

      await Swal.fire({ title: "지급 처리", text: "선택 인원 지급 처리 완료", icon: "success" });

      // ✅ 확인 후 모달 닫고 출근부로 복귀
      setPayRangeOpen(false);
      setPayRangeRows([]);
      setPayRangeSelected({});
      payRangeRecordMapRef.current = new Map();
    } catch (e) {
      console.error(e);
      Swal.fire("오류", e?.message || "지급 처리 중 오류", "error");
    }
  };

  // =========================================================
  // ✅ 유틸 출근부 엑셀 업로드
  //   - 유틸 직원(position_type 6,7)의 일자별 근무 업장을 엑셀로 일괄 등록
  //   - tb_account_util_member_mapping(고정 매핑)을 더 이상 사용하지 않고
  //     이 화면에서 업로드한 값이 tb_account_util_record 에 저장되어
  //     출근부 조회 시 해당 테이블과 조인되어 표시됨
  // =========================================================
  const UTIL_RECORD_DAY_COLS = 31; // 서식상 D~AH 고정 31일치 컬럼
  const UTIL_RECORD_FIRST_DAY_COL = 4; // D열부터 1일 시작(참고 이미지와 동일)
  const UTIL_RECORD_LAST_DAY_COL = UTIL_RECORD_FIRST_DAY_COL + UTIL_RECORD_DAY_COLS - 1; // AH열
  const UTIL_RECORD_GUBUN_COL_END = 2; // A:B 병합 = 구분
  const UTIL_RECORD_NAME_COL = 3; // C열 = 성명(회원 매칭 기준)
  const UTIL_RECORD_BLOCK_ROWS = 3; // 직원 1명당 세로 병합 행 수(A:B, C 모두 3행 병합)
  const UTIL_RECORD_OFF_SUM_COL = UTIL_RECORD_FIRST_DAY_COL + UTIL_RECORD_DAY_COLS; // AI열 = 휴무합계
  const UTIL_RECORD_NOTE_COL = UTIL_RECORD_OFF_SUM_COL + 1; // AJ열 = 비고
  const UTIL_RECORD_OFF_TEXT = "휴"; // 비근무일 표시 텍스트

  const utilRecordBorderThin = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
  const utilRecordHeaderFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
  const utilRecordGrayFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6E6" } };
  const utilRecordWeekdayNames = ["일", "월", "화", "수", "목", "금", "토"];
  // ✅ ExcelJS의 column.width는 엑셀 화면에 실제로 표시되는 "열 너비"와 다르다(OOXML 저장값 기준).
  //    엑셀 표시 너비 = ExcelJS width - 5/MDW(기본폰트 Calibri11 기준 MDW=7) 관계이므로,
  //    엑셀 기준으로 정확히 원하는 너비가 보이도록 역보정해서 넣는다.
  const toExcelDisplayWidth = (displayedChars) => displayedChars + 5 / 7;

  // 유틸 출근부 등록용 엑셀 서식 파일 다운로드
  // D2=기준일자(YYYY-MM-01), D3:AH3=1~말일, D4:AH4=요일, C열부터 성명 사전입력,
  // 사람은 D열부터(일자별) 근무한 거래처명(또는 "휴")만 입력하면 됨
  const handleUtilRecordTemplateDownload = async () => {
    try {
      // ✅ position_type: 6=유틸, 7=통합 → 이 서식은 유틸(6)만 대상으로 한다
      const utilMemberRes = await api.get("/Account/AccountUtilMemberList", {
        params: { position_type: 6 },
      });
      const utilMembers = extractArray(utilMemberRes.data);
      // ✅ AK열 참고용 거래처명 목록: 별도 API가 아니라 화면 "거래처 검색"과 동일한 accountList를 사용해
      //    두 목록이 서로 다르게 보이지 않도록 맞춘다.
      const accountNameRefList = accountList || [];
      const lastCol = UTIL_RECORD_NOTE_COL;
      const accountNameRefCol = UTIL_RECORD_NOTE_COL + 1; // AJ 바로 다음 열 = AK열

      const wb = new ExcelJS.Workbook();
      const sheet = wb.addWorksheet("유틸출근부");

      sheet.columns = [
        { key: "gubun", width: toExcelDisplayWidth(4) }, // A열: 구분(A:B 병합)
        { key: "gubun2", width: toExcelDisplayWidth(4) }, // B열: A열과 병합되는 구분 칸의 나머지 영역(값 없음)
        { key: "name", width: toExcelDisplayWidth(7) }, // C열: 성명
        ...Array.from({ length: UTIL_RECORD_DAY_COLS }, (_, i) => ({
          key: `day_${i + 1}`,
          width: toExcelDisplayWidth(4), // D~AH: 엑셀 화면 기준 열너비 4
        })),
        { key: "off_sum", width: 10 },
        { key: "note", width: 24 },
      ];

      // 1행: 제목 (A~마지막열 병합)
      sheet.mergeCells(1, 1, 1, lastCol);
      const titleCell = sheet.getCell(1, 1);
      titleCell.value = "유 틸 출 근 부";
      titleCell.font = { bold: true, size: 16 };
      titleCell.alignment = { vertical: "middle", horizontal: "center" };
      sheet.getRow(1).height = 28;

      // 2행: D2~AH2를 통째로 병합해 하나의 셀로 만들고, 그 값 자체를 업로드 파싱 기준일자('YYYY-MM-01')로 사용한다.
      sheet.mergeCells(2, UTIL_RECORD_FIRST_DAY_COL, 2, UTIL_RECORD_LAST_DAY_COL);
      const baseDateText = `${year}-${String(month).padStart(2, "0")}-01`;
      const baseDateCell = sheet.getCell(2, UTIL_RECORD_FIRST_DAY_COL);
      baseDateCell.value = baseDateText;
      baseDateCell.font = { bold: true, size: 14 };
      baseDateCell.alignment = { vertical: "middle", horizontal: "center" };
      sheet.getRow(2).height = 22;

      // 2~4행: 구분(A:B)/성명(C)/휴무합계/비고 헤더
      sheet.mergeCells(2, 1, 4, UTIL_RECORD_GUBUN_COL_END);
      sheet.getCell(2, 1).value = "구분";
      sheet.mergeCells(2, UTIL_RECORD_NAME_COL, 4, UTIL_RECORD_NAME_COL);
      sheet.getCell(2, UTIL_RECORD_NAME_COL).value = "성명";
      sheet.mergeCells(2, UTIL_RECORD_OFF_SUM_COL, 4, UTIL_RECORD_OFF_SUM_COL);
      sheet.getCell(2, UTIL_RECORD_OFF_SUM_COL).value = "휴무합계";
      sheet.mergeCells(2, lastCol, 4, lastCol);
      sheet.getCell(2, lastCol).value = "비고";

      // 3행: 일자(1~말일), 4행: 요일
      for (let d = 1; d <= UTIL_RECORD_DAY_COLS; d += 1) {
        const colNo = UTIL_RECORD_FIRST_DAY_COL + d - 1;
        const dateCell = sheet.getCell(3, colNo);
        const weekCell = sheet.getCell(4, colNo);
        if (d <= daysInMonth) {
          dateCell.value = d;
          const wd = dayjs(
            `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`
          ).day();
          weekCell.value = utilRecordWeekdayNames[wd];
        } else {
          // ✅ 해당 월에 존재하지 않는 날짜(예: 2월의 30~31일)는 회색으로 비워 구분
          dateCell.value = null;
          weekCell.value = null;
          dateCell.fill = utilRecordGrayFill;
          weekCell.fill = utilRecordGrayFill;
        }
      }

      [2, 3, 4].forEach((rowNo) => {
        const row = sheet.getRow(rowNo);
        row.height = rowNo === 2 ? 22 : 18;
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { ...(cell.font || {}), bold: true };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          if (!cell.fill) cell.fill = utilRecordHeaderFill;
          cell.border = utilRecordBorderThin;
        });
      });

      // 5행부터: 직원 1명당 3행(A:B=구분, C=성명 세로 병합) 블록으로 사전입력
      // 실제 일자별 업장명 입력/휴무합계 집계는 블록의 첫 행(예: 5행)에서만 이루어진다.
      const dayColLetter = (colNo) => sheet.getColumn(colNo).letter;
      (utilMembers || []).forEach((m, idx) => {
        const blockStartRow = 5 + idx * UTIL_RECORD_BLOCK_ROWS;
        const blockEndRow = blockStartRow + UTIL_RECORD_BLOCK_ROWS - 1;
        for (let r = blockStartRow; r <= blockEndRow; r += 1) {
          sheet.getRow(r).height = 55; // ✅ 엑셀 기준 행 높이 55
        }

        // 구분(A:B, 세로 병합) - position_type 명칭
        sheet.mergeCells(blockStartRow, 1, blockEndRow, UTIL_RECORD_GUBUN_COL_END);
        const gubunCell = sheet.getCell(blockStartRow, 1);
        gubunCell.value = "유틸";
        gubunCell.alignment = { vertical: "middle", horizontal: "center" };

        // 성명(C, 세로 병합)
        sheet.mergeCells(blockStartRow, UTIL_RECORD_NAME_COL, blockEndRow, UTIL_RECORD_NAME_COL);
        const nameCell = sheet.getCell(blockStartRow, UTIL_RECORD_NAME_COL);
        nameCell.value = m.name;
        nameCell.alignment = { vertical: "middle", horizontal: "center" };

        // 일자별(D~AH) 각 열도 C열과 동일하게 블록 내 세로 병합
        for (let colNo = UTIL_RECORD_FIRST_DAY_COL; colNo <= UTIL_RECORD_LAST_DAY_COL; colNo += 1) {
          sheet.mergeCells(blockStartRow, colNo, blockEndRow, colNo);
        }

        // 휴무합계(AI)/비고(AJ)도 C열과 동일하게 블록 내 세로 병합
        sheet.mergeCells(
          blockStartRow,
          UTIL_RECORD_OFF_SUM_COL,
          blockEndRow,
          UTIL_RECORD_OFF_SUM_COL
        );
        sheet.mergeCells(blockStartRow, UTIL_RECORD_NOTE_COL, blockEndRow, UTIL_RECORD_NOTE_COL);

        const rangeStart = `${dayColLetter(UTIL_RECORD_FIRST_DAY_COL)}${blockStartRow}`;
        const rangeEnd = `${dayColLetter(UTIL_RECORD_LAST_DAY_COL)}${blockStartRow}`;
        sheet.getCell(blockStartRow, UTIL_RECORD_OFF_SUM_COL).value = {
          formula: `COUNTIF(${rangeStart}:${rangeEnd},"${UTIL_RECORD_OFF_TEXT}")`,
        };

        for (let r = blockStartRow; r <= blockEndRow; r += 1) {
          for (let c = 1; c <= lastCol; c += 1) {
            const cell = sheet.getCell(r, c);
            cell.border = utilRecordBorderThin;
            if (!cell.alignment) cell.alignment = { vertical: "middle", horizontal: "center" };
          }
        }
      });

      // ✅ AK1:AK100을 하나로 병합해, 화면 "거래처 검색"과 동일한 accountList의 거래처명을
      //    가나다순으로 줄바꿈해 이어서 왼쪽 위부터 한 번에 보이게 표시
      sheet.getColumn(accountNameRefCol).width = 40;
      sheet.mergeCells(1, accountNameRefCol, 300, accountNameRefCol);
      const accountNameRefCell = sheet.getCell(1, accountNameRefCol);
      accountNameRefCell.value = (accountNameRefList || [])
        .map((a) => a?.account_name ?? "")
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "ko"))
        .join("\n");
      accountNameRefCell.alignment = { vertical: "top", horizontal: "left", wrapText: true };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `유틸출근부서식_${year}년${month}월.xlsx`);
    } catch (e) {
      console.error(e);
      Swal.fire({ title: "실패", text: "서식 생성 중 오류가 발생했습니다.", icon: "error" });
    }
  };

  const handleUtilRecordModalOpen = async () => {
    const { isConfirmed, isDenied } = await Swal.fire({
      title: "유틸 출근부 등록",
      html: `<p style="text-align:left; margin:0; font-size:14px; line-height:2.2; color:#555;">
        ① <b>업로드 서식 다운로드</b>를 눌러 현재 연/월(${year}년 ${month}월) 기준 엑셀 서식을 받습니다.<br/>
        ② 일자별 칸(D열~)에 <b>근무한 거래처명</b>을 입력합니다. (예: 지안실버)<br/>
        ③ 근무하지 않은 날은 <b>"휴"</b>로 입력합니다.<br/>
        ④ <b>업로드</b>를 눌러 작성한 파일을 등록합니다.
      </p>`,
      showDenyButton: true,
      confirmButtonText: "업로드",
      denyButtonText: "업로드 서식 다운로드",
      showCancelButton: false,
      reverseButtons: true,
      confirmButtonColor: "#4CAF50",
      denyButtonColor: "#7066e0",
    });

    if (isDenied) {
      handleUtilRecordTemplateDownload();
    } else if (isConfirmed) {
      utilRecordFileInputRef.current?.click();
    }
  };

  // 엑셀에 입력된 텍스트를 거래처명과 매칭(완전일치 우선, 부분일치 보조)
  // ✅ 후보가 여럿이거나 하나도 없으면 자동으로 실패 처리하지 않고,
  //    status(exact/ambiguous/none)와 candidates를 돌려줘서 업로드 화면에서 사용자가 직접 고르게 한다.
  const matchUtilRecordAccount = (text) => {
    const q = String(text ?? "").trim();
    if (!q) return { status: "none", account: null, candidates: [] };
    const list = accountList || [];
    const qLower = q.toLowerCase();

    // ✅ 입력값이 거래처명과 1:1로 완전히 똑같으면(예: "한결" = 계정명 "한결") 다른 후보가 있어도
    //    바로 그 계정으로 확정한다. 완전일치가 없을 때만 LIKE 검색처럼 후보를 모아 선택하게 한다.
    const exact = list.find((a) => String(a?.account_name || "").toLowerCase() === qLower);
    if (exact) return { status: "exact", account: exact, candidates: [exact] };

    // ✅ LIKE 검색처럼 이름에 포함되는 후보를 전부 모은다.
    //    (예: "계양" 입력 시 "계양고은"/"계양효중" 둘 다 후보로 뜨게)
    const containsCandidates = list.filter((a) =>
      String(a?.account_name || "")
        .toLowerCase()
        .includes(qLower)
    );
    if (containsCandidates.length === 1) {
      return { status: "exact", account: containsCandidates[0], candidates: containsCandidates };
    }
    if (containsCandidates.length > 1)
      return { status: "ambiguous", account: null, candidates: containsCandidates };

    // 거래처명이 입력 텍스트에 포함되는 경우(예: "한결소래점"처럼 뒤에 부가 텍스트가 붙은 경우)
    const reverseCandidates = list.filter(
      (a) => a?.account_name && qLower.includes(String(a.account_name).toLowerCase())
    );
    if (reverseCandidates.length === 1) {
      return { status: "exact", account: reverseCandidates[0], candidates: reverseCandidates };
    }
    if (reverseCandidates.length > 1) {
      return { status: "ambiguous", account: null, candidates: reverseCandidates };
    }

    return { status: "none", account: null, candidates: [] };
  };

  // 사용자가 작성한 유틸 출근부 엑셀 파일을 읽어 서버에 저장
  const handleUtilRecordUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // 같은 파일 재업로드 허용을 위해 input 초기화

    setUtilRecordUploading(true);
    showLoadingModal("유틸 출근부 업로드 중...");
    try {
      // ✅ 회원ID 숨김 컬럼 없이 C열 성명으로만 매칭하므로, 업로드 시점 기준 최신 유틸(6) 직원
      //    목록을 다시 조회해 이름 → member_id 매핑을 만든다. 동명이인은 매칭 실패로 처리한다.
      const utilMemberRes = await api.get("/Account/AccountUtilMemberList", {
        params: { position_type: 6 },
      });
      const utilMembers = extractArray(utilMemberRes.data);
      const nameToMemberId = new Map();
      const memberIdToName = new Map(); // ✅ 결과 안내에서 member_id → 이름 표시용
      const membersByName = new Map(); // ✅ 동명이인 선택 시 후보 목록으로 사용
      const duplicateNames = new Set();
      (utilMembers || []).forEach((m) => {
        const nm = String(m?.name ?? "").trim();
        if (!nm) return;
        if (m?.member_id) memberIdToName.set(m.member_id, nm);
        if (!membersByName.has(nm)) membersByName.set(nm, []);
        membersByName.get(nm).push(m);
        if (nameToMemberId.has(nm)) {
          duplicateNames.add(nm);
        } else {
          nameToMemberId.set(nm, m.member_id);
        }
      });

      const arrayBuffer = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(arrayBuffer);
      const sheet = wb.worksheets[0];

      // D2에 입력된 기준일자('YYYY-MM-01')로 등록 대상 연/월을 판단
      const baseDateRaw = sheet.getCell(2, UTIL_RECORD_FIRST_DAY_COL).value;
      const baseDateText =
        baseDateRaw instanceof Date
          ? dayjs(baseDateRaw).format("YYYY-MM-DD")
          : String(baseDateRaw ?? "").trim();
      const baseDate = dayjs(baseDateText);

      if (!baseDate.isValid()) {
        Swal.fire("서식 오류", "D2(기준일자) 값을 확인해주세요. 예: 2026-08-01", "warning");
        return;
      }

      const recYear = baseDate.year();
      const recMonth = baseDate.month() + 1;
      const recDaysInMonth = baseDate.daysInMonth();

      const rows = []; // 즉시 확정되는 행(휴무, 완전/단일 매칭)
      const pendingEntries = []; // 거래처명이 애매하거나 매칭 안 되어 사용자 선택이 필요한 행
      const unresolvedTextMap = new Map(); // cellText → 후보 거래처 목록(빈 배열이면 후보 없음)
      const skippedNoMemberRows = []; // 성명 매칭 실패(미등록)로 건너뛴 행
      const duplicateNameItemsMap = new Map(); // 동명이인 placeholder key → { name, filledDays, candidates }

      sheet.eachRow((row, rowNumber) => {
        if (rowNumber <= 4) return; // 1~4행은 제목/헤더

        const nameCell = row.getCell(UTIL_RECORD_NAME_COL);
        // ✅ 직원 1명당 3행이 세로 병합되어 있고, exceljs는 병합된 각 행에서도
        //    eachRow 콜백을 호출하면서 마스터 셀 값을 그대로 돌려준다.
        //    병합 블록의 첫 행(마스터 행)에서만 처리해야 데이터가 3배로 중복 저장되지 않는다.
        if (nameCell.isMerged && nameCell.master?.row !== rowNumber) return;

        const name = String(nameCell.value ?? "").trim();
        if (!name) return; // 빈 행

        // ✅ 동명이인/미등록으로 건너뛸 때, 실제로 며칠치 데이터가 입력되어 있었는지 같이 알려준다.
        const countFilledDays = () => {
          let cnt = 0;
          for (let d = 1; d <= UTIL_RECORD_DAY_COLS; d += 1) {
            if (d > recDaysInMonth) continue;
            const colNo = UTIL_RECORD_FIRST_DAY_COL + d - 1;
            if (String(row.getCell(colNo).value ?? "").trim()) cnt += 1;
          }
          return cnt;
        };

        let memberId;
        if (duplicateNames.has(name)) {
          const filledDays = countFilledDays();
          if (filledDays === 0) return; // 입력된 데이터가 없으면 선택받을 필요 없이 그냥 건너뜀

          // ✅ 동명이인이라도 입력된 날짜가 있으면 버리지 않고, 실제 어느 회원인지 사용자가 선택하게 한다.
          const dupKey = `__DUP__${name}__${rowNumber}`;
          duplicateNameItemsMap.set(dupKey, {
            name,
            filledDays,
            candidates: membersByName.get(name) || [],
          });
          memberId = dupKey;
        } else {
          const foundMemberId = nameToMemberId.get(name);
          if (!foundMemberId) {
            skippedNoMemberRows.push(`${name}(미등록, ${countFilledDays()}일 입력됨)`);
            return;
          }
          memberId = foundMemberId;
        }

        const noteCellText = String(row.getCell(UTIL_RECORD_NOTE_COL).value ?? "").trim();

        for (let d = 1; d <= UTIL_RECORD_DAY_COLS; d += 1) {
          if (d > recDaysInMonth) continue; // 해당 월에 없는 날짜는 무시

          const colNo = UTIL_RECORD_FIRST_DAY_COL + d - 1;
          const cellText = String(row.getCell(colNo).value ?? "").trim();
          if (!cellText) continue; // 입력 없는 날은 건너뜀(등록 대상 아님)

          if (cellText === UTIL_RECORD_OFF_TEXT) {
            rows.push({
              record_year: recYear,
              record_month: recMonth,
              record_date: d,
              type: 0, // 0 = 휴무
              account_id: "", // ✅ PK 제약상 NULL 불가 → 빈 문자열을 "미배정"으로 사용
              member_id: memberId,
              is_present: "N",
              note: "",
            });
            continue;
          }

          const matchResult = matchUtilRecordAccount(cellText);
          if (matchResult.status === "exact") {
            rows.push({
              record_year: recYear,
              record_month: recMonth,
              record_date: d,
              type: 7, // 7 = 유틸 근무(거래처 배정)
              account_id: matchResult.account.account_id,
              member_id: memberId,
              is_present: "Y",
              note: noteCellText || "",
            });
          } else {
            // ✅ 거래처명이 하나로 정해지지 않으면(동명 후보 여럿/매칭 실패) 자동으로 실패 처리하지 말고
            //    업로드 화면에서 사용자가 직접 거래처를 선택하도록 보류한다.
            if (!unresolvedTextMap.has(cellText)) {
              unresolvedTextMap.set(cellText, matchResult.candidates || []);
            }
            pendingEntries.push({
              record_year: recYear,
              record_month: recMonth,
              record_date: d,
              member_id: memberId,
              note: noteCellText || "",
              cellText,
            });
          }
        }
      });

      if (rows.length === 0 && pendingEntries.length === 0) {
        Swal.fire("업로드할 데이터가 없습니다.", "일자별로 입력된 값이 없습니다.", "info");
        return;
      }

      const userId = localStorage.getItem("user_id") || "";
      const saveMeta = { recYear, recMonth, skippedNoMemberRows, userId, memberIdToName };

      if (unresolvedTextMap.size > 0 || duplicateNameItemsMap.size > 0) {
        // ✅ 사용자가 선택을 마칠 때까지 저장을 미루고, 확정에 필요한 정보를 보관해둔다.
        // (로딩창은 닫지 않고 바로 아래 모달을 띄운다 — 모달이 로딩창 위에 그대로 겹쳐 보이는 걸
        //  막기 위해 로딩창만 닫아야 하므로 여기서는 close()가 필요하다)
        Swal.close();
        utilRecordUploadContextRef.current = { rows, pendingEntries, ...saveMeta };
        setUtilRecordAmbiguousItems(
          Array.from(unresolvedTextMap.entries()).map(([text, candidates]) => ({
            text,
            count: pendingEntries.filter((p) => p.cellText === text).length,
            candidates,
            selectedAccountId: "",
          }))
        );
        setUtilRecordDuplicateItems(
          Array.from(duplicateNameItemsMap.entries()).map(([key, info]) => ({
            key,
            ...info,
            selectedMemberId: "",
          }))
        );
        setUtilRecordAmbiguousOpen(true);
        return;
      }

      await saveUtilRecordRows(rows, saveMeta, new Set());
    } catch (err) {
      console.error(err);
      Swal.fire("업로드 실패", err?.message || "오류가 발생했습니다.", "error");
    } finally {
      setUtilRecordUploading(false);
    }
  };

  // ✅ 사용자가 선택한 거래처를 pendingEntries에 반영해 최종 저장용 행으로 변환
  //    거래처를 선택하지 않은("매칭 안함") 항목은 저장하지 않고 건너뛴다.
  const resolveUtilRecordPendingEntries = (pendingEntries, resolutionMap) => {
    const finalRows = [];
    const unmatchedAccountTexts = new Set(); // 거래처를 선택하지 않아 저장되지 않고 건너뛴 값

    (pendingEntries || []).forEach((p) => {
      const accountId = resolutionMap.get(p.cellText) || "";
      if (accountId) {
        finalRows.push({
          record_year: p.record_year,
          record_month: p.record_month,
          record_date: p.record_date,
          type: 7, // 7 = 유틸 근무(거래처 배정)
          account_id: accountId,
          member_id: p.member_id,
          is_present: "Y",
          note: p.note,
        });
      } else {
        // ✅ 거래처를 선택하지 않으면 저장 자체를 하지 않는다(비고로만 저장하지 않음)
        unmatchedAccountTexts.add(p.cellText);
      }
    });

    return { finalRows, unmatchedAccountTexts };
  };

  // ✅ 최종 확정된 행을 서버에 저장하고 결과 안내
  const saveUtilRecordRows = async (finalRows, meta, unmatchedAccountTexts) => {
    const { recYear, recMonth, skippedNoMemberRows, userId, memberIdToName } = meta;

    if (!finalRows || finalRows.length === 0) {
      Swal.fire("업로드할 데이터가 없습니다.", "일자별로 입력된 값이 없습니다.", "info");
      return;
    }

    const res = await api.post("/Account/AccountUtilRecordExcelSave", {
      rows: finalRows.map((r) => ({ ...r, user_id: userId })),
    });
    const result = typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};

    // ✅ 전체 등록 건수 대신, 실제로 궁금한 "누가 며칠 출근하고 며칠 휴무인지"를 인원별로 보여준다.
    const memberSummary = new Map(); // member_id → { work, off }
    finalRows.forEach((r) => {
      const cur = memberSummary.get(r.member_id) || { work: 0, off: 0 };
      if (Number(r.type) === 0) cur.off += 1; // 0 = 휴무
      else cur.work += 1; // 7 = 거래처 근무
      memberSummary.set(r.member_id, cur);
    });

    let html = `<div style="text-align:left; margin-bottom:10px; font-size:13px; color:#888;">총 ${finalRows.length
      }건 처리 &nbsp;|&nbsp; 등록: ${result.savedCount ?? finalRows.length}건</div>`;
    if (memberSummary.size > 0) {
      const summaryLines = Array.from(memberSummary.entries())
        .map(([mid, s]) => [memberIdToName?.get(mid) || mid, s])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0]), "ko"))
        .map(([name, s]) => `${name}: 출근 ${s.work}일 / 휴무 ${s.off}일`)
        .join("<br/>");
      html += `<div style="text-align:left; margin-bottom:8px; font-size:13px; color:#555;">📋 등록 현황<br/>${summaryLines}</div>`;
    }
    if (skippedNoMemberRows.length > 0) {
      html += `<div style="text-align:left; margin-bottom:8px; font-size:13px; color:#e67e22;">⚠ 회원ID 없음 - 건너뜀 (${skippedNoMemberRows.length
        }건)<br/>${skippedNoMemberRows.join(", ")}</div>`;
    }
    if (unmatchedAccountTexts.size > 0) {
      html += `<div style="text-align:left; font-size:13px; color:#e67e22;">⚠ 거래처를 선택하지 않아 저장되지 않고 건너뛴 값<br/>${Array.from(
        unmatchedAccountTexts
      ).join(", ")}</div>`;
    }

    Swal.fire({ title: "유틸 출근부 등록 결과", html, icon: "success" });

    // 현재 조회 중인 화면과 같은 연/월이면 즉시 재조회
    if (Number(year) === recYear && Number(month) === recMonth) {
      await fetchAllData?.();
    }
  };

  const closeUtilRecordAmbiguousModal = useCallback(() => {
    setUtilRecordAmbiguousOpen(false);
    setUtilRecordAmbiguousItems([]);
    setUtilRecordDuplicateItems([]);
    utilRecordUploadContextRef.current = null;
  }, []);

  const handleUtilRecordAmbiguousSelect = useCallback((text, accountId) => {
    setUtilRecordAmbiguousItems((prev) =>
      prev.map((it) => (it.text === text ? { ...it, selectedAccountId: accountId } : it))
    );
  }, []);

  const handleUtilRecordDuplicateSelect = useCallback((key, memberId) => {
    setUtilRecordDuplicateItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, selectedMemberId: memberId } : it))
    );
  }, []);

  // ✅ 동명이인 placeholder member_id를 사용자가 선택한 실제 member_id로 치환.
  //    선택하지 않으면(미선택) 해당 이름의 입력분은 저장하지 않고 건너뛴다.
  const resolveUtilRecordDuplicateMembers = (rows, pendingEntries, duplicateItems) => {
    const resolutionMap = new Map(duplicateItems.map((it) => [it.key, it.selectedMemberId]));
    const isDupKey = (mid) => typeof mid === "string" && mid.startsWith("__DUP__");

    const resolvedRows = [];
    (rows || []).forEach((r) => {
      if (!isDupKey(r.member_id)) {
        resolvedRows.push(r);
        return;
      }
      const resolvedId = resolutionMap.get(r.member_id);
      if (resolvedId) resolvedRows.push({ ...r, member_id: resolvedId });
    });

    const resolvedPending = [];
    (pendingEntries || []).forEach((p) => {
      if (!isDupKey(p.member_id)) {
        resolvedPending.push(p);
        return;
      }
      const resolvedId = resolutionMap.get(p.member_id);
      if (resolvedId) resolvedPending.push({ ...p, member_id: resolvedId });
    });

    const skippedDuplicateNames = duplicateItems
      .filter((it) => !it.selectedMemberId)
      .map((it) => `${it.name}(동명이인 미선택, ${it.filledDays}일 건너뜀)`);

    return { resolvedRows, resolvedPending, skippedDuplicateNames };
  };

  // ✅ 사용자가 애매한 거래처명/동명이인을 모두 선택(또는 미선택)한 뒤 최종 저장
  const handleUtilRecordAmbiguousConfirm = async () => {
    const ctx = utilRecordUploadContextRef.current;
    if (!ctx) return;

    const { resolvedRows, resolvedPending, skippedDuplicateNames } =
      resolveUtilRecordDuplicateMembers(ctx.rows, ctx.pendingEntries, utilRecordDuplicateItems);

    const resolutionMap = new Map(
      utilRecordAmbiguousItems.map((it) => [it.text, it.selectedAccountId])
    );
    const { finalRows, unmatchedAccountTexts } = resolveUtilRecordPendingEntries(
      resolvedPending,
      resolutionMap
    );
    const allRows = [...resolvedRows, ...finalRows];

    setUtilRecordAmbiguousOpen(false);
    setUtilRecordAmbiguousItems([]);
    setUtilRecordDuplicateItems([]);
    utilRecordUploadContextRef.current = null;

    setUtilRecordUploading(true);
    showLoadingModal("유틸 출근부 업로드 중...");
    try {
      await saveUtilRecordRows(
        allRows,
        {
          recYear: ctx.recYear,
          recMonth: ctx.recMonth,
          skippedNoMemberRows: [...(ctx.skippedNoMemberRows || []), ...skippedDuplicateNames],
          userId: ctx.userId,
          memberIdToName: ctx.memberIdToName,
        },
        unmatchedAccountTexts
      );
    } catch (err) {
      console.error(err);
      Swal.fire("업로드 실패", err?.message || "오류가 발생했습니다.", "error");
    } finally {
      setUtilRecordUploading(false);
    }
  };

  // =========================================================
  // ✅ 통합 출근부 로직
  // =========================================================
  const integrationAccountNameMap = useMemo(() => {
    const m = new Map();
    (accountList || []).forEach((a) => m.set(String(a.account_id), a.account_name));
    return m;
  }, [accountList]);

  // ✅ 오른쪽 업장 목록: 가운데 매핑 목록에 이미 있는 업장은 제외하고 보여준다
  const integrationAvailableAccountRows = useMemo(() => {
    const mappedIds = new Set((integrationMappingRows || []).map((r) => String(r.account_id ?? "")));
    return (accountList || []).filter((a) => !mappedIds.has(String(a.account_id ?? "")));
  }, [accountList, integrationMappingRows]);

  // ✅ "이번 달 이미 등록된 업장" / "등록할 업장 목록" 표시용 정렬(account_name 오름차순)
  const integrationMonthRecordRowsSorted = useMemo(() => {
    return [...(integrationMonthRecordRows || [])].sort((a, b) => {
      const nameA = integrationAccountNameMap.get(String(a.account_id ?? "")) ?? String(a.account_id ?? "");
      const nameB = integrationAccountNameMap.get(String(b.account_id ?? "")) ?? String(b.account_id ?? "");
      return nameA.localeCompare(nameB, "ko");
    });
  }, [integrationMonthRecordRows, integrationAccountNameMap]);

  const integrationMappingRowsSorted = useMemo(() => {
    return [...(integrationMappingRows || [])].sort((a, b) => {
      const nameA = integrationAccountNameMap.get(String(a.account_id ?? "")) ?? "";
      const nameB = integrationAccountNameMap.get(String(b.account_id ?? "")) ?? "";
      return nameA.localeCompare(nameB, "ko");
    });
  }, [integrationMappingRows, integrationAccountNameMap]);

  const fetchIntegrationMemberList = useCallback(async () => {
    const res = await api.get("/Account/AccountUtilMemberList", { params: { position_type: 7 } });
    return extractArray(res.data);
  }, []);

  const fetchIntegrationMappingList = useCallback(async (memberId) => {
    if (!memberId) return [];
    const res = await api.get("/Account/AccountUtilMappingList", { params: { member_id: memberId } });
    return extractArray(res.data);
  }, []);

  // ✅ 선택한 직원이 "이번 달"(조회 중인 year/month)에 tb_account_record에 재택근무(20)로
  //    이미 등록해 둔 업장을 확인용으로 조회(클릭/편집 불가, 참고 표시 전용)
  const fetchIntegrationMonthRecordAccounts = useCallback(
    async (memberId) => {
      if (!memberId) return [];
      const res = await api.get("/Account/AccountIntegrationHomeRecordMonthList", {
        params: { member_id: memberId, year, month },
      });
      return extractArray(res.data);
    },
    [year, month]
  );

  // ✅ Set 토글 헬퍼: 같은 값이 있으면 빼고, 없으면 넣는다(다중선택 체크박스 동작)
  const toggleSetValue = useCallback((set, value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }, []);

  const openIntegrationRecordModal = useCallback(async () => {
    setIntegrationRecordOpen(true);
    setIntegrationSelectedMember(null);
    setIntegrationMappingRows([]);
    setIntegrationSelectedMappingIds(new Set());
    setIntegrationSelectedAccountIds(new Set());
    setIntegrationMonthRecordRows([]);
    setIntegrationRecordLoading(true);
    try {
      const members = await fetchIntegrationMemberList();
      setIntegrationMemberRows(members || []);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류가 발생했습니다.", "error");
    } finally {
      setIntegrationRecordLoading(false);
    }
  }, [fetchIntegrationMemberList]);

  const closeIntegrationRecordModal = useCallback(() => {
    setIntegrationRecordOpen(false);
  }, []);

  const handleSelectIntegrationMember = useCallback(
    async (row) => {
      setIntegrationSelectedMember(row);
      setIntegrationSelectedMappingIds(new Set());
      setIntegrationSelectedAccountIds(new Set());

      const memberId = row?.member_id;
      if (!memberId) {
        setIntegrationMappingRows([]);
        setIntegrationMonthRecordRows([]);
        return;
      }

      setIntegrationMonthRecordLoading(true);
      try {
        const [mappings, monthRecords] = await Promise.all([
          fetchIntegrationMappingList(memberId),
          fetchIntegrationMonthRecordAccounts(memberId),
        ]);
        setIntegrationMappingRows(mappings || []);
        setIntegrationMonthRecordRows(monthRecords || []);
      } catch (err) {
        Swal.fire("조회 실패", err?.message || "오류가 발생했습니다.", "error");
      } finally {
        setIntegrationMonthRecordLoading(false);
      }
    },
    [fetchIntegrationMappingList, fetchIntegrationMonthRecordAccounts]
  );

  // ✅ 가운데(매핑) 목록 행 다중선택 토글
  const handleToggleIntegrationMappingSelected = useCallback(
    (accountId) => {
      setIntegrationSelectedMappingIds((prev) => toggleSetValue(prev, String(accountId ?? "")));
    },
    [toggleSetValue]
  );

  // ✅ 오른쪽(업장) 목록 행 다중선택 토글
  const handleToggleIntegrationAccountSelected = useCallback(
    (accountId) => {
      setIntegrationSelectedAccountIds((prev) => toggleSetValue(prev, String(accountId ?? "")));
    },
    [toggleSetValue]
  );

  // ✅ 오른쪽(업장)에서 선택된 항목 전부 → 가운데(매핑)로 이동
  //    화면 표시용일 뿐, tb_account_util_member_mapping에는 저장하지 않는다
  const handleIntegrationAddSelectedAccounts = useCallback(() => {
    if (!integrationSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 통합 직원을 먼저 선택해주세요.", "info");
      return;
    }
    if (!integrationSelectedAccountIds || integrationSelectedAccountIds.size === 0) {
      Swal.fire("안내", "오른쪽에서 추가할 업장을 먼저 선택해주세요.", "info");
      return;
    }

    // ✅ 오른쪽 목록은 이미 매핑에 없는 업장만 노출되므로 중복 걱정 없이 그대로 옮기면 되지만,
    //    선택 이후 데이터가 바뀌었을 수도 있어 한 번 더 방어적으로 걸러낸다.
    const existingIds = new Set((integrationMappingRows || []).map((r) => String(r.account_id ?? "")));
    const toAdd = (accountList || []).filter(
      (a) =>
        integrationSelectedAccountIds.has(String(a.account_id ?? "")) &&
        !existingIds.has(String(a.account_id ?? ""))
    );

    if (toAdd.length === 0) {
      setIntegrationSelectedAccountIds(new Set());
      return;
    }

    const newRows = toAdd.map((a) => ({
      idx: null,
      account_id: a.account_id,
      member_id: integrationSelectedMember.member_id,
      name: integrationSelectedMember.name ?? "",
      position_type: integrationSelectedMember.position_type ?? "7",
    }));

    setIntegrationMappingRows((prev) => [...newRows, ...(prev || [])]);
    setIntegrationSelectedAccountIds(new Set());
  }, [integrationSelectedMember, integrationSelectedAccountIds, integrationMappingRows, accountList]);

  // ✅ 가운데(매핑)에서 선택된 항목 전부 → 오른쪽(업장)으로 이동
  //    마찬가지로 화면 표시만 바뀌고 저장되지 않는다
  const handleIntegrationRemoveSelectedMappings = useCallback(() => {
    if (!integrationSelectedMappingIds || integrationSelectedMappingIds.size === 0) {
      Swal.fire("안내", "가운데 목록에서 뺄 업장을 먼저 선택해주세요.", "info");
      return;
    }
    setIntegrationMappingRows((prev) =>
      (prev || []).filter((r) => !integrationSelectedMappingIds.has(String(r.account_id ?? "")))
    );
    setIntegrationSelectedMappingIds(new Set());
  }, [integrationSelectedMappingIds]);

  // ✅ 등록: 가운데 매핑 목록의 업장마다, 조회 중인 연/월의 평일(공휴일 제외)에 재택근무(20) 등록
  //    - 유틸 출근부와 동일한 API를 재사용하므로, 같은 직원+연/월 기존 데이터는 삭제 후 재삽입된다.
  const handleIntegrationRegister = useCallback(async () => {
    if (!integrationSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 통합 직원을 먼저 선택해주세요.", "info");
      return;
    }
    if (!integrationMappingRows || integrationMappingRows.length === 0) {
      Swal.fire("안내", "매핑 목록에 등록할 업장이 없습니다.", "info");
      return;
    }

    const weekdayDays = [];
    for (let d = 1; d <= daysInMonth; d += 1) {
      const dow = dayjs(
        `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      ).day();
      if (dow === 0 || dow === 6) continue; // 주말 제외
      if (holidayDays.has(d)) continue; // 공휴일 제외
      weekdayDays.push(d);
    }

    if (weekdayDays.length === 0) {
      Swal.fire("안내", "등록할 평일이 없습니다.", "info");
      return;
    }

    const confirm = await Swal.fire({
      title: "통합 출근부 등록",
      html: `${year}년 ${month}월, 선택된 <b>${integrationMappingRows.length}개 업장</b>에<br/>평일(공휴일 제외) 재택근무로 등록합니다.<br/><br/>이번 달 기존 등록 내용은 삭제되고 새로 등록됩니다.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "등록",
      cancelButtonText: "취소",
    });
    if (!confirm.isConfirmed) return;

    const memberId = integrationSelectedMember.member_id;

    // ✅ tb_account_record insert(AccountMemberRecordSave)가 요구하는 컬럼만 채운다.
    //    재택근무(20)는 출퇴근 시간 입력칸이 없는 타입이라 start/end_time은 비워둔다.
    const rows = [];
    integrationMappingRows.forEach((m) => {
      weekdayDays.forEach((d) => {
        rows.push({
          record_year: year,
          record_month: month,
          record_date: d,
          type: 20, // 재택근무
          account_id: m.account_id,
          member_id: memberId,
          start_time: "",
          end_time: "",
          note: "",
          salary: 0,
        });
      });
    });

    setIntegrationRecordRegistering(true);
    try {
      await api.post("/Account/AccountIntegrationHomeRecordSave", { rows });

      Swal.fire(
        "등록 완료",
        `${integrationSelectedMember.name ?? ""} 님의 ${year}년 ${month}월 재택근무가 등록되었습니다.`,
        "success"
      );

      // ✅ 등록 후에도 모달은 열어둔 채로 "이번달 등록된 업장"을 새로고침해서
      //    방금 등록한 내용이 반영됐는지 바로 확인할 수 있게 한다.
      setIntegrationMonthRecordLoading(true);
      try {
        const monthRecords = await fetchIntegrationMonthRecordAccounts(memberId);
        setIntegrationMonthRecordRows(monthRecords || []);
      } finally {
        setIntegrationMonthRecordLoading(false);
      }

      await fetchAllData?.();
    } catch (err) {
      Swal.fire("등록 실패", err?.message || "오류가 발생했습니다.", "error");
    } finally {
      setIntegrationRecordRegistering(false);
    }
  }, [
    integrationSelectedMember,
    integrationMappingRows,
    year,
    month,
    daysInMonth,
    holidayDays,
    fetchAllData,
    fetchIntegrationMonthRecordAccounts,
  ]);

  // ✅ 유형 키가 여러 이름으로 올 수 있어 통일
  const pickType = (src) =>
    safeTrim(
      src?.type ??
      src?.record_type ??
      src?.work_type ??
      src?.recordType ??
      src?.workType ??
      src?.work_kind ??
      src?.work_cd ??
      "",
      ""
    );

  const toDayNumber = (v) => {
    if (v == null) return NaN;
    if (typeof v === "number") return v;
    const s = String(v).trim();
    if (!s) return NaN;
    // 날짜 문자열(연-월-일 형식, 시간 포함)
    const m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})([T\s].*)?$/);
    if (m) return Number(m[3]);
    // 날짜 문자열(연월일 8자리)
    if (/^\d{8}$/.test(s)) return Number(s.slice(6, 8));
    // 문자열 안에 연-월-일 패턴이 있으면 사용
    const m2 = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (m2) return Number(m2[3]);
    // 마지막 숫자 chunk 사용 (예: "2026-02-24 00:00:00.0")
    const m3 = s.match(/(\d{1,2})(?!.*\d)/);
    if (m3) return Number(m3[1]);
    const n = Number(s);
    return Number.isNaN(n) ? NaN : n;
  };

  const getDispatchDedupKey = (r, y, m, dayNum, t) => {
    const id =
      r?.record_id ??
      r?.recordId ??
      r?.record_seq ??
      r?.recordSeq ??
      r?.id ??
      r?.seq ??
      r?.idx ??
      "";
    const idStr = safeTrim(id, "");
    if (idStr) return `id_${idStr}`;

    const mid = safeTrim(r?.member_id ?? r?.memberId ?? "", "");
    const start = safeTrim(r?.start_time ?? r?.start ?? "", "");
    const end = safeTrim(r?.end_time ?? r?.end ?? "", "");
    const sal = safeTrim(r?.salary ?? "", "");
    return `${mid}_${y}_${m}_${dayNum}_${t}_${start}_${end}_${sal}`;
  };

  // =========================
  // ✅ 2) day 소스 찾기 (pivot/obj/arr)
  // =========================
  const getDaySource = (item, d) => {
    if (!item) return null;
    const key = `day_${d}`;

    if (item[key]) return item[key];

    if (item.days && typeof item.days === "object" && !Array.isArray(item.days)) {
      if (item.days[key]) return item.days[key];
      if (item.days[d]) return item.days[d];
    }

    if (Array.isArray(item.days)) {
      const found =
        item.days.find((x) => toDayNumber(x?.record_date) === d) ||
        item.days.find((x) => toDayNumber(x?.record_day) === d) ||
        item.days.find((x) => toDayNumber(x?.day) === d) ||
        item.days.find((x) => toDayNumber(x?.date) === d);
      if (found) return found;
    }

    const key2 = `day_${String(d).padStart(2, "0")}`;
    if (item[key2]) return item[key2];
    if (item.days && typeof item.days === "object" && item.days[key2]) return item.days[key2];

    return null;
  };

  // =========================
  // ✅ 3) long 형태를 pivot 형태로 변환
  // =========================
  const normalizeSheetRows = (rows, daysInMonthArg) => {
    const arr = Array.isArray(rows) ? rows : [];
    if (arr.length === 0) return [];

    const sample = arr[0] || {};
    const keys = Object.keys(sample);

    const hasPivotDayKey = keys.some((k) => /^day_\d+$/.test(k));
    const hasDaysField = arr.some(
      (r) => r?.days && typeof r.days === "object" && !Array.isArray(r.days)
    );
    if (hasPivotDayKey || hasDaysField) return arr;

    const hasLongDay = arr.some(
      (r) => r?.record_date != null || r?.record_day != null || r?.day != null || r?.date != null
    );

    if (!hasLongDay) return arr;

    const map = new Map();

    for (const r of arr) {
      const mid = safeTrim(r?.member_id ?? r?.memberId ?? "", "");
      if (!mid) continue;

      if (!map.has(mid)) {
        map.set(mid, {
          name: r.name,
          account_id: r.account_id,
          member_id: mid,
          position: r.position || "",
          del_yn: r.del_yn ?? "",
          del_dt: safeTrim(r.del_dt ?? "", ""),
          act_join_dt: safeTrim(r.act_join_dt ?? "", ""),
          cor_type: safeTrim(r?.cor_type ?? r?.corType ?? "", ""),
          gubun: r.gubun ?? "nor",
          position_type: r.position_type ?? "",
          day_default: r.day_default || null,
        });
      }

      const g = map.get(mid);
      const dayNum = toDayNumber(r.record_date ?? r.record_day ?? r.day ?? r.date);

      if (dayNum >= 1 && dayNum <= daysInMonthArg) {
        g[`day_${dayNum}`] = { ...r };
      }
    }

    return Array.from(map.values());
  };

  // =========================
  // ✅ 4) sheetRows -> attendanceRows
  // =========================
  const buildAttendanceRowsFromSheet = (
    sheetRowsArg,
    memberRowsArg,
    timesRowsArg,
    daysInMonthArg
  ) => {
    const normalizedSheetRows = normalizeSheetRows(sheetRowsArg, daysInMonthArg);

    const parseEmployDispatchAmount = (v) => {
      if (v == null || v === "") return "";
      const s = String(v);
      const matches = s.match(/([0-9][0-9,]*)/g);
      if (!matches || matches.length === 0) return "";
      const last = matches[matches.length - 1];
      const n = Number(String(last).replace(/,/g, ""));
      return Number.isNaN(n) ? "" : n;
    };

    const memberDispatchAmountMap = new Map();
    (memberRowsArg || []).forEach((m) => {
      const mid = safeTrim(m?.member_id ?? m?.memberId ?? "", "");
      if (!mid) return;
      const amt = parseEmployDispatchAmount(m?.employ_dispatch);
      if (amt !== "") memberDispatchAmountMap.set(String(mid), amt);
    });

    const memberRowsById = new Map();
    (memberRowsArg || []).forEach((m) => {
      const mid = safeTrim(m?.member_id ?? m?.memberId ?? "", "");
      if (!mid) return;
      memberRowsById.set(mid, m);
    });

    // ✅ member_id 기준 중복 제거 (동명이인/중복 row 병합)
    const isEmptyDay = (v) => {
      if (!v) return true;
      const t = safeTrim(v.type ?? "", "");
      const s = safeTrim(v.start_time ?? v.start ?? "", "");
      const e = safeTrim(v.end_time ?? v.end ?? "", "");
      const sal = safeTrim(v.salary ?? "", "");
      const note = safeTrim(v.note ?? v.note ?? "", "");
      return !t && !s && !e && !sal && !note;
    };

    const dedupedRows = (() => {
      const map = new Map();
      const passthrough = [];

      (normalizedSheetRows || []).forEach((item) => {
        const mid = safeTrim(item?.member_id ?? item?.memberId ?? "", "");
        if (!mid) {
          passthrough.push(item);
          return;
        }

        if (!map.has(mid)) {
          map.set(mid, { ...item, member_id: mid });
          return;
        }

        const target = map.get(mid);
        for (let d = 1; d <= daysInMonthArg; d++) {
          const key = `day_${d}`;
          const curr = target[key];
          const next = item[key];
          if (isEmptyDay(curr) && !isEmptyDay(next)) {
            target[key] = next;
          }
        }

        if (!target.day_default && item.day_default) target.day_default = item.day_default;
        if (!target.position && item.position) target.position = item.position;
        if (!target.cor_type && item.cor_type) target.cor_type = item.cor_type;
        if (!target.position_type && item.position_type) target.position_type = item.position_type;
        if (!target.gubun && item.gubun) target.gubun = item.gubun;
        if (!target.del_dt && item.del_dt) target.del_dt = item.del_dt;
        if (!target.act_join_dt && item.act_join_dt) target.act_join_dt = item.act_join_dt;
      });

      return [...map.values(), ...passthrough];
    })();

    const newAttendance = (dedupedRows || []).map((item) => {
      const memberId = safeTrim(item?.member_id ?? item?.memberId ?? "", "");
      const member = memberRowsById.get(memberId) || null;

      const baseGubun = safeTrim(item.gubun ?? item.day_default?.gubun, "nor");
      const baseCorType = safeTrim(
        item?.cor_type ?? item?.day_default?.cor_type ?? member?.cor_type ?? member?.corType ?? "",
        ""
      );
      const basePt = safeTrim(item.position_type ?? item.day_default?.position_type, "");

      const base = {
        name: item.name,
        account_id: item.account_id,
        member_id: memberId || item.member_id,
        position: item.position || member?.position || "",
        cor_type: baseCorType,
        del_yn: item.del_yn ?? member?.del_yn ?? "",
        del_dt: safeTrim(item.del_dt ?? member?.del_dt ?? "", ""),
        act_join_dt: safeTrim(item.act_join_dt ?? member?.act_join_dt ?? "", ""),
        gubun: baseGubun,
        position_type: basePt,
        day_default: item.day_default || null,
      };

      const dayEntries = {};
      for (let d = 1; d <= daysInMonthArg; d++) {
        const key = `day_${d}`;
        const source = getDaySource(item, d) || item[key] || null;

        const t = pickType(source);
        // const memberDispatchAmount = memberDispatchAmountMap.get(String(item.member_id)) ?? "";
        // const isEmployeeDispatch = String(t) === "6" || String(t) === "직원파출";

        dayEntries[key] = source
          ? {
            ...source,
            type: t,
            gubun: safeTrim(source.gubun, baseGubun),
            cor_type: safeTrim(source?.cor_type ?? source?.corType ?? baseCorType, baseCorType),
            position_type: safeTrim(source.position_type, basePt),
            start: source.start_time || source.start || "",
            end: source.end_time || source.end || "",
            start_time: source.start_time || "",
            end_time: source.end_time || "",
            salary: source.salary || "",
            note: source.note ?? source.note ?? "",
            pay_yn:
              safeTrim(source.pay_yn ?? source.payYn ?? "", "").toUpperCase() === "Y" ? "Y" : "N",
          }
          : {
            account_id: item.account_id,
            member_id: memberId || item.member_id,
            gubun: baseGubun,
            cor_type: baseCorType,
            position_type: basePt,
            type: "",
            start: "",
            end: "",
            start_time: "",
            end_time: "",
            salary: "",
            note: "",
            pay_yn: "N",
          };
      }

      return { ...base, ...dayEntries };
    });

    const defaultTimesMap = {};
    (normalizedSheetRows || []).forEach((item) => {
      defaultTimesMap[item.member_id] = {
        start:
          item.day_default?.start_time ||
          (timesRowsArg || []).find((t) => t.member_id === item.member_id)?.start_time ||
          "",
        end:
          item.day_default?.end_time ||
          (timesRowsArg || []).find((t) => t.member_id === item.member_id)?.end_time ||
          "",
      };
    });

    return { attendanceRowsBuilt: newAttendance, defaultTimesMap };
  };

  // ✅ 거래처 1개에 대한 모든 데이터 조회 (엑셀 전체다운용)
  const fetchBundleForAccount = async (accountId, y, m) => {
    const [sheetRes, memberRes, timeRes, disN, disY] = await Promise.all([
      api.get("/Account/AccountRecordSheetList", {
        params: { account_id: accountId, year: y, month: m },
      }),
      api.get("/Account/AccountRecordMemberList", {
        params: { account_id: accountId, year: y, month: m },
      }),
      api.get("/Account/AccountMemberRecordTime", {
        params: { account_id: accountId, year: y, month: m },
      }),
      api.get("/Account/AccountRecordDispatchList", {
        params: { account_id: accountId, year: y, month: m, del_yn: "N" },
      }),
      api.get("/Account/AccountRecordDispatchList", {
        params: { account_id: accountId, year: y, month: m, del_yn: "Y" },
      }),
    ]);

    const sheetRowsArg = extractArray(sheetRes.data);
    const memberRowsArg = (extractArray(memberRes.data) || []).map((m) => ({
      ...m,
      member_id: safeTrim(m?.member_id ?? m?.memberId ?? "", ""),
      cor_type: safeTrim(m?.cor_type ?? m?.corType ?? "", ""),
    }));
    const timesRowsArg = extractArray(timeRes.data);

    const dispatchN = extractArray(disN.data);
    const dispatchY = extractArray(disY.data);
    const dispatchRowsArg = [
      ...(Array.isArray(dispatchN) ? dispatchN : []),
      ...(Array.isArray(dispatchY) ? dispatchY : []),
    ];

    return { sheetRowsArg, memberRowsArg, timesRowsArg, dispatchRowsArg };
  };

  // ✅ 엑셀 셀 출력 (4행 분리용)
  function splitDayCell(cell) {
    if (!cell) return ["", "", "", ""];

    // 문자열로 내려오는 경우 (이미 포맷된 셀) 처리
    if (typeof cell === "string") {
      const s = cell.trim();
      if (!s) return ["", "", "", ""];

      const lines = s
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      let typeLabel = lines[0] || "";
      let start = "";
      let end = "";
      let salary = "";

      // 2번째 줄에 시간 (예: 6:00~18:00 / 6:00-18:00)
      const timeLine = lines.find((l) => /~|-/.test(l)) || "";
      if (timeLine) {
        const m = timeLine.match(/(\d{1,2}:\d{2})\s*[-~]\s*(\d{1,2}:\d{2})/);
        if (m) {
          start = m[1];
          end = m[2];
        }
      }

      // 급여 라인
      const salaryLine = lines.find((l) => l.includes("급여")) || "";
      if (salaryLine) {
        const m = salaryLine.match(/급여[:\s]*([0-9,]+)/);
        if (m) salary = m[1];
      }

      return [typeLabel, start, end, salary];
    }

    const t = safeTrim(cell?.type, "");
    if (!t || t === "0") return ["", "", "", ""];

    const typeLabel = TYPE_LABEL[String(t)] ?? String(t);
    const start = cell.start || cell.start_time || "";
    const end = cell.end || cell.end_time || "";

    const salaryRaw = cell.salary != null && String(cell.salary).trim() !== "" ? cell.salary : "";
    const isDispatchType = String(t) === "5" || String(t) === "6";
    const payYn = String(cell?.pay_yn ?? "").toUpperCase() === "Y";
    const salary = isDispatchType ? formatMoneyLike(salaryRaw) : "";
    const salaryOut = salary && payYn ? `${salary} (지급)` : salary;

    return [typeLabel, start || "", end || "", salaryOut || ""];
  }

  // ✅ 출근현황(전체)용: 지급 표기 없이 급여 숫자만
  function splitDayCellAttend(cell) {
    if (!cell) return ["", "", "", ""];

    const t = safeTrim(cell?.type, "");
    if (!t || t === "0") return ["", "", "", ""];

    const typeLabel = TYPE_LABEL[String(t)] ?? String(t);
    const start = cell.start || cell.start_time || "";
    const end = cell.end || cell.end_time || "";

    const salaryRaw = cell.salary != null && String(cell.salary).trim() !== "" ? cell.salary : "";
    const noteRaw = cell.note != null && String(cell.note).trim() !== "" ? cell.note : "";
    const isDispatchType = String(t) === "5" || String(t) === "6";
    // ✅ 파출은 salary, 초과/조기퇴근/대체휴무 등은 note 값을 4번째 줄에 반영
    let bottomValue = "";
    if (isDispatchType) {
      bottomValue = toNumberMaybe(salaryRaw);
    } else if (noteRaw !== "") {
      bottomValue = toNumberMaybe(noteRaw);
    } else if (salaryRaw !== "") {
      bottomValue = toNumberMaybe(salaryRaw);
    }

    return [typeLabel, start || "", end || "", bottomValue];
  }

  const EXCEL_ATTENDANCE_COLOR_TYPES = new Set(["3", "4", "5", "6", "16", "17", "19"]);
  const getExcelAttendanceFillArgb = (type) => {
    const key = safeTrim(type, "");
    if (!EXCEL_ATTENDANCE_COLOR_TYPES.has(key)) return "";
    const hex = String(typeColors[key] || "").trim();
    if (!/^#?[0-9a-fA-F]{6}$/.test(hex)) return "";
    const raw = hex.startsWith("#") ? hex.slice(1) : hex;
    return `FF${raw.toUpperCase()}`;
  };

  // ✅ 거래처 전체 엑셀 다운로드
  const handleExcelDownloadAllAccounts = async () => {
    if (excelDownloading) return;
    if (!accountList || accountList.length === 0) return;

    setExcelDownloading(true);

    try {
      Swal.fire({
        title: "엑셀 생성 중...",
        text: "거래처별 데이터를 조회하고 있습니다.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // 엑셀 워크북 생성
      const wb = new ExcelJS.Workbook();
      wb.creator = "RecordSheet";

      const rangeStart = excelRange?.start ? dayjs(excelRange.start) : null;
      const rangeEnd = excelRange?.end ? dayjs(excelRange.end) : null;
      const validStart = rangeStart && rangeStart.isValid();
      const validEnd = rangeEnd && rangeEnd.isValid();
      if (!validStart || !validEnd) {
        Swal.fire("기간 오류", "시작일/종료일을 올바르게 선택하세요.", "warning");
        return;
      }

      const startDate = rangeStart.startOf("day");
      const endDate = rangeEnd.startOf("day");
      const realStart = startDate.isBefore(endDate) ? startDate : endDate;
      const realEnd = startDate.isBefore(endDate) ? endDate : startDate;
      const rangeLabel = `${realStart.format("YYYY-MM-DD")} ~ ${realEnd.format("YYYY-MM-DD")}`;

      const filename = `출근부_전체거래처_${rangeLabel}.xlsx`;

      const accountNameMap = new Map(
        (accountList || []).map((a) => [String(a.account_id), safeTrim(a.account_name ?? "", "")])
      );

      // 시트 구성: 본문/검증/요약/파출
      const wsAttend = wb.addWorksheet("출근현황");
      const wsAttendValidation = wb.addWorksheet("출근부 검증");
      const wsAttendSummary = wb.addWorksheet("출근현황요약");
      const wsDispatch = wb.addWorksheet("파출정보");
      const wsDispatchSummary = wb.addWorksheet("파출정보 요약");
      const thinBorder = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      const headerFill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF0F0F0" },
      };
      const headerAlignment = { vertical: "middle", horizontal: "center", wrapText: true };
      const dataAlignment = { vertical: "top", horizontal: "left", wrapText: true };
      const attendDataAlignment = { vertical: "middle", horizontal: "center", wrapText: true };

      const addSectionTitle = (ws, title, colCount) => {
        ws.addRow([title]);
        const r = ws.lastRow.number;
        ws.mergeCells(r, 1, r, colCount);
        const cell = ws.getCell(r, 1);
        cell.font = { bold: true, size: 12 };
        cell.alignment = { vertical: "middle", horizontal: "left" };
        ws.getRow(r).height = 20;
      };

      const styleHeaderRow = (ws, rowNum) => {
        const row = ws.getRow(rowNum);
        row.font = { bold: true };
        row.alignment = headerAlignment;
        row.eachCell((cell) => {
          cell.border = thinBorder;
          cell.fill = headerFill;
        });
      };

      const styleDataRow = (ws, rowNum) => {
        const row = ws.getRow(rowNum);
        row.alignment = dataAlignment;
        row.eachCell((cell) => {
          cell.border = thinBorder;
        });
      };

      const styleAttendDataRow = (ws, rowNum) => {
        const row = ws.getRow(rowNum);
        row.alignment = attendDataAlignment;
        row.eachCell((cell) => {
          cell.border = thinBorder;
        });
      };

      // 다운로드 기간에 포함되는 월 목록
      const monthsInRange = [];
      let cursor = realStart.startOf("month");
      const endCursor = realEnd.startOf("month");
      while (cursor.isBefore(endCursor) || cursor.isSame(endCursor)) {
        monthsInRange.push({ y: cursor.year(), m: cursor.month() + 1 });
        cursor = cursor.add(1, "month");
      }

      // 다운로드 기간의 일자 목록 (헤더/본문 공통 사용)
      const dateList = [];
      let dcur = realStart.clone();
      while (dcur.isBefore(realEnd) || dcur.isSame(realEnd, "day")) {
        dateList.push(dcur.clone());
        dcur = dcur.add(1, "day");
      }
      const dateKeysInRange = dateList.map((d) => d.format("YYYY-MM-DD"));
      const monthDateKeysMap = new Map(
        monthsInRange.map(({ y, m }) => {
          const paddedMonth = String(m).padStart(2, "0");
          const daysInThisMonth = dayjs(`${y}-${paddedMonth}-01`).daysInMonth();
          const keys = Array.from(
            { length: daysInThisMonth },
            (_, idx) => `${y}-${paddedMonth}-${String(idx + 1).padStart(2, "0")}`
          );
          return [`${y}-${paddedMonth}`, keys];
        })
      );

      const attendColCount = 2 + dateList.length;
      const attendHeader = ["거래처", "직원명", ...dateList.map((d) => `${d.format("M/D")}`)];
      const attendRightHeader = [
        "업장",
        "직원명",
        "직책",
        "근로일수",
        "직원파출",
        "초과",
        "결근",
        "비고",
      ];
      const attendRightWidths = [
        24, // 업장
        18, // 직원명
        12, // 직책
        25, // 근로일수
        35, // 직원파출
        15, // 초과
        15, // 결근
        50, // 비고
      ];
      const dispatchRightHeader = [
        "업장",
        "직원명",
        "주민등록번호",
        "계좌번호",
        "파출횟수",
        "파출비",
        "파출비소계",
        "비고",
      ];
      const dispatchRightWidths = [
        14, // 업장
        12, // 직원명
        18, // 주민등록번호
        28, // 계좌번호
        12, // 파출횟수
        18, // 파출비
        18, // 파출비소계
        40, // 비고
      ];

      // 거래처명은 최대 길이에 맞춰 자동 폭 계산
      const accountNameVisualLen = (s) =>
        Array.from(String(s || "")).reduce(
          (sum, ch) => sum + (/[\u0000-\u007f]/.test(ch) ? 1 : 2),
          0
        );
      const maxAccountNameLen = Math.max(
        accountNameVisualLen("거래처"),
        ...(accountList || []).map((a) => accountNameVisualLen(a?.account_name || ""))
      );
      const accountNameWidth = maxAccountNameLen + 2;

      // 출근현황 시트 기본 컬럼
      wsAttend.columns = [
        { width: accountNameWidth },
        { width: 14 },
        ...Array.from({ length: dateList.length }, () => ({ width: 10 })),
      ];

      const dispatchHeader = [
        "거래처",
        "이름",
        "연락처",
        "주민등록번호",
        "은행",
        "계좌정보",
        "급여",
        "지급여부",
        "삭제여부(del_yn)",
      ];

      wsDispatch.columns = [
        { width: 18 },
        { width: 12 },
        { width: 14 },
        { width: 18 },
        { width: 18 },
        { width: 28 },
        { width: 14 },
        { width: 12 },
        { width: 14 },
      ];

      addSectionTitle(wsDispatch, `■ 파출정보 / ${rangeLabel}`, dispatchHeader.length);
      wsDispatch.addRow(dispatchHeader);
      styleHeaderRow(wsDispatch, wsDispatch.lastRow.number);

      const allDispatchRows = new Map();
      const dispatchRightRowsAll = [];
      const attendValidationMap = new Map();

      let globalDispatchTotal = 0;
      const employeeRightRowsAll = [];
      const employeeRightRowMap = new Map();
      const employeeDispatchSheetRowsAll = [];

      // 출근부 검증 시트 헤더/컬럼폭
      const validationHeader = [
        "거래처",
        "이름",
        "결근",
        "결근시간",
        "초과",
        "초과시간",
        "직원파출",
        "직원파출 금액",
        "파출",
        "파출비",
        "구분",
      ];
      wsAttendValidation.columns = [
        { width: 15 }, // 거래처(아래에서 자동 재조정)
        { width: 15 }, // 이름
        { width: 11 }, // 결근
        { width: 11 }, // 결근시간
        { width: 11 }, // 초과
        { width: 11 }, // 초과시간
        { width: 11 }, // 직원파출
        { width: 15 }, // 직원파출 금액
        { width: 11 }, // 파출
        { width: 15 }, // 파출비
        { width: 15 }, // 구분
      ];
      wsAttendValidation.addRow(validationHeader);
      styleHeaderRow(wsAttendValidation, wsAttendValidation.lastRow.number);

      const mergeEmployeeRow = (target, next) => {
        const keys = [
          "name",
          "position",
          "working_day",
          "employ_dispatch",
          "over_work",
          "non_work",
          "note",
        ];
        keys.forEach((k) => {
          const v = next[k];
          if (v != null && v !== "") target[k] = v;
        });
      };

      const collectEmployeeRows = (rows, accId, accName) => {
        (rows || []).forEach((m) => {
          const mid = m?.member_id ?? m?.memberId ?? m?.id ?? m?.name;
          if (!mid) return;
          const key = `${accId}::${mid}`;
          const rowData = {
            account_id: accId,
            account_name: accName,
            member_id: mid,
            name: m?.name || "",
            position: m?.position || "",
            working_day: m?.working_day ?? "",
            employ_dispatch: m?.employ_dispatch ?? "",
            over_work: m?.over_work ?? "",
            non_work: m?.non_work ?? "",
            note: m?.note ?? "",
          };
          const existing = employeeRightRowMap.get(key);
          if (!existing) {
            employeeRightRowMap.set(key, rowData);
            employeeRightRowsAll.push(rowData);
          } else {
            mergeEmployeeRow(existing, rowData);
          }
        });
      };

      // 거래처별로 월 데이터를 모아 출근현황/검증/요약 시트를 구성
      for (let i = 0; i < accountList.length; i++) {
        const acc = accountList[i];
        const accId = acc.account_id;
        const accName = acc.account_name || accId;
        const accNameOnly = safeTrim(acc.account_name ?? "", "");

        const employeeDispatchSummaryMap = new Map();
        let dispatchMappingRowsArg = null;

        wsAttend.addRow(attendHeader);
        styleHeaderRow(wsAttend, wsAttend.lastRow.number);

        const memberMap = new Map(); // 회원 아이디 → { 이름, 셀: { [날짜키]: 셀 } }
        const memberInfoMap = new Map(); // 회원 아이디 → { rrn, account_number, position_type, name }
        const rrnByNameMap = new Map(); // 이름 정규키 → rrn(숫자만) Set
        const dispatchSummaryMap = new Map(); // 회원 아이디 → 요약
        const ensureValidationRow = (
          memberId,
          name = "",
          corType = "",
          delYn = "N",
          delDt = ""
        ) => {
          const key = `${accId}::${String(memberId)}`;
          const existing = attendValidationMap.get(key);
          if (existing) {
            if (!existing.name && name) existing.name = name;
            if (!existing.cor_type && corType != null && corType !== "") {
              existing.cor_type = String(corType);
            }
            if (!existing.gubun && corType != null && corType !== "") {
              existing.gubun = String(corType);
            }
            if (!existing.del_dt && delDt) existing.del_dt = delDt;
            if (!existing.del_yn && delYn != null) existing.del_yn = delYn;
            return existing;
          }

          const created = {
            key,
            account_name: accNameOnly,
            name: name || "",
            member_id: String(memberId),
            cor_type: corType != null ? String(corType) : "",
            gubun: corType != null ? String(corType) : "",
            del_yn: delYn ?? "N",
            del_dt: delDt || "",
            non_work_count: 0,
            non_work_hours: 0,
            over_work_count: 0,
            over_work_hours: 0,
            employee_dispatch_count: 0,
            employee_dispatch_amount: 0,
            dispatch_count: 0,
            dispatch_amount: 0,
          };
          attendValidationMap.set(key, created);
          return created;
        };

        for (let mi = 0; mi < monthsInRange.length; mi++) {
          const { y, m } = monthsInRange[mi];
          const monthKey = `${y}-${String(m).padStart(2, "0")}`;
          const monthDateKeys = monthDateKeysMap.get(monthKey) || [];
          const daysInThisMonth = monthDateKeys.length;

          const { sheetRowsArg, memberRowsArg, timesRowsArg, dispatchRowsArg } =
            await fetchBundleForAccount(accId, y, m);

          // ✅ 출근현황 우측 직원정보 수집(월별 → 중복 병합)
          collectEmployeeRows(memberRowsArg, accId, accName);

          // ✅ 출근부 검증 시트 기본행(인원 1명당 1행)
          (memberRowsArg || []).forEach((m) => {
            const mid = m?.member_id ?? m?.memberId ?? m?.id;
            if (!mid) return;
            ensureValidationRow(
              mid,
              m?.name || "",
              m?.cor_type ?? "",
              m?.del_yn ?? "N",
              safeTrim(m?.del_dt ?? "", "")
            );
          });

          // ✅ account_members 정보(주민번호/계좌/이름) 확보 + 이름별 주민번호 맵 구축
          (memberRowsArg || []).forEach((m) => {
            const mid = m?.member_id ?? m?.memberId ?? m?.id;
            if (!mid) return;
            const key = String(mid);
            const prev = memberInfoMap.get(key) || {
              rrn: "",
              account_number: "",
              position_type: m?.position_type ?? m?.positionType ?? "",
              name: m?.name || "",
            };
            if (!prev.rrn) prev.rrn = m?.rrn || "";
            if (!prev.account_number) prev.account_number = m?.account_number || "";
            if (!prev.position_type) {
              prev.position_type = m?.position_type ?? m?.positionType ?? "";
            }
            if (!prev.name) prev.name = m?.name || "";
            memberInfoMap.set(key, prev);

            const nameKey = safeTrim(m?.name ?? "", "").replace(/\s+/g, "");
            const rrnDigits = safeTrim(m?.rrn ?? "", "").replace(/\D/g, "");
            if (nameKey && rrnDigits) {
              const rrnSet = rrnByNameMap.get(nameKey) || new Set();
              rrnSet.add(rrnDigits);
              rrnByNameMap.set(nameKey, rrnSet);
            }
          });

          // ✅ 파출 기본 정보(이름/주민번호/계좌) 확보
          (dispatchRowsArg || []).forEach((d) => {
            const mid = d?.member_id ?? d?.memberId;
            if (!mid) return;
            const key = String(mid);
            const prev = dispatchSummaryMap.get(key) || {
              member_id: key,
              account_name: accName,
              name: "",
              rrn: "",
              account_number: "",
              dispatchCount: 0,
              dispatchPays: [],
              dispatchTotal: 0,
              hasEmployeeDispatch: false,
            };
            prev.name = prev.name || d.name || "";
            prev.rrn = prev.rrn || d.rrn || "";
            prev.account_number = prev.account_number || d.account_number || "";
            dispatchSummaryMap.set(key, prev);
          });

          const monthStart = dayjs(`${y}-${String(m).padStart(2, "0")}-01`);
          const monthEnd = monthStart.endOf("month");
          const startInMonth =
            (realStart.isAfter(monthStart) || realStart.isSame(monthStart, "day")) &&
            (realStart.isBefore(monthEnd) || realStart.isSame(monthEnd, "day"));
          const endInMonth =
            (realEnd.isAfter(monthStart) || realEnd.isSame(monthStart, "day")) &&
            (realEnd.isBefore(monthEnd) || realEnd.isSame(monthEnd, "day"));

          const fromDay = startInMonth ? realStart.date() : realStart.isAfter(monthEnd) ? null : 1;
          const toDay = endInMonth
            ? realEnd.date()
            : realEnd.isBefore(monthStart)
              ? null
              : daysInThisMonth;

          if (fromDay == null || toDay == null) {
            continue;
          }

          const { attendanceRowsBuilt } = buildAttendanceRowsFromSheet(
            sheetRowsArg,
            memberRowsArg,
            timesRowsArg,
            daysInThisMonth
          );

          (attendanceRowsBuilt || []).forEach((row) => {
            const mid = row.member_id || row.id || row.name;
            if (!mid) return;
            if (!memberMap.has(mid)) {
              memberMap.set(mid, {
                name: row.name || "",
                del_yn: row.del_yn ?? "N",
                del_dt: safeTrim(row.del_dt ?? "", ""),
                cells: {},
              });
            }
            const entry = memberMap.get(mid);
            if (!entry.del_dt && safeTrim(row.del_dt ?? "", "")) {
              entry.del_dt = safeTrim(row.del_dt ?? "", "");
            }
            if (!entry.del_yn && row.del_yn != null) {
              entry.del_yn = row.del_yn;
            }
            for (let d = 1; d <= daysInThisMonth; d++) {
              const key = `day_${d}`;
              const dateKey = monthDateKeys[d - 1];
              if (!dateKey) continue;
              entry.cells[dateKey] = row[key];
            }
          });

          const monthPayMap = new Map();
          const monthPayStatusMap = new Map();
          // 같은 사람이 소스별로 다른 member_id/rrn 포맷으로 내려와도 1회만 반영되도록
          // rrn(숫자정규화) 우선, rrn이 비어있으면 이름으로 보정
          const validationSeenByDate = new Set();

          (attendanceRowsBuilt || []).forEach((row) => {
            const mid = row?.member_id ?? row?.memberId ?? row?.id ?? row?.name;
            if (!mid) return;
            const validationRow = ensureValidationRow(
              mid,
              row?.name || "",
              row?.cor_type ?? "",
              row?.del_yn ?? "N",
              safeTrim(row?.del_dt ?? "", "")
            );

            for (let d = fromDay; d <= toDay; d++) {
              const cell = row?.[`day_${d}`];
              if (!cell) continue;
              const t = safeTrim(cell?.type ?? "", "");
              if (!t || t === "0") continue;

              const dedupMemberKey = safeTrim(mid, "");
              const nameKey = safeTrim(row?.name ?? "", "").replace(/\s+/g, "");
              const rrnRaw = safeTrim(memberInfoMap.get(dedupMemberKey)?.rrn ?? "", "");
              let rrnKey = rrnRaw.replace(/\D/g, "");
              if (!rrnKey && nameKey) {
                const rrnSet = rrnByNameMap.get(nameKey);
                if (rrnSet && rrnSet.size === 1) {
                  rrnKey = Array.from(rrnSet)[0];
                }
              }
              const personKey = rrnKey
                ? `rrn:${rrnKey}`
                : nameKey
                  ? `name:${nameKey}`
                  : `mid:${dedupMemberKey}`;
              const dedupKey = `${personKey}_${y}_${m}_${d}_${t}`;
              if (validationSeenByDate.has(dedupKey)) continue;
              validationSeenByDate.add(dedupKey);

              const sal = toNumberLike(cell?.salary);
              const noteHoursRaw = toFirstNumberMaybe(cell?.note ?? "");
              const overHours =
                noteHoursRaw === ""
                  ? calcDurationHours(
                    cell?.start_time ?? cell?.start ?? "",
                    cell?.end_time ?? cell?.end ?? ""
                  )
                  : Number(noteHoursRaw);

              if (t === "4") {
                validationRow.non_work_count += 1;
                validationRow.non_work_hours += 8;
              } else if (t === "3") {
                validationRow.over_work_count += 1;
                validationRow.over_work_hours += overHours;
              } else if (t === "6" || t === "직원파출") {
                validationRow.employee_dispatch_count += 1;
                validationRow.employee_dispatch_amount += sal;
              } else if (t === "5" || t === "파출") {
                validationRow.dispatch_count += 1;
                validationRow.dispatch_amount += sal;
                // 파출(type 5)만 (주)더채움(=1)로 강제, 그 외는 DB cor_type 유지
                validationRow.cor_type = "1";
                validationRow.gubun = "1";
              }

              if (!isDispatchTypeValue(t)) continue;

              const isEmployeeDispatch = t === "6" || t === "직원파출";
              if (isEmployeeDispatch) {
                const midKey = String(mid);
                const prev = employeeDispatchSummaryMap.get(midKey) || {
                  member_id: midKey,
                  name: row?.name || "",
                  count: 0,
                  amount: 0,
                };
                if (!prev.name) prev.name = row?.name || "";
                prev.count += 1;
                prev.amount += sal;
                employeeDispatchSummaryMap.set(midKey, prev);
                continue;
              }

              const midKey = String(mid);
              monthPayMap.set(mid, (monthPayMap.get(mid) || 0) + sal);
              globalDispatchTotal += sal;

              // ✅ 출근현황 시트 우측 파출 요약 (직원파출 제외)
              const prev = dispatchSummaryMap.get(midKey) || {
                member_id: midKey,
                account_name: accName,
                name: "",
                rrn: "",
                account_number: "",
                dispatchCount: 0,
                dispatchPays: [],
                dispatchPayFlags: [],
                dispatchTotal: 0,
                hasEmployeeDispatch: false,
              };
              prev.name = prev.name || row?.name || "";
              if (!Array.isArray(prev.dispatchPays)) prev.dispatchPays = [];
              if (!Array.isArray(prev.dispatchPayFlags)) prev.dispatchPayFlags = [];
              prev.dispatchCount += 1;
              prev.dispatchPays.push(sal);
              prev.dispatchPayFlags.push(
                String(cell?.pay_yn ?? cell?.payYn ?? "").toUpperCase() === "Y"
              );
              prev.dispatchTotal += sal;
              dispatchSummaryMap.set(midKey, prev);

              const payYn = String(cell?.pay_yn ?? cell?.payYn ?? "").toUpperCase() === "Y";
              const stat = monthPayStatusMap.get(mid) || { paid: 0, total: 0 };
              stat.total += 1;
              if (payYn) stat.paid += 1;
              monthPayStatusMap.set(mid, stat);
            }
          });

          (dispatchRowsArg || []).forEach((d) => {
            const mid = d.member_id;
            if (!mid) return;
            const paySum = monthPayMap.get(mid) || 0;
            if (!paySum || Number(paySum) <= 0) return;
            const key = String(mid);
            const prev = allDispatchRows.get(key) || {
              accNames: new Set(),
              member_id: mid,
              name: d.name || "",
              phone: d.phone || "",
              rrn: d.rrn || "",
              account_number: d.account_number || "",
              salary: d.salary ?? "",
              total: d.total ?? "",
              del_yn: d.del_yn ?? "N",
              period_pay: 0,
              paid_cnt: 0,
              total_cnt: 0,
            };
            prev.accNames.add(accName);
            // del_yn은 하나라도 N이면 N 유지
            if (String(prev.del_yn).toUpperCase() !== "N") {
              prev.del_yn = d.del_yn ?? prev.del_yn;
            }
            prev.period_pay += paySum;
            const stat = monthPayStatusMap.get(mid);
            if (stat) {
              prev.paid_cnt += stat.paid;
              prev.total_cnt += stat.total;
            }
            allDispatchRows.set(key, prev);
          });
        }

        // ✅ 직원파출이 있다면 account_members 기반으로 주민번호/계좌번호 보완
        dispatchSummaryMap.forEach((d, key) => {
          if (!d?.hasEmployeeDispatch) return;
          if (d.rrn && d.account_number) return;
          const info = memberInfoMap.get(String(key));
          if (!info) return;
          if (!d.rrn) d.rrn = info.rrn || d.rrn;
          if (!d.account_number) d.account_number = info.account_number || d.account_number;
        });

        const dispatchRowsForAccountRaw = Array.from(dispatchSummaryMap.values()).map((d) => {
          const payLines = (d.dispatchPays || []).map((p) => toNumberLike(p));
          const payFlags = Array.isArray(d.dispatchPayFlags) ? d.dispatchPayFlags : [];
          const totalLine = payLines.length > 0 ? toNumberLike(d.dispatchTotal) : "";
          return {
            account_name: d.account_name || accName,
            name: d.name || "",
            rrn: d.rrn || "",
            account_number: d.account_number || "",
            dispatchCount: d.dispatchCount || 0,
            dispatchPays: payLines,
            dispatchPayFlags: payFlags,
            dispatchTotal: totalLine,
            hasEmployeeDispatch: !!d.hasEmployeeDispatch,
          };
        });

        const dispatchRowsForAccount = dispatchRowsForAccountRaw.filter(
          (d) => (d.dispatchCount || 0) > 0 || (d.dispatchPays || []).length > 0
        );

        if (employeeDispatchSummaryMap.size > 0) {
          if (dispatchMappingRowsArg == null) {
            try {
              const mappingRes = await api.get("/Account/AccountMemberDispatchMappingList", {
                params: {
                  dispatch_account_id: accId,
                  record_year: year,
                  record_month: month,
                  del_yn: "N",
                },
              });
              dispatchMappingRowsArg = extractArray(mappingRes.data);
            } catch (err) {
              console.error("직원파출 매핑 조회 실패(엑셀):", err);
              dispatchMappingRowsArg = [];
            }
          }

          const employeeDispatchRowsForAccount = [];
          const seenKey = new Set();
          const seenMember = new Set();

          (dispatchMappingRowsArg || []).forEach((row) => {
            const mid = safeTrim(row?.member_id ?? row?.memberId ?? "", "");
            if (!mid) return;

            const stat = employeeDispatchSummaryMap.get(mid);
            if (!stat || (!stat.count && !stat.amount)) return;

            const recordDateRaw = safeTrim(row?.record_date ?? row?.recordDate ?? "", "");
            if (recordDateRaw) {
              const rd = dayjs(recordDateRaw);
              if (rd.isValid() && (rd.isBefore(realStart, "day") || rd.isAfter(realEnd, "day"))) {
                return;
              }
            }

            const originId = safeTrim(row?.account_id ?? row?.origin_account_id ?? "", "");
            const dispatchId = safeTrim(
              row?.dispatch_account_id ?? row?.dispatchAccountId ?? "",
              ""
            );
            if (dispatchId && dispatchId !== String(accId)) return;

            const info = parseEmployeeDispatchInfo(row?.employ_dispatch);
            const origin = safeTrim(
              row?.origin_account_name ??
              row?.origin_account ??
              accountNameMap.get(originId) ??
              info.origin ??
              "",
              ""
            );
            const dispatch = safeTrim(
              row?.dispatch_account_name ??
              row?.dispatch_account ??
              row?.dispatch_account_nm ??
              row?.dispatch_accountName ??
              accountNameMap.get(dispatchId) ??
              info.dispatch ??
              accName ??
              "",
              ""
            );
            const name = safeTrim(row?.name ?? row?.member_name ?? stat?.name ?? "", "");

            if (!name && !origin && !dispatch) return;

            const key = `${mid}::${origin || originId}::${dispatch || dispatchId}`;
            if (seenKey.has(key)) return;
            seenKey.add(key);
            seenMember.add(mid);

            employeeDispatchRowsForAccount.push({
              name,
              origin,
              dispatch,
              count: stat.count || 0,
              amount: stat.amount || 0,
            });
          });

          employeeDispatchSummaryMap.forEach((stat, midKey) => {
            if (seenMember.has(midKey)) return;
            if (!stat || (!stat.count && !stat.amount)) return;
            employeeDispatchRowsForAccount.push({
              name: stat.name || "",
              origin: "",
              dispatch: accName || "",
              count: stat.count || 0,
              amount: stat.amount || 0,
            });
          });

          employeeDispatchRowsForAccount.forEach((row) => {
            employeeDispatchSheetRowsAll.push(row);
          });
        }

        Array.from(memberMap.values())
          .filter((row) => shouldIncludeRetiredForExcel(row, realStart))
          .forEach((row) => {
            const startRow = wsAttend.lastRow.number + 1;

            const r1 = [accNameOnly, row.name || ""];
            const r2 = ["", ""];
            const r3 = ["", ""];
            const r4 = ["", ""];
            const dayTypes = [];

            dateKeysInRange.forEach((key) => {
              const cell = row.cells[key];
              const [v1, v2, v3, v4] = splitDayCellAttend(cell);
              dayTypes.push(safeTrim(cell?.type ?? "", ""));
              r1.push(v1);
              r2.push(v2);
              r3.push(v3);
              r4.push(v4);
            });

            wsAttend.addRow(r1);
            styleAttendDataRow(wsAttend, wsAttend.lastRow.number);
            wsAttend.addRow(r2);
            styleAttendDataRow(wsAttend, wsAttend.lastRow.number);
            wsAttend.addRow(r3);
            styleAttendDataRow(wsAttend, wsAttend.lastRow.number);
            wsAttend.addRow(r4);
            styleAttendDataRow(wsAttend, wsAttend.lastRow.number);

            // ✅ 전체 거래처 엑셀: 날짜 셀(4줄 블록) 배경색 적용
            dayTypes.forEach((type, idx) => {
              const argb = getExcelAttendanceFillArgb(type);
              if (!argb) return;
              const col = idx + 3;
              for (let r = startRow; r <= startRow + 3; r++) {
                wsAttend.getCell(r, col).fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb },
                };
              }
            });

            // ✅ 급여(4번째 줄) 숫자 포맷 적용
            const salaryRowNum = startRow + 3;
            for (let c = 3; c <= attendColCount; c++) {
              const cell = wsAttend.getCell(salaryRowNum, c);
              if (typeof cell.value === "number") {
                cell.numFmt = "#,##0";
              }
            }

            wsAttend.mergeCells(startRow, 1, startRow + 3, 1);
            wsAttend.mergeCells(startRow, 2, startRow + 3, 2);

            const accountCell = wsAttend.getCell(startRow, 1);
            accountCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

            const nameCell = wsAttend.getCell(startRow, 2);
            nameCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          });

        dispatchRowsForAccount.forEach((d) => {
          dispatchRightRowsAll.push({
            ...d,
            account_name: d.account_name || accName,
          });
        });

        wsAttend.addRow([]);
      }

      // 파출정보 시트 데이터 작성
      Array.from(allDispatchRows.values()).forEach((d) => {
        if (!d.period_pay || Number(d.period_pay) <= 0) return;
        const bank = extractBankName(d.account_number);
        const accountOnly = extractAccountOnly(d.account_number);
        const accName =
          d.accNames && d.accNames.size > 0 ? Array.from(d.accNames).join(", ") : d.accName || "";
        const pay = d.period_pay;
        const totalCnt = Number(d.total_cnt || 0);
        const paidCnt = Number(d.paid_cnt || 0);
        const payStatus =
          totalCnt > 0
            ? `${paidCnt === totalCnt ? "지급" : paidCnt === 0 ? "미지급" : "부분"
            }(${paidCnt}/${totalCnt})`
            : "";

        wsDispatch.addRow([
          accName,
          d.name,
          d.phone,
          d.rrn,
          bank,
          accountOnly,
          formatMoneyLike(pay),
          payStatus,
          d.del_yn,
        ]);
        const lastRowNum = wsDispatch.lastRow.number;
        const salaryCell = wsDispatch.getCell(lastRowNum, 7);
        const salaryNum = toNumberLike(pay);
        if (salaryNum) {
          salaryCell.value = salaryNum;
          salaryCell.numFmt = "#,##0";
        }
        styleDataRow(wsDispatch, lastRowNum);
      });

      wsAttend.views = [{ state: "frozen", xSplit: 2, ySplit: 1, topLeftCell: "C2" }];
      wsDispatch.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];

      // 출근부 검증 시트 데이터 작성
      const attendValidationRows = Array.from(attendValidationMap.values())
        .filter((r) => shouldIncludeRetiredForExcel(r, realStart))
        .sort((a, b) => {
          const accCmp = String(a.account_name || "").localeCompare(
            String(b.account_name || ""),
            "ko",
            {
              sensitivity: "base",
              numeric: true,
            }
          );
          if (accCmp !== 0) return accCmp;
          return String(a.name || "").localeCompare(String(b.name || ""), "ko", {
            sensitivity: "base",
            numeric: true,
          });
        });

      wsAttendValidation.getColumn(1).width = accountNameWidth;

      attendValidationRows.forEach((r) => {
        const nonWorkHours = Number((r.non_work_hours || 0).toFixed(2));
        const overWorkHours = Number((r.over_work_hours || 0).toFixed(2));
        const corType = safeTrim(r.cor_type ?? "", "");
        const gubun = safeTrim(r.gubun ?? corType, "");
        wsAttendValidation.addRow([
          r.account_name || "",
          r.name || "",
          r.non_work_count || 0,
          nonWorkHours,
          r.over_work_count || 0,
          overWorkHours,
          r.employee_dispatch_count || 0,
          r.employee_dispatch_amount || 0,
          r.dispatch_count || 0,
          r.dispatch_amount || 0,
          gubun === "1" ? "(주)더채움" : gubun === "" ? "" : "더채움",
        ]);

        const rowNum = wsAttendValidation.lastRow.number;
        styleDataRow(wsAttendValidation, rowNum);
        wsAttendValidation.getCell(rowNum, 3).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 4).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 5).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 6).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 7).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 8).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 9).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 10).numFmt = "#,##0";
        wsAttendValidation.getCell(rowNum, 1).alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        wsAttendValidation.getCell(rowNum, 2).alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        wsAttendValidation.getCell(rowNum, 11).alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        for (let c = 3; c <= 10; c++) {
          wsAttendValidation.getCell(rowNum, c).alignment = {
            vertical: "middle",
            horizontal: "right",
            wrapText: true,
          };
        }
      });
      wsAttendValidation.views = [{ state: "frozen", xSplit: 0, ySplit: 1 }];

      // 출근현황 우측 표를 출근현황요약 시트로 분리
      wsAttendSummary.columns = attendRightWidths.map((w) => ({ width: w }));
      addSectionTitle(wsAttendSummary, `■ 출근현황요약 / ${rangeLabel}`, attendRightHeader.length);
      wsAttendSummary.addRow(attendRightHeader);
      styleHeaderRow(wsAttendSummary, wsAttendSummary.lastRow.number);

      employeeRightRowsAll.forEach((d) => {
        const workingDayNum = toNumberMaybe(d.working_day);
        const overWorkNum = toNumberMaybe(d.over_work);
        const nonWorkNum = toNumberMaybe(d.non_work);
        wsAttendSummary.addRow([
          d.account_name || "",
          d.name || "",
          d.position || "",
          workingDayNum === "" ? "" : workingDayNum,
          d.employ_dispatch ?? "",
          overWorkNum === "" ? "" : overWorkNum,
          nonWorkNum === "" ? "" : nonWorkNum,
          d.note ?? "",
        ]);
        const rowNum = wsAttendSummary.lastRow.number;
        styleDataRow(wsAttendSummary, rowNum);
        const workingDayCell = wsAttendSummary.getCell(rowNum, 4);
        if (typeof workingDayCell.value === "number") workingDayCell.numFmt = "#,##0";
        const overWorkCell = wsAttendSummary.getCell(rowNum, 6);
        if (typeof overWorkCell.value === "number") overWorkCell.numFmt = "#,##0";
        const nonWorkCell = wsAttendSummary.getCell(rowNum, 7);
        if (typeof nonWorkCell.value === "number") nonWorkCell.numFmt = "#,##0";
        for (let c = 1; c <= attendRightHeader.length; c++) {
          const cell = wsAttendSummary.getCell(rowNum, c);
          cell.alignment = {
            vertical: "middle",
            horizontal: c === attendRightHeader.length ? "left" : "center",
            wrapText: true,
          };
        }
      });
      wsAttendSummary.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];

      // 파출정보 요약 시트 작성
      wsDispatchSummary.columns = dispatchRightWidths.map((w) => ({ width: w }));
      addSectionTitle(
        wsDispatchSummary,
        `■ 파출정보 요약 / ${rangeLabel}`,
        dispatchRightHeader.length
      );
      wsDispatchSummary.addRow(dispatchRightHeader);
      styleHeaderRow(wsDispatchSummary, wsDispatchSummary.lastRow.number);

      wsDispatchSummary.addRow(["합계", "", "", "", "", globalDispatchTotal || 0, "", ""]);
      const dispatchSummaryTotalRowNum = wsDispatchSummary.lastRow.number;
      styleDataRow(wsDispatchSummary, dispatchSummaryTotalRowNum);
      const dispatchSummaryTotalCell = wsDispatchSummary.getCell(dispatchSummaryTotalRowNum, 6);
      dispatchSummaryTotalCell.numFmt = "#,##0";
      for (let c = 1; c <= dispatchRightHeader.length; c++) {
        const cell = wsDispatchSummary.getCell(dispatchSummaryTotalRowNum, c);
        cell.alignment = {
          vertical: "middle",
          horizontal: c === dispatchRightHeader.length ? "left" : "center",
          wrapText: true,
        };
      }

      dispatchRightRowsAll.forEach((d) => {
        const payLines = d.dispatchPays || [];
        const totalLine = d.dispatchTotal || "";
        const totalRows = payLines.length > 0 ? payLines.length : 1;
        const payFlags = Array.isArray(d.dispatchPayFlags) ? d.dispatchPayFlags : [];

        for (let iLine = 0; iLine < totalRows; iLine++) {
          const amountVal = toNumberMaybe(payLines[iLine] ?? "");
          const isLastRow = iLine === totalRows - 1;
          const countVal = iLine === 0 ? toNumberMaybe(d.dispatchCount || 0) : "";
          const totalVal =
            isLastRow && totalLine !== "" && totalLine != null ? toNumberMaybe(totalLine) : "";
          const hasBaro =
            String(d.rrn ?? "").includes("바로인력") ||
            String(d.account_number ?? "").includes("바로인력");
          const remarkParts = [];
          if (d.hasEmployeeDispatch) remarkParts.push("직원파출");
          if (hasBaro) remarkParts.push("바로인력");

          wsDispatchSummary.addRow([
            iLine === 0 ? d.account_name || "" : "",
            iLine === 0 ? d.name : "",
            iLine === 0 ? d.rrn : "",
            iLine === 0 ? d.account_number : "",
            countVal === "" ? "" : countVal,
            amountVal === "" ? "" : amountVal,
            totalVal === "" ? "" : totalVal,
            iLine === 0 ? remarkParts.join(", ") : "",
          ]);

          const rowNum = wsDispatchSummary.lastRow.number;
          styleDataRow(wsDispatchSummary, rowNum);

          const countCell = wsDispatchSummary.getCell(rowNum, 5);
          if (typeof countCell.value === "number") countCell.numFmt = "#,##0";
          const amountCell = wsDispatchSummary.getCell(rowNum, 6);
          if (typeof amountCell.value === "number") amountCell.numFmt = "#,##0";
          if (payFlags[iLine] && amountCell.value !== "") {
            amountCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFF2CC" },
            };
          }

          const totalCell = wsDispatchSummary.getCell(rowNum, 7);
          if (typeof totalCell.value === "number") totalCell.numFmt = "#,##0";

          for (let c = 1; c <= dispatchRightHeader.length; c++) {
            const cell = wsDispatchSummary.getCell(rowNum, c);
            cell.alignment = {
              vertical: "middle",
              horizontal: c === dispatchRightHeader.length ? "left" : "center",
              wrapText: true,
            };
          }
        }
      });
      wsDispatchSummary.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];

      // 직원파출 집계가 있을 때만 별도 시트 생성
      if (employeeDispatchSheetRowsAll.length > 0) {
        const wsEmployeeDispatch = wb.addWorksheet("직원파출정보");
        const employeeDispatchHeader = ["직원명", "원소속", "파견업장", "횟수", "금액"];

        wsEmployeeDispatch.columns = [
          { width: 16 },
          { width: 24 },
          { width: 24 },
          { width: 10 },
          { width: 16 },
        ];

        addSectionTitle(
          wsEmployeeDispatch,
          `■ 직원파출정보 / ${rangeLabel}`,
          employeeDispatchHeader.length
        );
        wsEmployeeDispatch.addRow(employeeDispatchHeader);
        styleHeaderRow(wsEmployeeDispatch, wsEmployeeDispatch.lastRow.number);

        employeeDispatchSheetRowsAll.forEach((d) => {
          const countNum = toNumberMaybe(d.count);
          const amountNum = toNumberMaybe(d.amount);
          wsEmployeeDispatch.addRow([
            d.name || "-",
            d.origin || "-",
            d.dispatch || "-",
            countNum === "" ? "" : countNum,
            amountNum === "" ? "" : amountNum,
          ]);
          const rowNum = wsEmployeeDispatch.lastRow.number;
          styleDataRow(wsEmployeeDispatch, rowNum);
          const countCell = wsEmployeeDispatch.getCell(rowNum, 4);
          if (typeof countCell.value === "number") countCell.numFmt = "#,##0";
          const amountCell = wsEmployeeDispatch.getCell(rowNum, 5);
          if (typeof amountCell.value === "number") amountCell.numFmt = "#,##0";
        });

        wsEmployeeDispatch.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];
      }

      // 파일 저장
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);

      Swal.fire({ title: "완료", text: "엑셀 다운로드가 완료되었습니다.", icon: "success" });
    } catch (e) {
      console.error(e);
      Swal.fire({ title: "실패", text: "엑셀 생성 중 오류가 발생했습니다.", icon: "error" });
    } finally {
      setExcelDownloading(false);
    }
  };

  // ✅ "출근한 사람" 카운트 타입
  const COUNT_TYPES = new Set(["1", "2", "3", "5", "6", "7", "8", "19", "20"]);
  const isWorkingType = (cell) => {
    const t = safeTrim(cell?.type, "");
    if (!t || t === "0") return false;
    return COUNT_TYPES.has(t);
  };

  const dayWorkCounts = useMemo(() => {
    const counts = {};
    for (let d = 1; d <= daysInMonth; d++) counts[`day_${d}`] = 0;

    (attendanceRows || []).forEach((row) => {
      for (let d = 1; d <= daysInMonth; d++) {
        const key = `day_${d}`;
        if (isWorkingType(row?.[key])) counts[key] += 1;
      }
    });

    return counts;
  }, [attendanceRows, daysInMonth]);

  const buildDispatchFormData = useCallback(
    () => ({
      account_id: selectedAccountId,
      name: "",
      phone: "",
      rrn: "",
      account_number: "",
      position_type: "5", // ✅ 기본값(조리원)
      dispatch_account: "",
      note: "",
    }),
    [selectedAccountId]
  );

  // ✅ 모달 입력은 ref로 관리해서 키입력마다 전체 화면이 리렌더링되지 않게 처리
  const dispatchFormDataRef = useRef(buildDispatchFormData());

  const handleModalClose = useCallback(() => {
    // ✅ 닫을 때 기본값으로 초기화해 다음 등록 시 이전 입력값이 남지 않게 처리
    dispatchFormDataRef.current = buildDispatchFormData();
    setOpen(false);
  }, [buildDispatchFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = formatSensitiveFieldInputValue(name, value);
    if (e.target.value !== nextValue) e.target.value = nextValue;
    dispatchFormDataRef.current[name] = nextValue;
  };

  // ============================================================
  // ✅ 파출 state / snapshot / 레이스방지 / 로딩표시
  // ============================================================
  const [dispatchRows, setDispatchRows] = useState([]);
  const [originalDispatchRows, setOriginalDispatchRows] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchLoadedViewKey, setDispatchLoadedViewKey] = useState("");
  const [mappingLoadedViewKey, setMappingLoadedViewKey] = useState("");
  const [attendanceLoadedViewKey, setAttendanceLoadedViewKey] = useState("");
  const dispatchReqSeqRef = useRef(0);
  const [dispatchMemoOpen, setDispatchMemoOpen] = useState({});

  const getDispatchRowKey = useCallback(
    (row) => String(row?._rid ?? row?.dispatch_id ?? row?.member_id ?? row?.id ?? row?.name ?? ""),
    []
  );

  const toggleDispatchMemo = useCallback(
    (row) => {
      const key = getDispatchRowKey(row);
      if (!key) return;
      setDispatchMemoOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [getDispatchRowKey]
  );

  // ✅ 파출만 조회 (조회 직후 original 스냅샷도 같이 갱신 => 빨간글씨 초기화)
  const fetchDispatchOnly = useCallback(
    async (overrideDelYn) => {
      if (!selectedAccountId) return;

      const del_yn = overrideDelYn ?? dispatchDelFilter;
      const viewKey = `${String(selectedAccountId || "").trim()}_${year}_${month}`;
      const mySeq = ++dispatchReqSeqRef.current;

      setDispatchLoading(true);
      try {
        // ✅ 파출 정보 표는 근무시간 미입력 상태의 신규 파출직원도 바로 보여야 함
        //    백엔드에서 0회/0원 행까지 내려주면 여기서는 그대로 화면에 반영
        const res = await api.get("/Account/AccountRecordDispatchList", {
          params: { account_id: selectedAccountId, year, month, del_yn },
        });

        if (mySeq !== dispatchReqSeqRef.current) return;

        const list = extractArray(res.data);

        const mapped = (Array.isArray(list) ? list : []).map((item) =>
          ensureDispatchRid({
            ...item,
            account_id: item.account_id ?? selectedAccountId,
            member_id: item.member_id,
            name: item.name,
            rrn: item.rrn ?? "",
            account_number: item.account_number ?? "",
            total: item.total,
            salary: item.salary,
            note: item.note ?? item.memo ?? "",
            phone: item.phone ?? "",
            dispatch_account: item.dispatch_account ?? "",
            del_yn: item.del_yn ?? del_yn ?? "N",
            dispatch_id: item.dispatch_id ?? item.id,
          })
        );

        startTransition(() => {
          setDispatchRows(mapped);
          // ✅ 중요: 조회 직후 스냅샷도 동일하게 세팅해야 빨간글씨가 남지 않음
          setOriginalDispatchRows(mapped.map((r) => ({ ...r })));
          setDispatchMemoOpen({});
        });
        setDispatchLoadedViewKey(viewKey);
      } catch (err) {
        if (mySeq !== dispatchReqSeqRef.current) return;

        console.error("파출 재조회 실패:", err);
        setDispatchLoadedViewKey(viewKey);
        Swal.fire({
          title: "오류",
          text: "파출직원 조회 중 오류가 발생했습니다.",
          icon: "error",
        });
      } finally {
        if (mySeq === dispatchReqSeqRef.current) setDispatchLoading(false);
      }
    },
    [selectedAccountId, year, month, dispatchDelFilter]
  );

  // ✅ 직원파출 매핑만 조회 (우측 직원파출 정보 계산/표시용)
  const fetchDispatchMappingOnly = useCallback(async () => {
    const accId = String(selectedAccountId ?? "");
    if (!accId) {
      startTransition(() => {
        setDispatchMappingRows([]);
      });
      setMappingLoadedViewKey("");
      return;
    }

    const viewKey = `${accId.trim()}_${year}_${month}`;
    const mySeq = ++dispatchMappingReqSeqRef.current;
    try {
      const res = await api.get("/Account/AccountMemberDispatchMappingList", {
        // ✅ 직원파출관리에서 선택한 "해당 연/월/업장" 매핑만 조회
        params: {
          dispatch_account_id: accId,
          record_year: year,
          record_month: month,
          del_yn: "N",
        },
      });

      if (mySeq !== dispatchMappingReqSeqRef.current) return;

      const list = extractArray(res.data);
      startTransition(() => {
        setDispatchMappingRows(Array.isArray(list) ? list : []);
      });
      setMappingLoadedViewKey(viewKey);
    } catch (err) {
      if (mySeq !== dispatchMappingReqSeqRef.current) return;
      console.error("직원파출 매핑 조회 실패:", err);
      startTransition(() => {
        setDispatchMappingRows([]);
      });
      setMappingLoadedViewKey(viewKey);
    }
  }, [selectedAccountId, year, month]);

  // ✅ 저장 후 화면 동기화(부하 최소화): 작업 종류별로 필요한 조회만 수행
  const refreshRecordSheetViews = useCallback(
    async (mode = "all") => {
      if (!selectedAccountId) return;

      if (mode === "attendance") {
        // 출근부 저장 후: 본문(출근/직원) + 직원파출 매핑만 갱신
        await Promise.all([fetchAllData?.(), fetchDispatchMappingOnly()]);
        return;
      }

      if (mode === "dispatch") {
        // 파출 등록/수정/삭제 후: 파출 테이블만 갱신
        await fetchDispatchOnly(dispatchDelFilter);
        return;
      }

      // ✅ 같은 조회키 재조회 시에도 전체 로딩을 유지하려면 완료 키를 먼저 비움
      setEmployeeLoadedViewKey("");
      setAttendanceLoadedViewKey("");
      setDispatchLoadedViewKey("");
      setMappingLoadedViewKey("");
      await Promise.all([
        fetchAllData?.(),
        fetchDispatchOnly(dispatchDelFilter),
        fetchDispatchMappingOnly(),
      ]);
    },
    [
      selectedAccountId,
      fetchAllData,
      fetchDispatchOnly,
      dispatchDelFilter,
      fetchDispatchMappingOnly,
      setEmployeeLoadedViewKey,
      setAttendanceLoadedViewKey,
    ]
  );

  // ✅ 직원파출 매핑 저장용: member_id/일자 기준 원소속 account_id 추론
  const resolveOriginAccountIdForMember = useCallback(
    (memberId, dayNum, fallback = "") => {
      const mid = safeTrim(memberId, "");
      if (!mid) return safeTrim(fallback, "");

      const dispatchAccId = safeTrim(selectedAccountId, "");
      const list = (dispatchMappingRows || []).filter(
        (r) => safeTrim(r?.member_id ?? r?.memberId ?? "", "") === mid
      );

      const pickOrigin = (r) => safeTrim(r?.account_id ?? r?.origin_account_id ?? "", "");
      const pickDispatch = (r) =>
        safeTrim(r?.dispatch_account_id ?? r?.dispatchAccountId ?? "", "");

      const isSameYearMonth = (r) => {
        const rawDate = safeTrim(r?.record_date ?? r?.recordDate ?? "", "");
        if (rawDate && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
          const d = dayjs(rawDate);
          if (d.isValid()) return d.year() === Number(year) && d.month() + 1 === Number(month);
        }

        const ry = Number(r?.record_year ?? r?.recordYear ?? NaN);
        const rm = Number(r?.record_month ?? r?.recordMonth ?? NaN);
        if (Number.isFinite(ry) && Number.isFinite(rm)) {
          return ry === Number(year) && rm === Number(month);
        }
        return true;
      };

      const getDayNum = (r) => {
        const rawDate = safeTrim(r?.record_date ?? r?.recordDate ?? "", "");
        if (rawDate && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
          const d = dayjs(rawDate);
          if (d.isValid()) return d.date();
        }
        const n = Number(r?.record_date ?? r?.record_day ?? r?.day ?? r?.date);
        return Number.isFinite(n) ? n : null;
      };

      const sameDay = list.find((r) => {
        const origin = pickOrigin(r);
        if (!origin) return false;
        const disp = pickDispatch(r);
        if (dispatchAccId && disp && disp !== dispatchAccId) return false;
        if (!isSameYearMonth(r)) return false;
        return getDayNum(r) === Number(dayNum);
      });
      if (sameDay) return pickOrigin(sameDay);

      const sameMember = list.find((r) => {
        const origin = pickOrigin(r);
        if (!origin) return false;
        const disp = pickDispatch(r);
        if (dispatchAccId && disp && disp !== dispatchAccId) return false;
        return isSameYearMonth(r);
      });
      if (sameMember) return pickOrigin(sameMember);

      return safeTrim(fallback, "");
    },
    [dispatchMappingRows, selectedAccountId, year, month]
  );

  // ✅ 출근부에서 직원파출(type=6) 저장 시 매핑 테이블도 함께 저장
  const saveEmployeeDispatchMappings = useCallback(
    async (rows) => {
      const dispatchAccountId = String(selectedAccountId ?? "").trim();
      if (!dispatchAccountId) return;

      const candidates = (Array.isArray(rows) ? rows : []).filter(
        (r) =>
          String(r?.type ?? "") === "6" &&
          String(r?.member_id ?? "").trim() &&
          String(r?.account_id ?? "").trim() &&
          Number.isFinite(Number(r?.record_year)) &&
          Number.isFinite(Number(r?.record_month)) &&
          Number.isFinite(Number(r?.record_date))
      );
      if (candidates.length === 0) return;

      const buildMappingKey = (
        memberId,
        originAccountId,
        dispatchId,
        recordYear,
        recordMonth,
        recordDate
      ) =>
        [
          String(memberId ?? "").trim(),
          String(originAccountId ?? "").trim(),
          String(dispatchId ?? "").trim(),
          Number(recordYear),
          Number(recordMonth),
          Number(recordDate),
          "6",
        ].join("|");

      // ✅ 기존 매핑이 있으면 idx를 같이 보내서 수정 경로를 타게 함
      const existingIdxMap = new Map();
      (dispatchMappingRows || []).forEach((row) => {
        if (String(row?.del_yn ?? "N").toUpperCase() === "Y") return;

        const memberId = String(row?.member_id ?? row?.memberId ?? "").trim();
        const originAccountId = String(row?.account_id ?? row?.accountId ?? "").trim();
        const dispatchId = String(row?.dispatch_account_id ?? row?.dispatchAccountId ?? "").trim();
        let recordYear = Number(row?.record_year ?? row?.recordYear ?? NaN);
        let recordMonth = Number(row?.record_month ?? row?.recordMonth ?? NaN);
        let recordDate = Number(row?.record_day ?? row?.day ?? row?.date ?? NaN);
        const rawDate = safeTrim(row?.record_date ?? row?.recordDate ?? "", "");

        if (rawDate) {
          const parsed = dayjs(rawDate);
          if (parsed.isValid()) {
            recordYear = parsed.year();
            recordMonth = parsed.month() + 1;
            recordDate = parsed.date();
          }
        }

        if (!memberId || !originAccountId || !dispatchId) return;
        if (
          !Number.isFinite(recordYear) ||
          !Number.isFinite(recordMonth) ||
          !Number.isFinite(recordDate)
        )
          return;

        const key = buildMappingKey(
          memberId,
          originAccountId,
          dispatchId,
          recordYear,
          recordMonth,
          recordDate
        );
        existingIdxMap.set(key, row?.idx ?? null);
      });

      // ✅ 같은 직원/원소속/파견업장/연월일/type 조합은 1건만 전송
      const dedup = new Map();
      candidates.forEach((r) => {
        const key = buildMappingKey(
          r.member_id,
          r.account_id,
          dispatchAccountId,
          r.record_year,
          r.record_month,
          r.record_date
        );
        if (dedup.has(key)) return;
        dedup.set(key, {
          idx: existingIdxMap.get(key) ?? null,
          member_id: r.member_id,
          // ✅ account_id는 원소속 유지 (파견업장으로 대체 금지)
          account_id: r.account_id,
          dispatch_account_id: dispatchAccountId,
          name: r.name ?? "",
          position_type: r.position_type ?? "",
          // ✅ 매핑 저장 시 AccountMemberRecordSave 재호출로 급여가 덮어쓰여서,
          //    직원파출 급여를 같이 전달해 NULL 덮어쓰기를 방지
          salary: Number.isFinite(Number(r?.salary)) ? Number(r.salary) : 0,
          note: null,
          record_year: Number(r.record_year),
          record_month: Number(r.record_month),
          record_date: Number(r.record_date),
          type: 6,
          del_yn: "N",
        });
      });

      const payload = Array.from(dedup.values());
      if (payload.length === 0) return;

      const res = await api.post("/Account/AccountMemberDispatchMappingSave", payload);
      const ok = res?.status === 200 || Number(res?.data?.code) === 200;
      if (!ok) {
        throw new Error(res?.data?.message || "직원파출 매핑 저장 실패");
      }
    },
    [selectedAccountId, dispatchMappingRows]
  );

  const showLoadingModal = useCallback((title = "처리 중...") => {
    Swal.fire({
      title,
      text: "잠시만 기다려주세요.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
  }, []);

  // ✅ 파출등록 모달/파출이력 조회 저장
  const saveDispatchMember = useCallback(
    async (
      source,
      {
        loadingTitle = "저장 중...",
        successTitle = "저장",
        successText = "저장되었습니다.",
        closeModal,
      } = {}
    ) => {
      const payload = {
        account_id: selectedAccountId,
        member_id: source?.member_id ?? "",
        name: source?.name ?? "",
        phone: source?.phone ?? "",
        rrn: source?.rrn ?? "",
        account_number: source?.account_number ?? "",
        position_type: safeTrim(source?.position_type, "") || "5",
        dispatch_account: source?.dispatch_account ?? "",
        note: source?.note ?? "",
        del_yn: "N",
        record_year: year,
        record_month: month,
      };

      showLoadingModal(loadingTitle);

      try {
        const response = await api.post("/Account/AccountDispatchMemberSave", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        Swal.close();
        if (response?.data?.code === 200) {
          const result = await Swal.fire({
            title: successTitle,
            text: successText,
            icon: "success",
            confirmButtonColor: "#d33",
            confirmButtonText: "확인",
          });

          if (result.isConfirmed) {
            closeModal?.();
            // ✅ 저장 후 재조회 순서도 기존 파출등록 저장과 동일하게 맞춤
            await fetchAllData?.();
            await fetchDispatchOnly(dispatchDelFilter);
            await fetchDispatchMappingOnly();
          }
          return true;
        }

        Swal.fire("실패", response?.data?.message || "저장을 실패했습니다.", "error");
        return false;
      } catch (err) {
        Swal.close();
        Swal.fire("실패", err?.message || "저장을 실패했습니다.", "error");
        return false;
      }
    },
    [
      selectedAccountId,
      year,
      month,
      showLoadingModal,
      fetchAllData,
      fetchDispatchOnly,
      dispatchDelFilter,
      fetchDispatchMappingOnly,
    ]
  );

  // ✅ 이력 모달에서 선택한 인원을 현재 연월 파출인원으로 그대로 등록
  const handleDispatchImportRegister = useCallback(
    async (row) => {
      if (!row) return;
      if (isDispatchImportAlreadyRegistered(row)) return;

      const result = await Swal.fire({
        title: "파출인원 등록",
        text: "선택한 인원을 파출인원으로 등록하겠습니까?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "등록",
        cancelButtonText: "취소",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#9e9e9e",
      });
      if (!result.isConfirmed) return;

      await saveDispatchMember(row, {
        loadingTitle: "등록 중...",
        successTitle: "등록 완료",
        successText: "등록완료되었습니다.",
        closeModal: closeDispatchImportModal,
      });
    },
    [closeDispatchImportModal, isDispatchImportAlreadyRegistered, saveDispatchMember]
  );

  // ✅ click 대신 mousedown에서 바로 처리해 확인 모달 체감 지연을 줄임
  const handleDispatchImportRowMouseDown = useCallback(
    (event) => {
      if (event?.button != null && event.button !== 0) return;

      const rowEl = event?.target?.closest?.("tr[data-row-index]");
      if (!rowEl) return;

      const rowIndex = Number(rowEl.getAttribute("data-row-index"));
      if (!Number.isFinite(rowIndex)) return;

      const row = dispatchImportRows?.[rowIndex];
      if (!row) return;
      if (isDispatchImportAlreadyRegistered(row)) return;

      event.preventDefault?.();
      handleDispatchImportRegister(row);
    },
    [dispatchImportRows, handleDispatchImportRegister, isDispatchImportAlreadyRegistered]
  );

  // ✅ 모달 행 렌더를 메모화해서 출근부 다른 상태 변경 시 불필요한 재계산을 줄임
  const dispatchImportRowNodes = useMemo(
    () =>
      (dispatchImportRows || []).map((row, idx) => {
        const isLocked = isDispatchImportAlreadyRegistered(row);

        return (
          <tr
            key={`${safeTrim(row?.member_id, "") || "dispatch-import"}-${idx}`}
            data-row-index={idx}
            style={{
              cursor: isLocked ? "not-allowed" : "pointer",
              backgroundColor: isLocked ? "#e0e0e0" : "#ffffff",
              color: isLocked ? "#757575" : "#000000",
            }}
          >
            <td>{formatDispatchImportCellValue("name", row?.name)}</td>
            <td>{formatDispatchImportCellValue("phone", row?.phone)}</td>
            <td>{formatDispatchImportCellValue("rrn", row?.rrn)}</td>
            <td>{formatDispatchImportCellValue("account_number", row?.account_number)}</td>
            <td>{formatDispatchImportCellValue("dispatch_account", row?.dispatch_account)}</td>
            <td
              style={{
                textAlign: "left",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {formatDispatchImportCellValue("note", row?.note)}
            </td>
          </tr>
        );
      }),
    [dispatchImportRows, formatDispatchImportCellValue, isDispatchImportAlreadyRegistered]
  );

  // ✅ 핵심: year/month/selectedAccountId/filter 바뀌면 자동 재조회
  useEffect(() => {
    if (!selectedAccountId) return;
    fetchDispatchOnly(dispatchDelFilter);
  }, [selectedAccountId, year, month, dispatchDelFilter, fetchDispatchOnly]);

  useEffect(() => {
    fetchDispatchMappingOnly();
  }, [fetchDispatchMappingOnly]);

  // ✅ 파출 등록
  const handleSubmit = async () => {
    const formData = dispatchFormDataRef.current;

    if (!formData.name || !formData.rrn || !formData.account_number) {
      Swal.fire({
        title: "경고",
        text: "필수항목을 확인하세요.",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "확인",
      });
      return;
    }

    await saveDispatchMember(formData, {
      loadingTitle: "저장 중...",
      successTitle: "저장",
      successText: "저장되었습니다.",
      closeModal: handleModalClose,
    });
  };

  // ✅ 파출 삭제/복원 (즉시 저장)
  const handleToggleDispatch = useCallback(
    async (row) => {
      const cur = row?.del_yn ?? "N";
      const next = String(cur).toUpperCase() === "Y" ? "N" : "Y";
      const actionLabel = next === "Y" ? "삭제" : "복원";

      const confirm = await Swal.fire({
        title: `${actionLabel} 하시겠습니까?`,
        text:
          next === "Y"
            ? "삭제 처리되면 목록에서 제외될 수 있습니다."
            : "복원 처리하면 목록에 다시 표시됩니다.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "예",
        cancelButtonText: "아니오",
        confirmButtonColor: "#d33",
      });

      if (!confirm.isConfirmed) return;

      const member_id = row.member_id;

      if (!member_id) {
        Swal.fire({
          title: "오류",
          text: "파출직원 식별키(member_id 등)를 찾을 수 없습니다.",
          icon: "error",
          confirmButtonColor: "#d33",
          confirmButtonText: "확인",
        });
        return;
      }

      try {
        const fd = new FormData();
        fd.append("account_id", row.account_id ?? selectedAccountId ?? "");
        fd.append("member_id", member_id ?? "");
        fd.append("del_yn", next);
        fd.append("name", row.name ?? "");
        fd.append("rrn", row.rrn ?? "");
        fd.append("account_number", row.account_number ?? "");
        fd.append("total", row.total ?? "");
        fd.append("salary", row.salary ?? "");
        fd.append("phone", row.phone ?? "");
        fd.append("record_year", String(year));
        fd.append("record_month", String(month));

        const response = await api.post("/Account/AccountDispatchMemberSave", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data?.code === 200) {
          const result = await Swal.fire({
            title: "저장",
            text: `${actionLabel} 처리되었습니다.`,
            icon: "success",
            confirmButtonColor: "#d33",
            confirmButtonText: "확인",
          });

          if (result.isConfirmed) {
            await refreshRecordSheetViews("all");
          }
        } else {
          Swal.fire({
            title: "실패",
            text: `${actionLabel} 저장에 실패했습니다.`,
            icon: "error",
            confirmButtonColor: "#d33",
            confirmButtonText: "확인",
          });
        }
      } catch (e) {
        Swal.fire({
          title: "실패",
          text: `${actionLabel} 저장에 실패했습니다.`,
          icon: "error",
          confirmButtonColor: "#d33",
          confirmButtonText: "확인",
        });
      }
    },
    [refreshRecordSheetViews, selectedAccountId, year, month]
  );

  // ✅ originalMap (_rid 매칭)
  const originalDispatchMap = useMemo(() => {
    const m = new Map();
    (originalDispatchRows || []).forEach((r) => m.set(String(r._rid), r));
    return m;
  }, [originalDispatchRows]);

  const updateDispatchByRid = useCallback((rid, patch) => {
    setDispatchRows((prev) =>
      (prev || []).map((r) => (String(r._rid) === String(rid) ? { ...r, ...patch } : r))
    );
  }, []);

  // ✅ 파출 저장: 변경된 row만 전송
  const handleDispatchSave = useCallback(async () => {
    if (!selectedAccountId) return;

    const editableFields = ["phone", "rrn", "account_number", "dispatch_account", "note"];

    const changedRows = (dispatchRows || []).filter((row) => {
      const rid = String(row?._rid ?? "");
      const original = originalDispatchMap.get(rid);
      if (!original) return true;

      return editableFields.some((f) => {
        const cur = normalizeDispatchValue(f, row?.[f]);
        const org = normalizeDispatchValue(f, original?.[f]);
        return cur !== org;
      });
    });

    if (changedRows.length === 0) {
      Swal.fire({ title: "안내", text: "변경된 내용이 없습니다.", icon: "info" });
      return;
    }

    try {
      for (const r of changedRows) {
        const fd = new FormData();
        fd.append("account_id", r.account_id || selectedAccountId);
        fd.append("member_id", r.member_id || "");
        fd.append("name", r.name || "");
        fd.append("rrn", r.rrn || "");
        fd.append("phone", r.phone || "");
        fd.append("account_number", r.account_number || "");
        fd.append("dispatch_account", r.dispatch_account || "");
        fd.append("note", r.note ?? "");
        fd.append("total", r.total || "");
        fd.append("salary", r.salary ?? "");
        fd.append("del_yn", r.del_yn ?? "N");
        fd.append("record_year", String(year));
        fd.append("record_month", String(month));

        await api.post("/Account/AccountDispatchMemberSave", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const result = await Swal.fire({ title: "저장", text: "저장 완료", icon: "success" });
      if (result.isConfirmed) {
        // ✅ 파출정보 저장 확인 후 조회 버튼과 동일한 순서로 재조회
        await fetchAllData?.();
        await fetchDispatchOnly(dispatchDelFilter);
        await fetchDispatchMappingOnly();
      }
    } catch (e) {
      Swal.fire({ title: "오류", text: e.message || "저장 중 오류", icon: "error" });
    }
  }, [
    dispatchRows,
    originalDispatchMap,
    selectedAccountId,
    fetchAllData,
    fetchDispatchOnly,
    dispatchDelFilter,
    fetchDispatchMappingOnly,
    year,
    month,
  ]);

  // ✅ accountList 로딩 후 account_id 1회 적용
  useEffect(() => {
    if (!accountList || accountList.length === 0) return;

    setSelectedAccountId((prev) => {
      if (prev) return prev;

      if (account_id && accountList.some((row) => row.account_id === account_id)) {
        return account_id;
      }
      // ✅ 기본은 첫 거래처로 조회
      return accountList[0].account_id;
    });
  }, [accountList, account_id]);

  useEffect(() => {
    dispatchFormDataRef.current.account_id = selectedAccountId;
  }, [selectedAccountId]);

  // ✅ 화면도 buildAttendanceRowsFromSheet 로 통일
  useLayoutEffect(() => {
    if (!currentViewKey) {
      setAttendanceRows([]);
      setAttendanceSummaryRows([]);
      setOriginalAttendanceRows([]);
      setDefaultTimes({});
      setAttendanceLoadedViewKey("");
      return;
    }
    if (loading) return;

    if (!sheetRows || !sheetRows.length) {
      setAttendanceRows([]);
      setAttendanceSummaryRows([]);
      setOriginalAttendanceRows([]);
      setDefaultTimes({});
      setAttendanceLoadedViewKey(currentViewKey);
      return;
    }

    const { attendanceRowsBuilt, defaultTimesMap } = buildAttendanceRowsFromSheet(
      sheetRows,
      memberRows,
      timesRows,
      daysInMonth
    );

    const visibleAttendanceRows = (attendanceRowsBuilt || []).filter((row) =>
      shouldIncludeRetiredForViewMonth(row, year, month)
    );

    const snapshotRows = JSON.parse(JSON.stringify(visibleAttendanceRows));

    setAttendanceRows(visibleAttendanceRows);
    setAttendanceSummaryRows(snapshotRows);
    setOriginalAttendanceRows(JSON.parse(JSON.stringify(visibleAttendanceRows)));
    setDefaultTimes(defaultTimesMap);
    setAttendanceLoadedViewKey(currentViewKey);
  }, [sheetRows, memberRows, timesRows, daysInMonth, year, month, loading, currentViewKey]);

  const getOrgTimes = (row, defaultTimesObj) => {
    const orgStart = row.day_default?.start_time || defaultTimesObj[row.member_id]?.start || "";
    const orgEnd = row.day_default?.end_time || defaultTimesObj[row.member_id]?.end || "";
    return { org_start_time: orgStart, org_end_time: orgEnd };
  };

  // ✅ 직원파출관리(tb_account_member_dispatch_mapping) 기준으로
  // - 원소속 업장 != 파견업장: 해당 월 전체를 직원파출 전용 옵션(0/6)으로 제한
  // - 원소속 업장 == 파견업장: 등록된 일자만 직원파출 전용 옵션(0/6)으로 제한
  const employeeDispatchDayStatusMap = useMemo(() => {
    const map = new Map();
    const accId = safeTrim(selectedAccountId, "");

    (dispatchMappingRows || []).forEach((row) => {
      if (String(row?.del_yn ?? "N").toUpperCase() === "Y") return;

      const mid = safeTrim(row?.member_id ?? row?.memberId ?? "", "");
      if (!mid) return;

      const dispatchId = safeTrim(row?.dispatch_account_id ?? row?.dispatchAccountId ?? "", "");
      if (accId && dispatchId && dispatchId !== accId) return;

      let y = Number(row?.record_year ?? row?.recordYear ?? NaN);
      let m = Number(row?.record_month ?? row?.recordMonth ?? NaN);
      let d = Number(row?.record_day ?? row?.day ?? row?.date ?? NaN);

      const rawDate = safeTrim(row?.record_date ?? row?.recordDate ?? "", "");
      if (rawDate) {
        const parsed = dayjs(rawDate);
        if (parsed.isValid()) {
          y = parsed.year();
          m = parsed.month() + 1;
          d = parsed.date();
        } else if (!Number.isFinite(d)) {
          const n = Number(rawDate);
          if (Number.isFinite(n)) d = n;
        }
      }

      if (Number.isFinite(y) && Number.isFinite(m)) {
        if (y !== Number(year) || m !== Number(month)) return;
      }
      if (!Number.isFinite(d) || d < 1 || d > Number(daysInMonth)) return;

      const originAccId = safeTrim(row?.account_id ?? row?.accountId ?? "", "");
      const isSameAccount = accId && originAccId && originAccId === accId;

      if (!map.has(mid)) {
        map.set(mid, { restrictWholeMonth: false, dayMap: new Map() });
      }

      const memberStatus = map.get(mid);
      if (!memberStatus) return;

      if (isSameAccount) {
        memberStatus.dayMap.set(Number(d), true);
      } else {
        memberStatus.restrictWholeMonth = true;
      }
    });

    return map;
  }, [dispatchMappingRows, selectedAccountId, year, month, daysInMonth]);

  // ✅ tb_account_record에서 내려온 원본 type(조회 시점)을 멤버/일자 단위로 보관
  //    옵션 제한 판단은 현재 편집값이 아니라 이 원본 type 기준으로 수행
  const originalRecordTypeMap = useMemo(() => {
    const map = new Map();
    (originalAttendanceRows || []).forEach((row) => {
      const mid = safeTrim(row?.member_id ?? row?.memberId ?? "", "");
      if (!mid) return;
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = row?.[`day_${d}`];
        const t = safeTrim(cell?.type ?? "", "");
        map.set(`${mid}|${d}`, t);
      }
    });
    return map;
  }, [originalAttendanceRows, daysInMonth]);

  const dayColumns = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, i) => {
        const date = dayjs(`${year}-${month}-${i + 1}`);
        const weekday = ["일", "월", "화", "수", "목", "금", "토"][date.day()];
        const dayNum = i + 1;
        const isSat = date.day() === 6;
        const isSunOrHoliday = date.day() === 0 || holidayDays.has(dayNum);
        const headerBg = isSunOrHoliday ? "#ffe5e5" : isSat ? "#ddf0ff" : undefined;

        return {
          header: `${dayNum}일(${weekday})`,
          accessorKey: `day_${dayNum}`,
          meta: { headerBg },
          cell: (props) => {
            const memberId = safeTrim(
              props.row.original?.member_id ?? props.row.original?.memberId ?? "",
              ""
            );
            const memberStatus = memberId ? employeeDispatchDayStatusMap.get(memberId) : null;

            // ✅ 다른 업장 파출이면 해당 월 전체를 0/6으로 제한
            // ✅ 같은 업장 파출이면 등록된 일자만 0/6으로 제한
            const shouldLimitEmployeeDispatchOptions = Boolean(
              memberStatus?.restrictWholeMonth || memberStatus?.dayMap?.has(i + 1)
            );

            const rowGubun = safeTrim(props.row.original?.gubun ?? "", "").toLowerCase();

            let typeOptions = FULL_TYPE_OPTIONS;
            // ✅ 파출회원(dis)은 기존처럼 0/5 옵션 유지
            if (rowGubun === "dis") typeOptions = DISPATCH_TYPE_OPTIONS;
            // ✅ 직원파출관리 조건에 따라 월 전체 또는 등록일자만 0/6 옵션 적용
            if (shouldLimitEmployeeDispatchOptions) typeOptions = EMPLOYEE_DISPATCH_TYPE_OPTIONS;

            return <AttendanceCell {...props} typeOptions={typeOptions} />;
          },
          size: isMobile ? 52 : 80,
        };
      }),
    [
      daysInMonth,
      year,
      month,
      isMobile,
      employeeDispatchDayStatusMap,
      originalRecordTypeMap,
      holidayDays,
    ]
  );

  // 회원 퇴사 메타 맵
  const memberRetireMap = useMemo(() => {
    const map = new Map();
    (memberRows || []).forEach((m) => {
      const retireMeta = {
        del_yn: String(m?.del_yn ?? "").toUpperCase() || "N",
        del_dt: safeTrim(m?.del_dt ?? "", ""),
      };
      const mid = safeTrim(m?.member_id ?? m?.memberId ?? "", "");
      if (mid) map.set(mid, retireMeta);
      const name = safeTrim(m?.name ?? "", "");
      if (name && !map.has(name)) map.set(name, retireMeta);
    });
    return map;
  }, [memberRows]);

  // 근태 테이블 컬럼 구성
  const attendanceColumns = useMemo(
    () => [
      {
        header: "직원명",
        accessorKey: "name",
        size: "2%",
        cell: (info) => {
          return <b>{info.getValue()}</b>;
        },
      },
      ...dayColumns,
    ],
    [dayColumns, memberRetireMap]
  );

  const attendanceTable = useReactTable({
    data: attendanceRows,
    columns: attendanceColumns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: (rowIndex, columnId, newValue) => {
        setAttendanceRows((old) => {
          const targetRow = old[rowIndex];
          if (!targetRow) return old;

          const nextRows = old.slice();
          nextRows[rowIndex] = {
            ...targetRow,
            [columnId]: { ...(targetRow?.[columnId] || {}), ...newValue },
          };
          return nextRows;
        });
      },
      getOrgTimes: (row) => getOrgTimes(row, defaultTimes),
      isCellLocked: (row, columnId) => isCellLockedByActJoin(row, columnId),
    },
  });

  // 파출 지급 상태 집계 맵
  const dispatchPayStatusMap = useMemo(() => {
    const map = new Map();
    (attendanceSummaryRows || []).forEach((row) => {
      const keys = getDispatchKeys(row);
      if (keys.length === 0) return;
      let total = 0;
      let paid = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = row?.[`day_${d}`];
        if (!cell) continue;
        const t = safeTrim(cell?.type ?? "", "");
        if (!isDispatchTypeValue(t)) continue;
        total += 1;
        if (String(cell?.pay_yn ?? "").toUpperCase() === "Y") paid += 1;
      }
      if (total > 0) {
        const stat = { total, paid };
        keys.forEach((k) => {
          if (!map.has(k)) map.set(k, stat);
        });
      }
    });
    return map;
  }, [attendanceSummaryRows, daysInMonth]);

  // 파출 건수/금액 집계 맵
  const dispatchAmountMap = useMemo(() => {
    const map = new Map();
    (attendanceSummaryRows || []).forEach((row) => {
      const keys = getDispatchKeys(row);
      if (keys.length === 0) return;
      let totalCnt = 0;
      let totalPay = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = row?.[`day_${d}`];
        if (!cell) continue;
        const t = safeTrim(cell?.type ?? "", "");
        if (!isDispatchTypeValue(t)) continue;
        totalCnt += 1;
        totalPay += toNumberLike(cell?.salary);
      }
      if (totalCnt > 0 || totalPay > 0) {
        const stat = { totalCnt, totalPay };
        keys.forEach((k) => {
          if (!map.has(k)) map.set(k, stat);
        });
      }
    });
    return map;
  }, [attendanceSummaryRows, daysInMonth]);

  // 직원파출 건수/금액 집계 맵
  const employeeDispatchStatMap = useMemo(() => {
    const map = new Map();
    (attendanceSummaryRows || []).forEach((row) => {
      const mid = safeTrim(row.member_id ?? row.memberId ?? "", "");
      if (!mid) return;
      let totalCnt = 0;
      let totalPay = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = row?.[`day_${d}`];
        if (!cell) continue;
        const t = safeTrim(cell?.type ?? "", "");
        if (!isEmployeeDispatchType(t)) continue;
        totalCnt += 1;
        totalPay += toNumberLike(cell?.salary);
      }
      if (totalCnt > 0 || totalPay > 0) {
        map.set(String(mid), { totalCnt, totalPay });
      }
    });
    return map;
  }, [attendanceSummaryRows, daysInMonth]);

  // ✅ 직원파출관리 모달에 등록된 건수도 같이 집계
  //    아직 출근부를 따로 저장하지 않았더라도 같은 업장 등록 횟수가 바로 보이게 함
  const employeeDispatchRegisteredCountMap = useMemo(() => {
    const map = new Map();
    const accId = String(selectedAccountId ?? "");
    if (!accId) return map;

    const isSameMonthRecord = (row) => {
      const raw = safeTrim(row?.record_date ?? row?.recordDate ?? "", "");
      if (!raw) return true;
      const d = dayjs(raw);
      if (!d.isValid()) return true;
      return d.year() === Number(year) && d.month() + 1 === Number(month);
    };

    (dispatchMappingRows || []).forEach((row) => {
      const mid = safeTrim(row?.member_id ?? row?.memberId ?? "", "");
      const dispatchId = safeTrim(row?.dispatch_account_id ?? row?.dispatchAccountId ?? "", "");
      if (!mid || !dispatchId || dispatchId !== accId) return;
      if (String(row?.del_yn ?? "N").toUpperCase() === "Y") return;
      if (!isSameMonthRecord(row)) return;

      map.set(mid, (map.get(mid) ?? 0) + 1);
    });

    return map;
  }, [dispatchMappingRows, selectedAccountId, year, month]);

  // 급여범위 선택 요약
  const payRangeSummary = useMemo(() => {
    const totalSum = (payRangeRows || []).reduce(
      (acc, r) => acc + (Number(r.total_pay || 0) || 0),
      0
    );
    const selectedRows = (payRangeRows || []).filter((r) => payRangeSelected?.[r.member_id]);
    const selectedSum = selectedRows.reduce((acc, r) => acc + (Number(r.total_pay || 0) || 0), 0);
    return {
      totalCount: payRangeRows?.length || 0,
      selectedCount: selectedRows.length,
      totalSum,
      selectedSum,
    };
  }, [payRangeRows, payRangeSelected]);

  const payRangeAllSelected =
    (payRangeRows || []).length > 0 &&
    (payRangeRows || []).every((r) => payRangeSelected?.[r.member_id]);
  const payRangeSomeSelected =
    (payRangeRows || []).some((r) => payRangeSelected?.[r.member_id]) && !payRangeAllSelected;

  const excelModalBtn = {
    outline: {
      color: "#111",
      borderColor: "#111",
      "&:hover": { bgcolor: "rgba(0,0,0,0.06)", borderColor: "#000", color: "#000" },
    },
    solid: {
      bgcolor: "#111",
      color: "#fff",
      "&:hover": { bgcolor: "#000", color: "#fff" },
    },
  };

  const payModalBtn = {
    outline: {
      color: "#e91e63",
      borderColor: "#e91e63",
      "&:hover": {
        bgcolor: "#e91e63",
        borderColor: "#e91e63",
        color: "#fff",
      },
    },
    solid: {
      bgcolor: "#e91e63",
      color: "#fff",
      "&:hover": { bgcolor: "#d81b60", color: "#fff" },
      "&.Mui-disabled": { bgcolor: "#f8bbd0", color: "#fff" },
    },
  };

  const payCheckboxSx = (theme) => ({
    color: theme.palette.primary.main,
    "&.Mui-checked": { color: theme.palette.primary.main },
    "&.MuiCheckbox-indeterminate": { color: theme.palette.primary.main },
  });

  const handlePayRangeToggleAll = useCallback(
    (checked) => {
      setPayRangeSelected(
        (payRangeRows || []).reduce((acc, r) => {
          acc[r.member_id] = checked;
          return acc;
        }, {})
      );
    },
    [payRangeRows]
  );

  const handlePayRangeToggleOne = useCallback((memberId, checked) => {
    setPayRangeSelected((prev) => ({ ...(prev || {}), [memberId]: checked }));
  }, []);

  const handleToggleDispatchPay = useCallback(
    (memberId, nextChecked) => {
      if (!memberId) return;
      const payVal = nextChecked ? "Y" : "N";
      setAttendanceRows((prev) =>
        (prev || []).map((row) => {
          if (String(row.member_id) !== String(memberId)) return row;
          const updated = { ...row };
          for (let d = 1; d <= daysInMonth; d++) {
            const key = `day_${d}`;
            const cell = updated[key];
            if (!cell) continue;
            const t = safeTrim(cell?.type ?? "", "");
            if (!(t === "5" || t === "6")) continue;
            updated[key] = { ...cell, pay_yn: payVal };
          }
          return updated;
        })
      );
    },
    [daysInMonth]
  );

  const employeeTable = useReactTable({
    data: employeeRowsView,
    columns: [
      { header: "직원명", accessorKey: "name", size: "3%", cell: ReadonlyCell },
      { header: "직책", accessorKey: "position", size: "3%", cell: ReadonlyCell },
      { header: "근로일수", accessorKey: "working_day", size: "3%", cell: ReadonlyCell },
      { header: "초과", accessorKey: "over_work", size: "3%", cell: ReadonlyCell },
      { header: "결근", accessorKey: "non_work", size: "3%", cell: ReadonlyCell },
      { header: "비고", accessorKey: "note", size: "20%", cell: ReadonlyCell },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  const employeeDispatchRows = useMemo(() => {
    const accId = String(selectedAccountId ?? "");
    if (!accId) return [];

    const accNameMap = new Map(
      (accountList || []).map((a) => [String(a.account_id), safeTrim(a.account_name ?? "", "")])
    );

    const rows = [];
    const seen = new Set();

    const isSameMonthRecord = (row) => {
      const raw = safeTrim(row?.record_date ?? row?.recordDate ?? "", "");
      if (!raw) return true;
      const d = dayjs(raw);
      if (!d.isValid()) return true;
      return d.year() === Number(year) && d.month() + 1 === Number(month);
    };

    (dispatchMappingRows || []).forEach((row) => {
      const mid = safeTrim(row?.member_id ?? row?.memberId ?? "", "");
      if (!mid || seen.has(mid)) return;
      if (!isSameMonthRecord(row)) return;

      const originId = safeTrim(row?.account_id ?? row?.origin_account_id ?? "", "");
      const dispatchId = safeTrim(row?.dispatch_account_id ?? row?.dispatchAccountId ?? "", "");

      // ✅ 현재 업장으로 파견된 건은 모두 표시(원소속이 자기 업장인 경우도 포함)
      if (dispatchId && dispatchId !== accId) return;

      seen.add(mid);

      const info = parseEmployeeDispatchInfo(row?.employ_dispatch);
      const stat = employeeDispatchStatMap.get(mid);

      const origin = safeTrim(
        row?.origin_account_name ??
        row?.origin_account ??
        accNameMap.get(originId) ??
        info.origin ??
        "",
        ""
      );

      const dispatch = safeTrim(
        row?.dispatch_account_name ??
        row?.dispatch_account ??
        row?.dispatch_account_nm ??
        row?.dispatch_accountName ??
        accNameMap.get(dispatchId) ??
        info.dispatch ??
        "",
        ""
      );

      const registeredCount = employeeDispatchRegisteredCountMap.get(mid) ?? 0;
      const count = Math.max(Number(stat?.totalCnt ?? 0), Number(info.count ?? 0), registeredCount);
      const amount = stat?.totalPay ?? info.amount ?? "";
      const name = safeTrim(row?.name ?? row?.member_name ?? "", "");

      if (!name && !origin && !dispatch) return;

      rows.push({ name, origin, dispatch, count, amount });
    });

    return rows;
  }, [
    dispatchMappingRows,
    selectedAccountId,
    employeeDispatchStatMap,
    employeeDispatchRegisteredCountMap,
    accountList,
    year,
    month,
  ]);

  // ✅ 파출 컬럼: 편집/변경감지/삭제복원 유지
  const dispatchColumns = useMemo(
    () => [
      {
        header: "이름",
        accessorKey: "name",
        size: "3%",
        cell: ({ row, getValue }) => {
          const name = getValue() || "";
          const key = getDispatchRowKey(row.original);
          const isOpen = !!dispatchMemoOpen?.[key];
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={() => toggleDispatchMemo(row.original)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") toggleDispatchMemo(row.original);
              }}
              style={{
                cursor: "pointer",
                textDecoration: isOpen ? "underline" : "none",
                fontSize: "0.75rem",
              }}
              title="메모 보기"
            >
              {isOpen ? "▾ " : "▸ "}
              {name}
            </span>
          );
        },
      },
      {
        header: "연락처",
        accessorKey: "phone",
        size: "3%",
        cell: (props) => (
          <DispatchEditableCell {...props} field="phone" maskingEnabled={dispatchMaskingEnabled} />
        ),
      },
      {
        header: "주민등록번호",
        accessorKey: "rrn",
        size: "3%",
        cell: (props) => (
          <DispatchEditableCell {...props} field="rrn" maskingEnabled={dispatchMaskingEnabled} />
        ),
      },
      {
        header: "계좌정보",
        accessorKey: "account_number",
        size: "3%",
        cell: (props) => (
          <DispatchEditableCell
            {...props}
            field="account_number"
            maskingEnabled={dispatchMaskingEnabled}
          />
        ),
      },
      {
        header: "금액",
        accessorKey: "total",
        size: "15%",
        cell: ({ row, getValue }) => {
          const stat = getDispatchStatFromMap(dispatchAmountMap, row.original);
          if (!stat) {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.1,
                  fontSize: "0.72rem",
                  width: "100%",
                }}
              >
                0회
              </div>
            );
          }
          const cnt = Number(stat.totalCnt || 0);
          const pay = Number(stat.totalPay || 0);
          if (cnt <= 0) {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.1,
                  fontSize: "0.72rem",
                  width: "100%",
                }}
              >
                0회
              </div>
            );
          }
          const payText = pay > 0 ? `${formatMoneyLike(pay)}원` : "-";
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: 1.1,
                fontSize: "0.72rem",
                width: "100%",
              }}
            >
              <span>{cnt}회</span>
              <span>{payText}</span>
            </div>
          );
        },
      },
      {
        header: "지급",
        id: "pay_yn",
        size: "3%",
        cell: ({ row }) => (
          <DispatchPayCell
            row={row}
            status={getDispatchStatFromMap(dispatchPayStatusMap, row.original)}
            onToggle={handleToggleDispatchPay}
          />
        ),
      },
      {
        header: "파출업체",
        accessorKey: "dispatch_account",
        size: "3%",
        cell: (props) => <DispatchEditableCell {...props} field="dispatch_account" />,
      },
      {
        header: "관리",
        id: "actions",
        size: "1%",
        cell: ({ row }) => <DispatchActionCell row={row} onToggle={handleToggleDispatch} />,
      },
    ],
    [
      dispatchPayStatusMap,
      dispatchAmountMap,
      handleToggleDispatchPay,
      handleToggleDispatch,
      dispatchMemoOpen,
      toggleDispatchMemo,
      getDispatchRowKey,
      dispatchMaskingEnabled,
    ]
  );

  const dispatchTable = useReactTable({
    data: dispatchRows,
    columns: dispatchColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row?._rid ?? row?.dispatch_id ?? row?.member_id ?? row?.id ?? ""),
    meta: {
      updateDispatchByRid: (rid, patch) => updateDispatchByRid(rid, patch),
      getOriginalDispatchValueByRid: (rid, field) => {
        const org = originalDispatchMap.get(String(rid));
        return org ? org[field] ?? "" : "";
      },
    },
  });

  const dispatchColStyle = (columnId) => {
    if (columnId === "phone") {
      return { width: 110, minWidth: 110, maxWidth: 110 };
    }
    if (columnId === "rrn") {
      return { width: 115, minWidth: 115, maxWidth: 115 };
    }
    if (columnId === "account_number") {
      return { width: 180, minWidth: 180, maxWidth: 180 };
    }
    if (columnId === "total") {
      return { width: 80, minWidth: 80, maxWidth: 80 };
    }
    return undefined;
  };

  const tableSx = {
    maxHeight: "430px",
    overflow: "auto",
    "& table": {
      width: "max-content",
      minWidth: "100%",
      borderSpacing: 0,
      borderCollapse: "separate",
    },
    "& th, & td": {
      border: "1px solid #686D76",
      textAlign: "center",
      padding: "4px",
      whiteSpace: "nowrap",
      fontSize: "12px",
    },
    "& .dispatch-centered-input": {
      textAlign: "center !important",
      textAlignLast: "center !important",
      direction: "ltr",
    },
    "& th": {
      backgroundColor: "#f0f0f0",
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
    "& td:first-of-type, & th:first-of-type": {
      position: "sticky",
      left: 0,
      background: "#f0f0f0",
      zIndex: 3,
      border: "1px solid #686D76",
    },
    "thead th:first-of-type": { zIndex: 5 },
    "& button": { height: "20px !important", padding: "2px" },
  };

  const handleApplyDefaultTime = () => {
    setAttendanceRows((prevRows) =>
      prevRows.map((row) => {
        const updated = { ...row };
        const { org_start_time, org_end_time } = getOrgTimes(row, defaultTimes);

        Object.keys(updated)
          .filter((k) => k.startsWith("day_"))
          .forEach((dayKey) => {
            const cell = updated[dayKey];
            if (!cell) return;

            const typeNum = Number(cell.type);
            if (typeNum === 1 || typeNum === 2) {
              updated[dayKey] = {
                ...cell,
                start: org_start_time,
                end: org_end_time,
                start_time: org_start_time,
                end_time: org_end_time,
              };
            }
          });

        return updated;
      })
    );
  };

  // ✅ 저장(출근현황)
  // 저장 성공 시 직원정보/파출정보를 로딩 없이 "쓱" 갱신
  const handleSave = async () => {
    if (!attendanceRows || !attendanceRows.length) return;

    const loadingStartedAt = Date.now();
    showLoadingModal("저장 중...");
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const closeLoadingModal = async () => {
      const minVisibleMs = 350;
      const elapsed = Date.now() - loadingStartedAt;
      if (elapsed < minVisibleMs) {
        await new Promise((resolve) => setTimeout(resolve, minVisibleMs - elapsed));
      }
      if (Swal.isVisible()) Swal.close();
    };

    const normalRecords = [];
    const disRecords = [];
    const recRecords = [];
    const employeeDispatchMappingCandidates = [];
    // ✅ 저장 이력에 남길 작성자
    const loginUserId = safeTrim(localStorage.getItem("user_id"), "");

    const useDiffMode =
      originalAttendanceRows && originalAttendanceRows.length === attendanceRows.length;

    attendanceRows.forEach((row, rowIndex) => {
      const originalRow = useDiffMode ? originalAttendanceRows[rowIndex] : null;
      const { org_start_time, org_end_time } = getOrgTimes(row, defaultTimes);

      const rowGubun = safeTrim(row.gubun, "nor");
      const rowPt = safeTrim(row.position_type, "");

      Object.entries(row)
        .filter(([key]) => key.startsWith("day_"))
        .forEach(([key, val]) => {
          const dayNum = parseInt(key.replace("day_", ""), 10);
          if (Number.isNaN(dayNum) || dayNum === 0) return;

          const originalVal = useDiffMode && originalRow ? originalRow[key] : null;

          if (useDiffMode) {
            if (isCellEqual(val, originalVal)) return;
          }

          const curType = safeTrim(val?.type, "");
          const orgType = safeTrim(originalVal?.type, "");

          const cleared =
            (curType === "0" || curType === "") && !(orgType === "" || orgType === "0");

          const gubun = safeTrim(val?.gubun, rowGubun);
          const pt = safeTrim(val?.position_type, rowPt);

          // ✅ 저장 키 안정화: account_id를 현재 선택값으로 강제하지 않고
          //    기존(row/original) account_id를 우선 사용해 기존 row 업데이트를 유도
          const resolvedAccountId =
            originalVal?.account_id ?? val?.account_id ?? row.account_id ?? selectedAccountId ?? "";
          const oldTypeNum = orgType === "" ? null : Number(orgType);
          const isDispatchType = isDispatchTypeValue(curType);
          const isNoteType = curType === "3" || curType === "11" || curType === "17";
          const isAccountHolidayType = curType === "16";
          const recordStartTime = isAccountHolidayType ? org_start_time : val.start || "";
          const recordEndTime = isAccountHolidayType ? org_end_time : val.end || "";
          const normalizedSalary = isDispatchType
            ? val?.salary
              ? Number(String(val.salary).replace(/,/g, ""))
              : 0
            : 0;
          const normalizedNote = shouldSendEmptyNoteForType(curType)
            ? ""
            : isNoteType
              ? safeTrim(val?.note ?? "", "") || null
              : null;

          if (cleared) {
            const recordObj = {
              gubun,
              account_id: resolvedAccountId,
              member_id: val?.member_id || row.member_id || "",
              position_type: pt,
              positionType: pt,
              record_date: dayNum,
              record_year: year,
              record_month: month,
              type: 0,
              // ✅ 추가: type 0(삭제/비출근 처리)면 출근여부 N
              is_present: "N",
              start_time: "",
              end_time: "",
              salary: 0,
              note: "",
              position: row.position || "",
              org_start_time,
              org_end_time,
              user_id: loginUserId,
              // ✅ 기존 row 기준 update용 키(백엔드 보강 로직에서 사용)
              old_account_id: originalVal?.account_id ?? resolvedAccountId,
              old_member_id: originalVal?.member_id ?? (val?.member_id || row.member_id || ""),
              old_record_year: year,
              old_record_month: month,
              old_record_date: dayNum,
              old_type: oldTypeNum,
            };

            const gg = safeTrim(recordObj.gubun, "nor").toLowerCase();
            if (gg === "dis") {
              recordObj.pay_yn = "N";
              disRecords.push(recordObj);
            } else if (gg === "rec") {
              recRecords.push(recordObj);
            } else {
              normalRecords.push(recordObj);
            }
            return;
          }

          if (!val || !curType || curType === "0") return;

          const recordObj = {
            gubun,
            account_id: resolvedAccountId,
            member_id: val.member_id || row.member_id || "",
            position_type: pt,
            positionType: pt,
            record_date: dayNum,
            record_year: year,
            record_month: month,
            type: Number(curType),
            start_time: recordStartTime,
            end_time: recordEndTime,
            salary: normalizedSalary,
            note: normalizedNote,
            position: row.position || "",
            org_start_time,
            org_end_time,
            user_id: loginUserId,
            // ✅ 기존 row 기준 update용 키(백엔드 보강 로직에서 사용)
            old_account_id: originalVal?.account_id ?? resolvedAccountId,
            old_member_id: originalVal?.member_id ?? (val.member_id || row.member_id || ""),
            old_record_year: year,
            old_record_month: month,
            old_record_date: dayNum,
            old_type: oldTypeNum,
          };

          if (isEmployeeDispatchType(curType)) {
            const originAccountId = resolveOriginAccountIdForMember(
              recordObj.member_id,
              dayNum,
              row.account_id ?? val?.account_id ?? ""
            );
            employeeDispatchMappingCandidates.push({
              type: 6,
              member_id: recordObj.member_id,
              // ✅ 매핑용 account_id는 원소속 업장
              account_id: originAccountId,
              name: row.name ?? "",
              position_type: pt,
              start_time: recordObj.start_time,
              end_time: recordObj.end_time,
              salary: Number.isFinite(Number(recordObj.salary)) ? Number(recordObj.salary) : 0,
              record_year: year,
              record_month: month,
              record_date: dayNum,
            });
          }

          const gg = safeTrim(recordObj.gubun, "nor").toLowerCase();
          if (gg === "dis") {
            recordObj.pay_yn = String(val?.pay_yn ?? "N").toUpperCase() === "Y" ? "Y" : "N";
            disRecords.push(recordObj);
          } else if (gg === "rec") {
            recRecords.push(recordObj);
          } else {
            normalRecords.push(recordObj);
          }
        });
    });

    if (!normalRecords.length && !disRecords.length && !recRecords.length) {
      await closeLoadingModal();
      Swal.fire({ title: "안내", text: "변경된 내용이 없습니다.", icon: "info" });
      return;
    }

    try {
      const res = await api.post("/Account/AccountRecordSave", {
        normalRecords,
        disRecords,
        recRecords,
      });

      if (res.data?.code === 200) {
        if (employeeDispatchMappingCandidates.length > 0) {
          await saveEmployeeDispatchMappings(employeeDispatchMappingCandidates);
        }
        await closeLoadingModal();
        const result = await Swal.fire({ title: "저장", text: "저장 완료", icon: "success" });
        if (result.isConfirmed) {
          // ✅ 출근현황 저장 확인 후 조회 버튼과 동일한 순서로 재조회
          await fetchAllData?.();
          await fetchDispatchOnly(dispatchDelFilter);
          await fetchDispatchMappingOnly();
        }
      } else {
        await closeLoadingModal();
        Swal.fire({ title: "실패", text: "저장 실패", icon: "error" });
      }
    } catch (err) {
      await closeLoadingModal();
      console.error("저장 실패:", err);
      Swal.fire({
        title: "실패",
        text: err?.message ? `저장 실패 (${err.message})` : "저장 실패",
        icon: "error",
      });
    }
  };

  const isPageLoading =
    !currentViewKey ||
    loading ||
    employeeLoadedViewKey !== currentViewKey ||
    attendanceLoadedViewKey !== currentViewKey ||
    dispatchLoadedViewKey !== currentViewKey ||
    mappingLoadedViewKey !== currentViewKey;

  if (isPageLoading) return <LoadingScreen />;

  const filterAndActionArea = (
    <MDBox
      pt={1.5}
      pb={1.5}
      sx={{
        display: "flex",
        flexWrap: isTopToolbarWrap ? "wrap" : "nowrap",
        justifyContent: isTopToolbarWrap ? "flex-start" : "flex-end",
        alignItems: "center",
        gap: isTopToolbarWrap ? 1 : 2,
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #eee",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: isTopToolbarWrap ? "wrap" : "nowrap",
          justifyContent: isTopToolbarWrap ? "flex-start" : "space-between",
          alignItems: "right",
          gap: 1,
          width: isTopToolbarWrap ? "100%" : "auto",
        }}
      >
        <Autocomplete
          size="small"
          options={accountList || []}
          value={(accountList || []).find((a) => a.account_id === selectedAccountId) || null}
          onChange={(_, newVal) => {
            if (!newVal) return;
            setSelectedAccountId(newVal?.account_id || "");
            accountInputRef.current = newVal?.account_name || "";
          }}
          onInputChange={(_, newValue) => {
            accountInputRef.current = String(newValue ?? "");
          }}
          getOptionLabel={(opt) => opt?.account_name || ""}
          isOptionEqualToValue={(opt, val) => opt?.account_id === val?.account_id}
          sx={{
            minWidth: isTopToolbarWrap ? 220 : 200,
            flex: isTopToolbarWrap ? "1 1 220px" : "0 0 auto",
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="거래처 검색"
              placeholder="거래처명을 입력"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (e.nativeEvent?.isComposing) return;
                  e.preventDefault();
                  selectAccountByInput(e.currentTarget.value);
                }
              }}
              sx={{
                "& .MuiInputBase-root": { height: 40, fontSize: 12 },
                "& .MuiInputLabel-root": { fontSize: 12 },
                "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
              }}
            />
          )}
        />

        <Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          size="small"
          sx={{
            minWidth: isMobile ? 70 : 85,
            "& .MuiSelect-select": { fontSize: isMobile ? "0.75rem" : "0.875rem" },
          }}
        >
          {Array.from({ length: 10 }, (_, i) => today.year() - 5 + i).map((y) => (
            <MenuItem key={y} value={y}>
              {y}년
            </MenuItem>
          ))}
        </Select>

        <Select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          size="small"
          sx={{
            minWidth: isMobile ? 60 : 75,
            "& .MuiSelect-select": { fontSize: isMobile ? "0.75rem" : "0.875rem" },
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <MenuItem key={m} value={m}>
              {m}월
            </MenuItem>
          ))}
        </Select>

        <MDButton
          variant="gradient"
          color="success"
          onClick={handleApplyDefaultTime}
          sx={{
            fontSize: isMobile ? "0.7rem" : "0.8rem",
            minWidth: isMobile ? 95 : 115,
            px: isMobile ? 1 : 1.5,
          }}
        >
          출퇴근 일괄 적용
        </MDButton>

        {canUseBulkExcelAndPay && (
          <MDButton
            variant="gradient"
            color="dark"
            onClick={openExcelRangeModal}
            disabled={excelDownloading}
            sx={{
              fontSize: isMobile ? "0.7rem" : "0.8rem",
              minWidth: isMobile ? 78 : 122,
              px: isMobile ? 1 : 1.5,
              opacity: excelDownloading ? 0.6 : 1,
            }}
          >
            {excelDownloading ? "엑셀 생성 중..." : "전체 거래처 엑셀"}
          </MDButton>
        )}

        {canUseBulkExcelAndPay && (
          <MDButton
            variant="gradient"
            color="primary"
            onClick={openPayRangeModal}
            sx={{
              fontSize: isMobile ? "0.7rem" : "0.8rem",
              minWidth: "unset !important",
              padding: isMobile ? "6px 10px !important" : "6px 14px !important",
              whiteSpace: "nowrap",
            }}
          >
            지급 일괄
          </MDButton>
        )}

        {canUseUtilRecord && (
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleUtilRecordModalOpen}
            disabled={utilRecordUploading}
            sx={{
              fontSize: isMobile ? "0.7rem" : "0.8rem",
              minWidth: "unset !important",
              padding: isMobile ? "6px 10px !important" : "6px 14px !important",
              whiteSpace: "nowrap",
              opacity: utilRecordUploading ? 0.6 : 1,
            }}
          >
            {utilRecordUploading ? "업로드 중..." : "유틸 출근부"}
          </MDButton>
        )}
        {/* ✅ 유틸 출근부 엑셀 업로드용 숨김 input */}
        <input
          ref={utilRecordFileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleUtilRecordUpload}
        />

        {canUseIntegrationRecord && (
          <MDButton
            variant="gradient"
            color="success"
            onClick={openIntegrationRecordModal}
            sx={{
              fontSize: isMobile ? "0.7rem" : "0.8rem",
              minWidth: "unset !important",
              padding: isMobile ? "6px 10px !important" : "6px 14px !important",
              whiteSpace: "nowrap",
            }}
          >
            통합 출근부
          </MDButton>
        )}

        <MDButton
          variant="outlined"
          color={dispatchMaskingEnabled ? "dark" : "secondary"}
          onClick={() => setDispatchMaskingEnabled((prev) => !prev)}
          sx={{
            fontSize: isMobile ? "0.7rem" : "0.8rem",
            minWidth: "unset !important",
            padding: isMobile ? "6px 14px !important" : "6px 20px !important",
            whiteSpace: "nowrap",
          }}
        >
          {dispatchMaskingEnabled ? "* 해제" : "* 적용"}
        </MDButton>

        <MDButton
          variant="gradient"
          color="warning"
          onClick={async () => {
            // ✅ 전체 조회를 병렬로 묶어 완료 시점까지 로딩 유지
            await refreshRecordSheetViews("all");
          }}
          sx={{
            fontSize: isMobile ? "0.7rem" : "0.8rem",
            minWidth: "unset !important",
            padding: isMobile ? "6px 14px !important" : "6px 20px !important",
            whiteSpace: "nowrap",
          }}
        >
          조회
        </MDButton>

        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          sx={{
            fontSize: isMobile ? "0.7rem" : "0.8rem",
            minWidth: isMobile ? 70 : 90,
            px: isMobile ? 1 : 2,
          }}
        >
          저장
        </MDButton>
      </Box>
    </MDBox>
  );

  return (
    <DashboardLayout>
      {isMobile ? (
        <>
          <MDBox
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#ffffff",
            }}
          >
            <DashboardNavbar title="🚌 출근부" />
          </MDBox>
          {filterAndActionArea}
        </>
      ) : (
        <MDBox
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#ffffff",
          }}
        >
          <DashboardNavbar title="🚌 출근부" />
          {filterAndActionArea}
        </MDBox>
      )}

      <Grid container spacing={5}>
        {/* 출근 현황 */}
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={0}
              mt={1}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                rowGap: 0.5,
              }}
            >
              <MDTypography variant="h6" color="white">
                출근 현황
              </MDTypography>
              {!!selectedAccountId && (
                <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <MDTypography variant="button" color="white" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                    평균식수
                  </MDTypography>
                  {mealColumnAverages.length > 0 && (
                    <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
                      <thead>
                        <tr>
                          {mealColumnAverages.map((item) => (
                            <th
                              key={item.key}
                              style={{
                                border: "1px solid #ddd",
                                padding: "2px 8px",
                                background: "#288ebe",
                                textAlign: "center",
                                color: "#fff",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {mealColumnAverages.map((item) => (
                            <td
                              key={item.key}
                              style={{
                                border: "1px solid #ddd",
                                padding: "2px 8px",
                                background: "#fff",
                                textAlign: "center",
                                color: "#111",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.average.toLocaleString()}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  )}
                </MDBox>
              )}
            </MDBox>

            <MDBox pt={0} sx={tableSx}>
              <table className="recordsheet-table" style={{ tableLayout: "fixed" }}>
                <thead>
                  {attendanceTable.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => {
                        const hBg = header.column.columnDef.meta?.headerBg;
                        return (
                          <th key={header.id} style={hBg ? { backgroundColor: hBg } : undefined}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {attendanceTable.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        const rowRetired = isRetiredByDelInfo(row.original);
                        const memberId = safeTrim(
                          row.original?.member_id ?? row.original?.memberId ?? "",
                          ""
                        );
                        // ⚠ 예전엔 member_id 불일치 시 이름으로도 찾았으나, 동명이인/재입사(새 member_id)
                        //   케이스에서 남의 퇴사기록을 잘못 표시하는 오탐이 있어 이름 폴백 제거함(2026-09-15).
                        const fallbackRetire = (memberId && memberRetireMap.get(memberId)) || null;
                        const isRetired = rowRetired || isRetiredByDelInfo(fallbackRetire);
                        const isJoinLocked =
                          cell.column.id.startsWith("day_") &&
                          isCellLockedByActJoin(row.original, cell.column.id);
                        let bg = "";
                        if (isRetired && cell.column.id === "name") {
                          bg = "#ffe5e5";
                        } else if (isJoinLocked) {
                          bg = "#e0e0e0";
                        } else if (cell.column.id.startsWith("day_")) {
                          const v = cell.getValue();
                          bg = typeColors[v?.type || ""] || "";
                        }
                        return (
                          <td
                            key={cell.id}
                            style={{
                              width: cell.column.columnDef.size,
                              backgroundColor: bg,
                              pointerEvents: isJoinLocked ? "none" : "auto",
                              userSelect: isJoinLocked ? "none" : "auto",
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* ✅ 일자별 출근자 수 요약 행 */}
                  <tr>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        bottom: 0,
                        background: "#f0f0f0",
                        zIndex: 6,
                        fontWeight: "bold",
                      }}
                    >
                      출근자 수
                    </td>

                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const key = `day_${i + 1}`;
                      const cnt = dayWorkCounts[key] || 0;
                      return (
                        <td
                          key={key}
                          style={{
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: "#fafafa",
                            fontWeight: "bold",
                            textAlign: "center",
                            zIndex: 5,
                          }}
                        >
                          {cnt}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </MDBox>
          </Card>
        </Grid>

        {/* 직원 정보 */}
        <Grid item xs={12} md={6}>
          <Card>
            <MDBox
              mx={0}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                직원 정보
              </MDTypography>
            </MDBox>
            <MDBox pt={0} sx={tableSx}>
              <table className="recordsheet-table">
                <thead>
                  {employeeTable.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th key={header.id}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {employeeTable.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </MDBox>
          </Card>
          {employeeDispatchRows.length > 0 && (
            <Card sx={{ mt: 6 }}>
              <MDBox
                mx={0}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  직원파출 정보
                </MDTypography>
              </MDBox>
              <MDBox pt={0} sx={tableSx}>
                <table className="recordsheet-table">
                  <thead>
                    <tr>
                      <th>직원명</th>
                      <th>원소속</th>
                      <th>파견업장</th>
                      <th>횟수</th>
                      <th>금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeDispatchRows.map((r, idx) => (
                      <tr key={`${r.name}_${idx}`}>
                        <td>{r.name || "-"}</td>
                        <td>{r.origin || "-"}</td>
                        <td>{r.dispatch || "-"}</td>
                        <td>{r.count > 0 ? `${r.count}회` : "-"}</td>
                        <td>{r.amount ? `${formatMoneyLike(r.amount)}원` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MDBox>
            </Card>
          )}
        </Grid>

        {/* 파출 정보 */}
        <Grid item xs={12} md={6}>
          <Card>
            <MDBox
              mx={0}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              sx={{
                display: "flex",
                alignItems: isTabletOrSmallDesktop ? "flex-start" : "center",
                justifyContent: "space-between",
                flexWrap: isTabletOrSmallDesktop ? "wrap" : "nowrap",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <MDTypography variant="h6" color="white">
                파출 정보 {dispatchLoading ? "(조회중...)" : ""}
              </MDTypography>

              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: isTabletOrSmallDesktop ? "wrap" : "nowrap",
                  justifyContent: isTabletOrSmallDesktop ? "flex-start" : "flex-end",
                  width: isTabletOrSmallDesktop ? "100%" : "auto",
                }}
              >
                <Select
                  value={dispatchDelFilter}
                  onChange={(e) => {
                    // ✅ 필터만 바꾸면 useEffect가 자동 재조회 + snapshot 갱신
                    setDispatchDelFilter(e.target.value);
                  }}
                  size="small"
                  sx={{
                    minWidth: isMobile ? 110 : 140,
                    background: "white",
                    borderRadius: 1,
                    "& .MuiSelect-select": {
                      fontSize: isMobile ? "0.75rem" : "0.8rem",
                      py: 0.5,
                    },
                  }}
                >
                  <MenuItem value="N">유지</MenuItem>
                  <MenuItem value="Y">삭제</MenuItem>
                </Select>

                {canViewDispatchHistory && (
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    onClick={openDispatchImportModal}
                    sx={{ minWidth: 120, fontSize: isMobile ? "0.75rem" : "0.8rem", py: 0.5 }}
                  >
                    파출이력 조회
                  </MDButton>
                )}

                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  onClick={handleModalOpen}
                  sx={{ minWidth: 90, fontSize: isMobile ? "0.75rem" : "0.8rem", py: 0.5 }}
                >
                  파출등록
                </MDButton>

                <MDButton
                  variant="gradient"
                  color="warning"
                  size="small"
                  onClick={handleDispatchSave}
                  sx={{ minWidth: 70, fontSize: isMobile ? "0.75rem" : "0.8rem", py: 0.5 }}
                >
                  저장
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox pt={0} sx={tableSx}>
              <table className="recordsheet-table">
                <thead>
                  {dispatchTable.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th key={header.id} style={dispatchColStyle(header.column.id)}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {dispatchTable.getRowModel().rows.map((row) => {
                    const key = getDispatchRowKey(row.original);
                    const isOpen = !!dispatchMemoOpen?.[key];
                    const rid = String(row.original?._rid ?? "");
                    const note = row.original?.note ?? "";
                    const originalNote = originalDispatchMap.get(rid)?.note ?? "";
                    const isNoteChanged =
                      normalizeDispatchValue("note", note) !==
                      normalizeDispatchValue("note", originalNote);
                    return (
                      <React.Fragment key={row.id}>
                        <tr>
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              style={{
                                ...(dispatchColStyle(cell.column.id) || {}),
                                ...(["phone", "rrn", "account_number", "dispatch_account"].includes(
                                  cell.column.id
                                )
                                  ? { textAlign: "center" }
                                  : {}),
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                        {isOpen && (
                          <tr>
                            <td
                              colSpan={row.getVisibleCells().length}
                              style={{
                                textAlign: "left",
                                background: "#fafafa",
                                fontSize: "0.75rem",
                                padding: "6px 8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: 4,
                                }}
                              >
                                <b>메모</b>
                                {!safeTrim(note, "") && (
                                  <span style={{ color: "#9e9e9e", fontSize: "0.72rem" }}>
                                    메모 없음
                                  </span>
                                )}
                              </div>
                              <input
                                className="dispatch-centered-input"
                                value={note}
                                onChange={(e) => {
                                  updateDispatchByRid(rid, { note: e.target.value });
                                }}
                                placeholder="메모를 입력하세요"
                                style={{
                                  width: "100%",
                                  height: 20,
                                  boxSizing: "border-box",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.1,
                                  textAlign: "center",
                                  textAlignLast: "center",
                                  direction: "ltr",
                                  border: "1px solid #ccc",
                                  borderRadius: 3,
                                  padding: "0 2px",
                                  color: isNoteChanged ? "red" : "black",
                                  fontWeight: isNoteChanged ? 700 : 400,
                                  background: "#fff",
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* 등록 모달 */}
      <Modal open={open} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 5,
          }}
        >
          <MDTypography variant="h6" gutterBottom>
            파출직원 등록
          </MDTypography>

          <TextField
            fullWidth
            margin="normal"
            label="이름"
            name="name"
            defaultValue={dispatchFormDataRef.current.name}
            InputLabelProps={{ style: { fontSize: "0.7rem" } }}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="연락처"
            name="phone"
            defaultValue={dispatchFormDataRef.current.phone}
            InputLabelProps={{ style: { fontSize: "0.7rem" } }}
            inputProps={{ style: { textAlign: "center" } }}
            sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="주민번호"
            name="rrn"
            defaultValue={dispatchFormDataRef.current.rrn}
            InputLabelProps={{ style: { fontSize: "0.7rem" } }}
            inputProps={{ style: { textAlign: "center" } }}
            sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="계좌정보"
            name="account_number"
            defaultValue={dispatchFormDataRef.current.account_number}
            InputLabelProps={{ style: { fontSize: "0.7rem" } }}
            inputProps={{ style: { textAlign: "center" } }}
            sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
            onChange={handleChange}
          />
          <Select
            fullWidth
            size="small"
            name="position_type" // ✅ 중요
            defaultValue={dispatchFormDataRef.current.position_type || "5"}
            onChange={handleChange} // ✅ 중요 (공용 handleChange 사용)
            displayEmpty
            sx={{ mt: 2, height: 40 }}
          >
            <MenuItem value="4">조리사</MenuItem>
            <MenuItem value="5">조리원</MenuItem>
          </Select>
          <TextField
            fullWidth
            margin="normal"
            label="파출업체"
            name="dispatch_account"
            defaultValue={dispatchFormDataRef.current.dispatch_account}
            InputLabelProps={{ style: { fontSize: "0.7rem" } }}
            inputProps={{ style: { textAlign: "center" } }}
            sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="메모"
            name="note"
            defaultValue={dispatchFormDataRef.current.note}
            InputLabelProps={{ style: { fontSize: "0.7rem" } }}
            onChange={handleChange}
          />

          <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="contained"
              onClick={handleModalClose}
              sx={{
                bgcolor: "#e8a500",
                color: "#ffffff",
                "&:hover": { bgcolor: "#e8a500", color: "#ffffff" },
              }}
            >
              취소
            </Button>
            <Button variant="contained" onClick={handleSubmit} sx={{ color: "#ffffff" }}>
              저장
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={dispatchImportOpen} onClose={closeDispatchImportModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "96vw" : 980,
            maxWidth: "96vw",
            height: isMobile ? "78vh" : "72vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDTypography variant="h6">
              파출이력 조회 {dispatchImportLoading ? "(조회중...)" : ""}
            </MDTypography>
          </MDBox>

          <MDBox sx={{ ...tableSx, flex: 1, maxHeight: "none" }}>
            <table className="recordsheet-table">
              <thead>
                <tr>
                  <th>이름</th>
                  <th>연락처</th>
                  <th>주민등록번호</th>
                  <th>계좌번호</th>
                  <th>파출업체</th>
                  <th>메모</th>
                </tr>
              </thead>
              <tbody onMouseDown={handleDispatchImportRowMouseDown}>
                {dispatchImportRows.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      {dispatchImportLoading ? "조회중입니다." : "조회된 인원이 없습니다."}
                    </td>
                  </tr>
                ) : (
                  dispatchImportRowNodes
                )}
              </tbody>
            </table>
          </MDBox>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <MDButton
              variant="outlined"
              color={dispatchImportMaskingEnabled ? "dark" : "secondary"}
              onClick={() => setDispatchImportMaskingEnabled((prev) => !prev)}
              sx={{
                fontSize: isMobile ? "0.7rem" : "0.8rem",
                minWidth: isMobile ? 78 : 96,
                px: isMobile ? 1 : 2,
              }}
            >
              {dispatchImportMaskingEnabled ? "* 해제" : "* 적용"}
            </MDButton>
            <Button
              variant="contained"
              onClick={closeDispatchImportModal}
              sx={{
                bgcolor: "#e8a500",
                color: "#ffffff",
                "&:hover": { bgcolor: "#e8a500", color: "#ffffff" },
              }}
            >
              닫기
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* ✅ 통합 출근부 모달 */}
      <Modal open={integrationRecordOpen} onClose={closeIntegrationRecordModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "98vw" : "92vw",
            maxWidth: 1200,
            height: isMobile ? "90vh" : "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              bgcolor: "#fff",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <MDBox>
              <MDTypography variant="h6">통합 출근부</MDTypography>
              <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                {year}년 {month}월 · 평일(공휴일 제외)만 재택근무로 등록됩니다.
              </MDTypography>
            </MDBox>

            <MDBox display="flex" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleIntegrationRegister}
                disabled={integrationRecordRegistering}
              >
                {integrationRecordRegistering ? "등록 중..." : "등록"}
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={closeIntegrationRecordModal}>
                닫기
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              display: "flex",
              gap: 1,
              p: 1.5,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {/* 왼쪽: 통합 직원 목록 */}
            <MDBox
              sx={{
                flex: 0.6,
                minWidth: 130,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  통합 직원 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 클릭 → 매핑 조회
                </MDTypography>
              </MDBox>

              {/* ✅ 헤더를 스크롤 영역 밖(고정)으로 분리 — sticky th 사용 시 생기던
                  트랜스폼 모달 특유의 상단 미세 틈 문제를 구조적으로 없앤다 */}
              <MDBox
                sx={{
                  display: "flex",
                  bgcolor: "#f7f7f7",
                  borderBottom: "1px solid #eee",
                }}
              >
                <MDBox sx={{ flex: 1, padding: "8px 6px", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                  성명
                </MDBox>
              </MDBox>
              <MDBox sx={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                {integrationRecordLoading ? (
                  <MDBox sx={{ padding: "8px 6px", fontSize: 12, textAlign: "center" }}>조회중...</MDBox>
                ) : (
                  (integrationMemberRows || []).map((r, i) => {
                    const selected =
                      String(integrationSelectedMember?.member_id ?? "") === String(r.member_id ?? "");
                    return (
                      <MDBox
                        key={`${r.member_id ?? "m"}-${i}`}
                        onClick={() => handleSelectIntegrationMember(r)}
                        sx={{
                          display: "flex",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                          backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                        }}
                      >
                        <MDBox
                          sx={{
                            flex: 1,
                            padding: "8px 6px",
                            fontSize: 12,
                            textAlign: "center",
                            fontWeight: selected ? 700 : 400,
                          }}
                        >
                          {r.name ?? ""}
                        </MDBox>
                      </MDBox>
                    );
                  })
                )}
              </MDBox>
            </MDBox>

            {/* 이번달 등록된 업장: tb_account_util_record 확인용(클릭/편집 불가), 재등록 전 대조용 */}
            <MDBox
              sx={{
                flex: 0.8,
                minWidth: 170,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: "#fafafa",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  이번 달 이미 등록된 업장
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  {year}년 {month}월 · 확인용(클릭 불가)
                </MDTypography>
              </MDBox>

              <MDBox sx={{ display: "flex", bgcolor: "#eee", borderBottom: "1px solid #ddd" }}>
                <MDBox sx={{ flex: 1, padding: "8px 6px", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                  고객사
                </MDBox>
                <MDBox sx={{ width: 50, padding: "8px 6px", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                  일수
                </MDBox>
              </MDBox>
              <MDBox sx={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                {integrationMonthRecordLoading ? (
                  <MDBox sx={{ padding: "8px 6px", fontSize: 12, textAlign: "center" }}>조회중...</MDBox>
                ) : integrationMonthRecordRowsSorted.length === 0 ? (
                  <MDBox sx={{ padding: "8px 6px", fontSize: 12, textAlign: "center", color: "#999" }}>
                    없음
                  </MDBox>
                ) : (
                  integrationMonthRecordRowsSorted.map((r, i) => {
                    const accName =
                      integrationAccountNameMap.get(String(r.account_id ?? "")) ?? String(r.account_id ?? "");
                    return (
                      <MDBox
                        key={`${r.account_id ?? "a"}-${i}`}
                        sx={{ display: "flex", borderBottom: "1px solid #eee" }}
                      >
                        <MDBox sx={{ flex: 1, padding: "8px 6px", fontSize: 12, textAlign: "left" }}>
                          {accName}
                        </MDBox>
                        <MDBox sx={{ width: 50, padding: "8px 6px", fontSize: 12, textAlign: "center" }}>
                          {r.reg_days ?? ""}
                        </MDBox>
                      </MDBox>
                    );
                  })
                )}
              </MDBox>
            </MDBox>

            {/* 가운데: 등록할 업장 목록 (등록 전 임시 편집용, tb_account_util_member_mapping에는 저장되지 않음) */}
            <MDBox
              sx={{
                flex: 0.9,
                minWidth: 220,
                border: "1.5px solid #ffa726",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #ffe0b2", bgcolor: "#fff8ef" }}>
                <MDTypography variant="button" fontWeight="bold">
                  등록할 업장 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  선택된 통합 직원: <b>{integrationSelectedMember?.name ?? "-"}</b>
                  <br />
                  다중선택 후 &gt; 버튼으로 업장 취소
                  <br />
                  저장은 안 되고 등록 시에만 사용됩니다.
                </MDTypography>
              </MDBox>

              <MDBox sx={{ display: "flex", bgcolor: "#fff3e0", borderBottom: "1px solid #ffe0b2" }}>
                <MDBox sx={{ flex: 1, padding: "8px 6px", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                  고객사
                </MDBox>
              </MDBox>
              <MDBox sx={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                {integrationMappingRowsSorted.map((r, i) => {
                  const accId = String(r.account_id ?? "");
                  const selected = integrationSelectedMappingIds.has(accId);
                  const accName = integrationAccountNameMap.get(accId) ?? "";
                  return (
                    <MDBox
                      key={`${r.idx ?? "new"}-${accId || "a"}-${i}`}
                      onClick={() => handleToggleIntegrationMappingSelected(accId)}
                      sx={{
                        display: "flex",
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                        backgroundColor: selected ? "rgba(255,193,7,0.18)" : "#fff",
                      }}
                    >
                      <MDBox
                        sx={{
                          flex: 1,
                          padding: "8px 6px",
                          fontSize: 12,
                          textAlign: "left",
                          fontWeight: selected ? 700 : 400,
                        }}
                      >
                        {accName}
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            </MDBox>

            {/* 가운데-오른쪽 컨트롤: < 추가 / > 제외 (선택된 항목 전부 이동, 화면 표시만 바뀌고 매핑 테이블엔 저장 안 함) */}
            <MDBox
              sx={{
                width: isMobile ? 54 : 70,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleIntegrationAddSelectedAccounts}
                sx={{ minWidth: isMobile ? 46 : 56, px: 0 }}
              >
                {"<"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleIntegrationRemoveSelectedMappings}
                sx={{ minWidth: isMobile ? 46 : 56, px: 0 }}
              >
                {">"}
              </MDButton>
              <MDTypography variant="caption" sx={{ color: "#666", textAlign: "center" }}>
                업장 ↔ 목록
                <br />
                (다중선택)
              </MDTypography>
            </MDBox>

            {/* 오른쪽: 업장 목록 (이미 매핑 목록에 있는 업장은 제외하고 노출, 다중선택 가능) */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 220,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  업장 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  다중선택 후 &lt; 으로 업장 추가
                </MDTypography>
              </MDBox>

              <MDBox sx={{ display: "flex", bgcolor: "#f7f7f7", borderBottom: "1px solid #eee" }}>
                <MDBox sx={{ flex: 1, padding: "8px 6px", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                  고객사
                </MDBox>
              </MDBox>
              <MDBox sx={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
                {integrationAvailableAccountRows.map((r, i) => {
                  const accId = String(r.account_id ?? "");
                  const selected = integrationSelectedAccountIds.has(accId);
                  return (
                    <MDBox
                      key={`${accId || "a"}-${i}`}
                      onClick={() => handleToggleIntegrationAccountSelected(accId)}
                      sx={{
                        display: "flex",
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                        backgroundColor: selected ? "rgba(30,136,229,0.14)" : "#fff",
                      }}
                    >
                      <MDBox
                        sx={{
                          flex: 1,
                          padding: "8px 6px",
                          fontSize: 12,
                          textAlign: "left",
                          fontWeight: selected ? 700 : 400,
                        }}
                      >
                        {r.account_name ?? ""}
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            </MDBox>
          </MDBox>
        </Box>
      </Modal>
      <Modal open={utilRecordAmbiguousOpen} onClose={closeUtilRecordAmbiguousModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "96vw" : 640,
            maxWidth: "96vw",
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <MDBox>
            <MDTypography variant="h6">선택 확인 필요</MDTypography>
            <MDTypography variant="caption" sx={{ color: "#666" }}>
              동명이인이거나 거래처명이 하나로 정해지지 않은 항목이 있습니다.
              <br />각 항목을 직접 선택해주세요. 선택하지 않은 항목은 저장되지 않고 건너뜁니다.
            </MDTypography>
          </MDBox>

          <Box
            sx={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}
          >
            {utilRecordDuplicateItems.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                  동명이인 확인 ({utilRecordDuplicateItems.length}명)
                </MDTypography>
                {utilRecordDuplicateItems.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 1,
                      p: 1.5,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: 1,
                    }}
                  >
                    <MDBox sx={{ minWidth: isMobile ? "auto" : 160 }}>
                      <MDTypography variant="button" fontWeight="bold">
                        {item.name}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#999", display: "block" }}>
                        {item.filledDays}일 입력됨 · 동명 후보 {item.candidates.length}명
                      </MDTypography>
                    </MDBox>
                    <Autocomplete
                      size="small"
                      options={item.candidates}
                      value={
                        item.candidates.find((c) => c.member_id === item.selectedMemberId) || null
                      }
                      onChange={(_, newVal) =>
                        handleUtilRecordDuplicateSelect(item.key, newVal?.member_id || "")
                      }
                      getOptionLabel={(opt) => (opt ? `${opt.name} (ID: ${opt.member_id})` : "")}
                      isOptionEqualToValue={(opt, val) => opt?.member_id === val?.member_id}
                      sx={{ flex: 1, minWidth: isMobile ? "100%" : 260 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="실제 회원 선택"
                          placeholder="미선택 시 저장 안함"
                          sx={{
                            "& .MuiInputBase-root": { fontSize: 12 },
                            "& .MuiInputLabel-root": { fontSize: 12 },
                          }}
                        />
                      )}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {utilRecordAmbiguousItems.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#555" }}>
                  거래처 선택 ({utilRecordAmbiguousItems.length}건)
                </MDTypography>
                {utilRecordAmbiguousItems.map((item) => (
                  <Box
                    key={item.text}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 1,
                      p: 1.5,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: 1,
                    }}
                  >
                    <MDBox sx={{ minWidth: isMobile ? "auto" : 160 }}>
                      <MDTypography variant="button" fontWeight="bold">
                        {item.text}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#999", display: "block" }}>
                        {item.count}건
                        {item.candidates.length > 1
                          ? ` · 후보 ${item.candidates.length}개`
                          : " · 매칭 후보 없음"}
                      </MDTypography>
                    </MDBox>
                    <Autocomplete
                      size="small"
                      // ✅ MUI Autocomplete의 groupBy는 options 배열 순서대로 그룹을 나누므로,
                      //    "추천 후보"가 먼저 오고 그 다음 나머지 "전체 거래처"가 오도록 미리 정렬해서 넣는다.
                      options={[
                        ...item.candidates,
                        ...(accountList || []).filter(
                          (a) => !item.candidates.some((c) => c.account_id === a.account_id)
                        ),
                      ]}
                      value={
                        (accountList || []).find((a) => a.account_id === item.selectedAccountId) ||
                        null
                      }
                      onChange={(_, newVal) =>
                        handleUtilRecordAmbiguousSelect(item.text, newVal?.account_id || "")
                      }
                      getOptionLabel={(opt) => opt?.account_name || ""}
                      isOptionEqualToValue={(opt, val) => opt?.account_id === val?.account_id}
                      groupBy={(opt) =>
                        item.candidates.some((c) => c.account_id === opt.account_id)
                          ? "추천 후보"
                          : "전체 거래처"
                      }
                      sx={{ flex: 1, minWidth: isMobile ? "100%" : 260 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="거래처 선택"
                          placeholder="미선택 시 저장 안함"
                          sx={{
                            "& .MuiInputBase-root": { fontSize: 12 },
                            "& .MuiInputLabel-root": { fontSize: 12 },
                          }}
                        />
                      )}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={closeUtilRecordAmbiguousModal}
              sx={{
                color: "#333",
                borderColor: "#999",
                "&:hover": { borderColor: "#666", bgcolor: "#f0f0f0" },
              }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleUtilRecordAmbiguousConfirm}
              sx={{
                bgcolor: "#4CAF50",
                color: "#ffffff",
                "&:hover": { bgcolor: "#43a047", color: "#ffffff" },
              }}
            >
              선택 완료 후 저장
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={excelRangeOpen} onClose={() => setExcelRangeOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "92vw" : 420,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
          }}
        >
          <MDTypography variant="h6" sx={{ mb: 1 }}>
            엑셀 기간 선택
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "#666" }}>
            월을 넘어도 선택 가능합니다. (예: 2026-02-19 ~ 2026-03-04)
          </MDTypography>

          <Box mt={2} display="flex" gap={1}>
            <TextField
              type="date"
              fullWidth
              label="시작일"
              InputLabelProps={{ shrink: true }}
              value={excelRange.start}
              onChange={(e) => setExcelRange((prev) => ({ ...prev, start: e.target.value }))}
            />
            <TextField
              type="date"
              fullWidth
              label="종료일"
              InputLabelProps={{ shrink: true }}
              value={excelRange.end}
              onChange={(e) => setExcelRange((prev) => ({ ...prev, end: e.target.value }))}
            />
          </Box>

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={() => setExcelRangeOpen(false)}
              sx={excelModalBtn.outline}
            >
              취소
            </Button>
            <Button variant="contained" onClick={handleExcelRangeConfirm} sx={excelModalBtn.solid}>
              다운로드
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={payRangeOpen} onClose={() => setPayRangeOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "94vw" : 620,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
          }}
        >
          <MDTypography variant="h6" sx={{ mb: 0.5 }}>
            지급 일괄 처리
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "#666" }}>
            기간을 선택하고 계산을 누르면 파출 급여 합계가 계산됩니다.
          </MDTypography>

          <Box mt={2} display="flex" gap={1}>
            <TextField
              type="date"
              fullWidth
              label="시작일"
              InputLabelProps={{ shrink: true }}
              value={payRange.start}
              onChange={(e) => setPayRange((prev) => ({ ...prev, start: e.target.value }))}
            />
            <TextField
              type="date"
              fullWidth
              label="종료일"
              InputLabelProps={{ shrink: true }}
              value={payRange.end}
              onChange={(e) => setPayRange((prev) => ({ ...prev, end: e.target.value }))}
            />
          </Box>

          <Box mt={1.5} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={() => setPayRangeOpen(false)}
              sx={payModalBtn.outline}
            >
              닫기
            </Button>
            <Button
              variant="contained"
              onClick={handlePayRangeCompute}
              disabled={payRangeLoading}
              sx={payModalBtn.solid}
            >
              {payRangeLoading ? "계산 중..." : "계산"}
            </Button>
          </Box>

          {payRangeRows.length > 0 && (
            <>
              <Box
                mt={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ fontSize: "0.8rem" }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    size="small"
                    sx={payCheckboxSx}
                    checked={payRangeAllSelected}
                    indeterminate={payRangeSomeSelected}
                    onChange={(e) => handlePayRangeToggleAll(e.target.checked)}
                  />
                  <span>전체선택</span>
                </Box>
                <Box>
                  총 {payRangeSummary.totalCount}명 / 합계{" "}
                  {formatMoneyLike(payRangeSummary.totalSum)}
                  {payRangeSummary.selectedCount > 0 && (
                    <>
                      {" "}
                      | 선택 {payRangeSummary.selectedCount}명 / 선택합계{" "}
                      {formatMoneyLike(payRangeSummary.selectedSum)}
                    </>
                  )}
                </Box>
              </Box>

              <Box
                mt={1}
                sx={{
                  maxHeight: 260,
                  overflow: "auto",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <table className="recordsheet-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>선택</th>
                      <th>이름</th>
                      <th>연락처</th>
                      <th>합계</th>
                      <th>지급상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payRangeRows.map((r) => (
                      <tr key={r.member_id}>
                        <td>
                          <Checkbox
                            size="small"
                            sx={payCheckboxSx}
                            checked={!!payRangeSelected?.[r.member_id]}
                            onChange={(e) => handlePayRangeToggleOne(r.member_id, e.target.checked)}
                          />
                        </td>
                        <td>{r.name || ""}</td>
                        <td>
                          {maskSensitiveFieldValue("phone", r.phone || "", dispatchMaskingEnabled)}
                        </td>
                        <td>{formatMoneyLike(r.total_pay || 0)}</td>
                        <td>
                          {r.pay_status} {r.total_cnt > 0 ? `(${r.paid_cnt}/${r.total_cnt})` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  onClick={() => handlePayRangeToggleAll(false)}
                  sx={payModalBtn.outline}
                >
                  선택 해제
                </Button>
                <Button variant="contained" onClick={handlePayRangeApply} sx={payModalBtn.solid}>
                  선택 지급
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </DashboardLayout>
  );
}

export default RecordSheet;
